/**
 * Vista: Detalle/Nuevo Producto No Conforme
 * Formato: AMX-CC-FR-05 R10
 * Estructura: Datos generales fijos arriba + 4 tabs (Apt.1, Apt.2, Apt.3, Anexo)
 */

import calidadService from '../services/calidad.service.js';
import tractorService from '../services/tractor.service.js';
import recepcionService from '../services/recepcion.service.js';
import personaService from '../services/persona.service.js';
import authService from '../services/auth.service.js';
import api from '../services/api.js';
import router from '../router/router.js';
import permisos from '../services/permissions.service.js';
import { compressImage } from '../utils/imageCompressor.js';

import { hoyLocal } from '../utils/fechas.js';
/**
 * Catálogo de clasificación de daños (tipo de daño).
 * DEBE coincidir con backend/src/constants/clasificacionDanos.js (mismos códigos y etiquetas).
 * Los 4 con ⭐ los propuso el cliente.
 */
// El cliente decidió trabajar con SOLO estas 4 clasificaciones (2026-07-28).
// Debe coincidir con backend/src/constants/clasificacionDanos.js (CATALOGO).
const CLASIF_DANOS = {
  PINT: { label: 'Pintura y hojalatería', color: '#2563eb' },
  NEUM: { label: 'Neumáticos', color: '#0891b2' },
  ELEC: { label: 'Eléctrico', color: '#d97706' },
  REFA: { label: 'Refacción', color: '#475569' }
};

export default {
  data: {
    pnc: null,
    tractor: null,
    isNew: false,
    hasUnsavedChanges: false,
    fotosNuevasProblema: [],
    fotosNuevasSolucion: [],
    responsablesProduccion: [],
    responsablesLinea: [],
    responsablesCalidad: []
  },

  async render(params) {
    this.data.isNew = !params.id || params.id === 'nuevo';

    return `
      <div class="card">
        <div class="card-header" style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem;">
          <h2 class="card-title">${this.data.isNew ? 'Nuevo Producto No Conforme' : 'Producto No Conforme'}</h2>
          <div id="headerButtons" style="display: flex; gap: 0.5rem;">
            <button class="btn btn-secondary" onclick="window.pncDetalleView._headerVolver()">← Volver</button>
          </div>
        </div>
        <div id="pncContent"><div style="text-align: center; padding: 2rem;">Cargando...</div></div>
      </div>
    `;
  },

  async afterRender(params) {
    window.pncDetalleView = this;
    this.data.hasUnsavedChanges = false;
    this.injectStyles();

    // Cargar catálogos — ahora vienen de usuarios con persona asociada
    try {
      const response = await api.get('/usuarios/responsables');
      const todos = Array.isArray(response.data) ? response.data : (response.data?.usuarios || []);
      // Solo usuarios con persona asociada; normalizamos a { nombre_completo }
      const usuariosConPersona = todos
        .filter(u => u.persona_id)
        .map(u => ({ nombre_completo: u.persona_nombre_completo || u.nombre_completo }));
      // Los tres selects usan el mismo catálogo
      this.data.responsablesProduccion = usuariosConPersona;
      this.data.responsablesLinea = usuariosConPersona;
      this.data.responsablesCalidad = usuariosConPersona;
    } catch (e) { /* catálogos opcionales */ }

    if (this.data.isNew) {
      this.renderFormularioNuevo();
    } else {
      await this.cargarPNC(params.id);
    }
  },

  injectStyles() {
    if (document.getElementById('pnc-detalle-styles')) return;
    const style = document.createElement('style');
    style.id = 'pnc-detalle-styles';
    style.textContent = `
      .pnc-tabs-bar { display: flex; padding: 0; background: transparent; border-bottom: 2px solid #e5e7eb; margin-bottom: 1rem; }
      .pnc-tab { flex: 1; padding: 0.6rem 1rem; border: none; border-bottom: 3px solid transparent; margin-bottom: -2px; background: transparent; color: #6b7280; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; text-align: center; }
      .pnc-tab:hover:not(.active):not(:disabled) { color: #374151; border-bottom-color: #d1d5db; }
      .pnc-tab.active { color: var(--primary); font-weight: 600; border-bottom-color: var(--primary); }
      .pnc-tab:disabled { color: #6b7280; opacity: 0.4; cursor: not-allowed; }
      .pnc-uppercase { text-transform: uppercase; }
      @media (max-width: 640px) {
        .pnc-tabs-bar { display: grid; grid-template-columns: 1fr 1fr; gap: 0.3rem; padding: 0.3rem; }
        .pnc-tab { padding: 0.4rem 0.5rem; font-size: 0.75rem; }
        .pnc-grid-2col { grid-template-columns: 1fr !important; }
      }
      .pnc-checkbox-visual {
        display: inline-block; width: 20px; height: 20px; border: 2px solid var(--border-color);
        border-radius: 3px; text-align: center; line-height: 16px; font-weight: bold; font-size: 0.85rem;
      }
      .pnc-checkbox-visual.checked { background: var(--primary); color: white; border-color: var(--primary); }
    `;
    document.head.appendChild(style);
  },

  // ======================== CARGAR PNC ========================

  /**
   * Limpia los buffers diferidos de edición (cambios por sección, fotos/firmas
   * pendientes de subir o borrar). Necesario al ENTRAR a un PNC porque la vista es
   * un singleton reutilizado por el router: sin este reset, cambios de un PNC
   * abandonado sin guardar se procesarían contra el siguiente PNC al "Guardar
   * Cambios" (escritura cruzada / borrado de archivos ajenos).
   * Ver docs/CHANGELOG_KAREN.md [2026-07-17].
   */
  _resetBuffersEdicion() {
    this._problemasDirty = {};
    this._problemasFirmasPendientes = {};
    this._fotosPendientesPorProblema = {};
    this._fotosPendientesEliminar = new Set();
    this._firmasViejasABorrar = new Set();
    this._firmasPendientesReemplazo = {};
    this._anexoGrupoKey = null;
    this._gruposAbiertos = new Set();
    this._gruposEnEdicion = new Set();
  },

  async cargarPNC(id) {
    try {
      const response = await calidadService.obtenerPNC(id);
      this.data.pnc = response.data || response;
      this.data.hasUnsavedChanges = false;
      this.data.fotosNuevasProblema = [];
      this.data.fotosNuevasSolucion = [];
      this._resetBuffersEdicion();

      if (this.data.pnc.estado === 'cerrado') {
        const headerButtons = document.getElementById('headerButtons');
        if (headerButtons) {
          headerButtons.innerHTML = `
            <button class="btn btn-secondary" onclick="router.navigate('/calidad/pnc')">← Volver</button>
            ${permisos.canPrint('pnc') ? `<button class="btn btn-primary" onclick="window.pncDetalleView.imprimir()">🖨️ Imprimir</button>
            <button class="btn btn-success" onclick="window.pncDetalleView.descargarPDF()">📄 Descargar PDF</button>` : ''}
          `;
        }
        this.renderPNCCompletado();
      } else {
        this.renderFormularioEdicion();
      }

      // Si la URL trae ?addDano=1 (recién creado), abrir modal "Agregar daño"
      const params = new URLSearchParams(window.location.search);
      if (params.get('addDano') === '1' && this.data.pnc.estado !== 'cerrado') {
        setTimeout(() => this.agregarProblema(), 100);
        // Limpiar el query param para que no se reabra al recargar
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }
    } catch (error) {
      toast.error('Error al cargar PNC');
      router.navigate('/calidad/pnc');
    }
  },

  // ======================== FORMULARIO NUEVO ========================

  renderFormularioNuevo() {
    const container = document.getElementById('pncContent');

    // La vista es un singleton reutilizado por el router: si no limpiamos el buffer
    // de captura, los daños de una creación anterior (abandonada sin guardar) se
    // arrastran al siguiente PNC y se guardan en el tractor equivocado.
    // Ver docs/CHANGELOG_KAREN.md [2026-07-17].
    this._creacionProblemas = [];
    this._clearCreacionBeforeUnload();

    container.innerHTML = `
      <!-- Buscar Tractor -->
      <div style="background: white; border: 1px solid var(--gray-200); padding: 1.25rem 1.5rem; border-radius: 8px; margin-bottom: 1rem;">
        <h4 style="margin: 0 0 0.25rem; font-size: 1rem; font-weight: 600;">Buscar por número de serie</h4>
        <p style="margin: 0 0 1rem; color: var(--text-muted); font-size: 0.85rem;">Ingrese el número de serie del tractor para crear el PNC. Después podrá agregar los daños individuales.</p>
        <div style="display: flex; gap: 0.75rem; align-items: center;">
          <input
            type="text"
            id="numSerieBuscar"
            class="form-input"
            style="flex: 1;"
            placeholder="Ej: TE354-2026-0127"
          />
          <button type="button" class="btn btn-primary" onclick="window.pncDetalleView.buscarTractor()">
            Buscar
          </button>
        </div>
        <div id="tractorResult" style="margin-top: 1rem;"></div>
      </div>

      <div id="formularioPrincipal" style="display: none;">
        <!-- Datos del Tractor -->
        <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1rem; margin-bottom: 1.5rem;">
          <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
          <input type="hidden" id="pncNumSerie" />
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;" class="pnc-grid-2col">
            <div class="form-group">
              <label class="form-label">Marca</label>
              <input type="text" id="pncMarca" class="form-input" readonly />
            </div>
            <div class="form-group">
              <label class="form-label">Modelo</label>
              <input type="text" id="pncModelo" class="form-input" readonly />
            </div>
          </div>
        </div>

        <!-- Botones -->
        <div style="display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1.5rem;">
          <button type="button" class="btn btn-secondary" onclick="router.navigate('/calidad/pnc')">Cancelar</button>
          <button type="button" class="btn btn-success" onclick="window.pncDetalleView.guardarNuevo()">Crear PNC y agregar daño</button>
        </div>
      </div>

      <div id="legacyFormBlock" style="display: none;">
        <!-- Datos generales fijos (siempre visibles) -->
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
          <h3 style="margin-bottom: 1rem;">Datos Generales</h3>
          <input type="hidden" id="pncNumSerie" />
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;" class="pnc-grid-2col">
            <div class="form-group">
              <label class="form-label">Marca</label>
              <input type="text" id="pncMarca" class="form-input" readonly />
            </div>
            <div class="form-group">
              <label class="form-label">Modelo</label>
              <input type="text" id="pncModelo" class="form-input" readonly />
            </div>
            <div class="form-group">
              <label class="form-label">Fecha de Detección</label>
              <input type="date" id="pncFechaDeteccion" class="form-input" value="${hoyLocal()}" />
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="pnc-tabs-bar" id="pncTabsBar">
          <button class="pnc-tab active" onclick="window.pncDetalleView.showTab('apt1')">Identificación</button>
          <button class="pnc-tab" onclick="window.pncDetalleView.showTab('apt2')">Plan de acción</button>
          <button class="pnc-tab" onclick="window.pncDetalleView.showTab('apt3')">Evaluación</button>
          <button class="pnc-tab" onclick="window.pncDetalleView.showTab('anexo')">Anexo</button>
        </div>

        <!-- Tab Apt. 1: Identificación -->
        <div class="pnc-panel" id="panel-apt1" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem; font-style: italic;">
            Apartado 1. Llenado por el técnico de producción que detecta la no conformidad.
          </p>
          <div class="form-group">
            <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
              <input type="checkbox" id="pncEvidenciaWsProblema" />
              Evidencia del problema enviada por WhatsApp
            </label>
          </div>
          <div class="form-group">
            <label class="form-label">Descripción del Problema *</label>
            <textarea id="pncDescripcion" class="form-input pnc-uppercase" rows="5" placeholder="Describa detalladamente el problema encontrado..."></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Detectado por (Nombre) *</label>
            <input type="text" id="pncDetectadoPor" class="form-input pnc-uppercase" placeholder="Nombre del responsable" />
          </div>
        </div>

        <!-- Tab Apt. 2: Plan de acción -->
        <div class="pnc-panel" id="panel-apt2" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem; font-style: italic;">
            Apartado 2. Requiere la autorización de la acción del responsable del área de producción.
          </p>
          <div class="form-group">
            <label class="form-label">Acción *</label>
            <div style="display: flex; gap: 1.5rem; flex-wrap: wrap; margin-top: 0.25rem;">
              <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
                <input type="radio" name="pncAccion" value="concesion" /> Concesión (Justificar)
              </label>
              <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
                <input type="radio" name="pncAccion" value="mantenimiento" /> Mantenimiento
              </label>
              <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
                <input type="radio" name="pncAccion" value="reemplazo" /> Reemplazo
              </label>
            </div>
          </div>
          <div class="form-group" style="margin-top: 1rem;">
            <label class="form-label">Justificación</label>
            <textarea id="pncJustificacion" class="form-input pnc-uppercase" rows="4" placeholder="Justifique la acción seleccionada..."></textarea>
          </div>
          <div class="form-group" style="margin-top: 1rem;">
            <label class="form-label">Responsable del área de producción *</label>
            <select id="pncRespProduccion" class="form-input" onchange="document.getElementById('pncRespProduccionCierre').value = this.value">
              <option value="">Seleccione...</option>
              ${this.data.responsablesProduccion.map(r => `<option value="${r.nombre_completo}">${r.nombre_completo}</option>`).join('')}
            </select>
          </div>
        </div>

        <!-- Tab Apt. 3: Evaluación -->
        <div class="pnc-panel" id="panel-apt3" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem; font-style: italic;">
            Apartado 3. Evaluación y cierre.
          </p>

          <!-- Verificación de la acción -->
          <p style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.75rem;">
            Llenado por el responsable de la línea de producto.
          </p>
          <div class="form-group">
            <label class="form-label">Fecha de cierre</label>
            <input type="date" id="pncFechaCierreNuevo" class="form-input" />
          </div>
          <div class="form-group" style="display: flex; align-items: center;">
            <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
              <input type="checkbox" id="pncEvidenciaWsSolucion" />
              Evidencia de la acción recibida por WhatsApp
            </label>
          </div>
          <div class="form-group">
            <label class="form-label">Detalles de la acción</label>
            <textarea id="pncDetallesAccion" class="form-input pnc-uppercase" rows="4" placeholder="Describa las acciones realizadas..."></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Responsable de la línea de producto</label>
            <select id="pncRespLinea" class="form-input">
              <option value="">Seleccione...</option>
              ${this.data.responsablesLinea.map(r => `<option value="${r.nombre_completo}">${r.nombre_completo}</option>`).join('')}
            </select>
          </div>

          <hr style="border: none; border-top: 1px solid var(--border-color); margin: 1.5rem 0;" />

          <!-- Cierre del producto no conforme -->
          <div class="form-group">
            <label class="form-label">Resultado final</label>
            <div style="display: flex; gap: 1.5rem; margin-top: 0.25rem;">
              <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
                <input type="radio" name="pncResultadoFinalNuevo" value="conforme" /> Conforme
              </label>
              <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
                <input type="radio" name="pncResultadoFinalNuevo" value="no_conforme" /> No conforme
              </label>
            </div>
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; margin-top: 1rem;" class="pnc-grid-2col">
            <div class="form-group">
              <label class="form-label">Responsable del área de producción</label>
              <input type="text" id="pncRespProduccionCierre" class="form-input" readonly placeholder="Se llena del Apartado 2" />
            </div>
            <div class="form-group">
              <label class="form-label">Responsable del área de calidad</label>
              <select id="pncRespCalidadNuevo" class="form-input">
                <option value="">Seleccione...</option>
                ${this.data.responsablesCalidad.map(r => `<option value="${r.nombre_completo}">${r.nombre_completo}</option>`).join('')}
              </select>
            </div>
          </div>
        </div>

        <!-- Tab Anexo -->
        <div class="pnc-panel" id="panel-anexo" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
          <h4 style="margin-bottom: 1rem; font-size: 0.95rem;">Anexo: Evidencia fotográfica</h4>
          <div style="margin-bottom: 1.5rem;">
            <p style="font-weight: 600; margin-bottom: 0.75rem;">1. Evidencia del problema</p>
            <p style="color: var(--text-secondary); font-style: italic;">Guarde el PNC primero para agregar fotos.</p>
          </div>
          <hr style="border: none; border-top: 1px solid var(--border-color); margin: 1.5rem 0;" />
          <div>
            <p style="font-weight: 600; margin-bottom: 0.75rem;">2. Evidencia de la solución</p>
            <p style="color: var(--text-secondary); font-style: italic;">Guarde el PNC primero para agregar fotos.</p>
          </div>
        </div>

        <!-- Botones -->
        <div style="display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1.5rem;">
          <button type="button" class="btn btn-secondary" onclick="router.navigate('/calidad/pnc')">Cancelar</button>
          <button type="button" class="btn btn-success" onclick="window.pncDetalleView.guardarNuevo()">Crear PNC</button>
        </div>
      </div>
    `;

    // Buscar al presionar Enter; ocultar formulario si modifican el NS después de buscar
    const nsBuscar = document.getElementById('numSerieBuscar');
    if (nsBuscar) {
      nsBuscar.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.buscarTractor();
        }
      });
      nsBuscar.addEventListener('input', () => {
        if (this.data.tractor) {
          this.data.tractor = null;
          const fp = document.getElementById('formularioPrincipal');
          if (fp) fp.style.display = 'none';
          const result = document.getElementById('tractorResult');
          if (result) result.innerHTML = '';
        }
      });
    }
  },

  // ======================== BUSCAR TRACTOR ========================

  async buscarTractor() {
    const numSerie = document.getElementById('numSerieBuscar')?.value?.trim();
    if (!numSerie) { toast.error('Ingrese el número de serie'); return; }

    const resultDiv = document.getElementById('tractorResult');
    resultDiv.innerHTML = '<p>Buscando...</p>';

    try {
      const response = await tractorService.getByNumSerie(numSerie);
      const tractor = response.data || response;
      this.data.tractor = tractor;
      this.data.tractor.num_serie = numSerie;
      resultDiv.innerHTML = '';

      // Verificar si ya existe un PNC abierto para este tractor antes de
      // permitir capturar daños. Solo se permite un PNC abierto por tractor.
      try {
        const respAbierto = await calidadService.obtenerPNCAbiertoPorTractor(numSerie);
        const pncAbierto = respAbierto.data || respAbierto;
        if (pncAbierto && pncAbierto.id) {
          modal.open(
            'PNC ya existe',
            `<p>Ya hay un PNC abierto (<strong>#${pncAbierto.id}</strong>) para el tractor <strong>${numSerie}</strong>.</p>
             <p style="margin-top:0.5rem;color:var(--gray-600);font-size:0.9rem;">Solo se permite un PNC abierto por tractor.</p>`,
            [
              { text: 'Cancelar', class: 'btn-secondary', onClick: () => router.navigate('/calidad/pnc') },
              {
                text: 'Ir al PNC existente',
                class: 'btn-primary',
                onClick: () => router.navigate(`/calidad/pnc/${pncAbierto.id}`)
              }
            ]
          );
          return;
        }
      } catch (err) {
        // Si la consulta falla, dejamos pasar — el backend volverá a validar al crear
        console.warn('No se pudo verificar PNC abierto:', err.message);
      }

      // No hay PNC abierto → render datos del tractor + sección Daños
      this.renderCreacionConTractor();
    } catch (error) {
      resultDiv.innerHTML = `
        <div style="padding: 0.75rem; background: var(--warning); color: white; border-radius: 8px;">
          <strong>No se encontró tractor con ese número de serie</strong>
        </div>
      `;
    }
  },

  renderCreacionConTractor() {
    const t = this.data.tractor;
    if (!t) return;
    if (!this._creacionProblemas) this._creacionProblemas = [];
    const problemasPend = this._creacionProblemas;

    let extra = document.getElementById('pncCreacionExtra');
    if (!extra) {
      extra = document.createElement('div');
      extra.id = 'pncCreacionExtra';
      document.getElementById('pncContent').appendChild(extra);
    }

    const formatAccion = (a) => ({ concesion: 'Concesión', mantenimiento: 'Mantenimiento', reemplazo: 'Reemplazo' }[a] || '—');
    const truncate = (s, n) => {
      const txt = String(s || '');
      return txt.length > n ? txt.slice(0, n) + '…' : txt;
    };

    const danosHtml = problemasPend.length === 0 ? `
      <p style="color:var(--gray-500);font-size:0.85rem;font-style:italic;text-align:center;padding:1.5rem 1rem;">
        No hay daños registrados. Click "+ Agregar daño" para capturar el primero.
      </p>
    ` : `
      <div style="border:1px solid var(--gray-200);border-radius:8px;overflow:hidden;background:white;">
        <table class="table" style="margin:0;">
          <thead>
            <tr>
              <th style="width:40px;">#</th>
              <th>Daño</th>
              <th class="tbl-hide-mobile">Detectado por</th>
              <th class="tbl-hide-mobile">Acción</th>
              <th style="width:100px;text-align:right;">Acciones</th>
            </tr>
          </thead>
          <tbody>
            ${problemasPend.map((pr, idx) => `
              <tr>
                <td style="color:var(--gray-500);">${idx + 1}</td>
                <td><strong>${truncate(pr.descripcion, 70)}</strong><div style="margin-top:0.25rem;">${this._clasifBadge(pr.clasificacion, true)}</div></td>
                <td class="tbl-hide-mobile" style="font-size:0.85rem;">${pr.detectado_por || '—'}</td>
                <td class="tbl-hide-mobile" style="font-size:0.85rem;">${formatAccion(pr.accion)}</td>
                <td style="text-align:right;white-space:nowrap;">
                  <button class="btn btn-xs btn-secondary" type="button" onclick="window.pncDetalleView.agregarDanoCreacion(${idx})" style="padding:0.2rem 0.5rem;font-size:0.75rem;margin-right:0.25rem;">Editar</button>
                  ${(pr.origen_modulo || 'manual') === 'manual' ? `
                    <button class="btn btn-xs btn-danger" type="button" onclick="window.pncDetalleView.removerDanoCreacion(${idx})" style="padding:0.2rem 0.5rem;font-size:0.75rem;">Quitar</button>
                  ` : ''}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    extra.innerHTML = `
      <!-- Datos del Tractor -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;" class="pnc-grid-2col">
          <div class="form-group">
            <label class="form-label">Marca</label>
            <input type="text" class="form-input" readonly value="${t.marca || ''}" />
          </div>
          <div class="form-group">
            <label class="form-label">Modelo</label>
            <input type="text" class="form-input" readonly value="${t.modelo || ''}" />
          </div>
        </div>
      </div>

      <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
        <div style="margin-top:1rem;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.75rem;">
            <h5 style="margin:0;font-size:0.85rem;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:var(--primary);">
              Daños${problemasPend.length > 0 ? ` (${problemasPend.length})` : ''}
            </h5>
            <button type="button" onclick="window.pncDetalleView.agregarDanoCreacion()"
              style="font-size: 0.85rem; border: 1px solid var(--gray-300); background: white; padding: 0.45rem 1rem; border-radius: 8px; cursor: pointer; color: var(--gray-700); font-weight: 600; transition: border-color 0.2s, color 0.2s;"
              onmouseover="this.style.borderColor='var(--primary)'; this.style.color='var(--primary)';"
              onmouseout="this.style.borderColor='var(--gray-300)'; this.style.color='var(--gray-700)';">
              + Agregar daño
            </button>
          </div>
          ${danosHtml}
        </div>
      </div>

      <div style="display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1.5rem;">
        <button type="button" class="btn btn-secondary" onclick="window.pncDetalleView._cancelarCreacion()">Cancelar</button>
        <button type="button" class="btn btn-primary" onclick="window.pncDetalleView._guardarPNCConDanos()">
          Crear PNC
        </button>
      </div>
    `;
  },

  _setCreacionBeforeUnload() {
    if (this._creacionBeforeUnloadHandler) return;
    this._creacionBeforeUnloadHandler = (e) => {
      if (this._creacionProblemas && this._creacionProblemas.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this._creacionBeforeUnloadHandler);
  },

  _clearCreacionBeforeUnload() {
    if (this._creacionBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._creacionBeforeUnloadHandler);
      this._creacionBeforeUnloadHandler = null;
    }
  },

  _cancelarCreacion() {
    const tienePendientes = (this._creacionProblemas || []).length > 0;
    const irse = () => {
      this._creacionProblemas = [];
      this._clearCreacionBeforeUnload();
      router.navigate('/calidad/pnc');
    };
    if (!tienePendientes) {
      irse();
      return;
    }
    modal.confirm(
      'Salir sin guardar',
      `Tienes <strong>${this._creacionProblemas.length}</strong> daño(s) capturado(s) sin guardar. Si sales ahora se perderán y el PNC no se creará. ¿Estás seguro?`,
      () => irse()
    );
  },

  removerDanoCreacion(idx) {
    if (!this._creacionProblemas || !this._creacionProblemas[idx]) return;
    const desc = this._creacionProblemas[idx].descripcion || '';
    const preview = desc.length > 60 ? desc.slice(0, 60) + '…' : desc;
    modal.confirm(
      'Quitar daño',
      `¿Seguro que deseas quitar este daño de la lista?<br><br><em>"${preview}"</em>`,
      () => {
        this._creacionProblemas.splice(idx, 1);
        this.renderCreacionConTractor();
        toast.info('Daño removido');
      }
    );
  },

  async _guardarPNCConDanos() {
    const t = this.data.tractor;
    if (!t) return;
    if (!this._creacionProblemas || this._creacionProblemas.length === 0) {
      toast.error('Agrega al menos un daño antes de crear el PNC');
      return;
    }
    try {
      this._showProgress('Creando PNC...');
      const respCrear = await calidadService.crearPNC({
        num_serie: t.num_serie,
        marca: t.marca || null,
        modelo: t.modelo || null,
        problemas: this._creacionProblemas
      });
      const pncCreado = respCrear.data || respCrear;
      this._hideProgress();
      this._creacionProblemas = []; // limpiar buffer
      this._clearCreacionBeforeUnload();
      toast.success(`PNC creado con ${pncCreado.problemas?.length || 0} daño(s)`);
      router.navigate(`/calidad/pnc/${pncCreado.id}`);
    } catch (err) {
      this._hideProgress();
      // 409 → ya existe un PNC abierto. Ofrecer ir al existente.
      if (err.status === 409) {
        const match = (err.message || '').match(/#(\d+)/);
        const existingId = match ? match[1] : null;
        if (existingId) {
          modal.open(
            'PNC ya existe',
            `<p>Ya hay un PNC abierto (<strong>#${existingId}</strong>) para el tractor <strong>${this.data.tractor?.num_serie || ''}</strong>.</p>
             <p style="margin-top:0.5rem;color:var(--gray-600);font-size:0.9rem;">Solo se permite un PNC abierto por tractor.</p>`,
            [
              { text: 'Cancelar', class: 'btn-secondary' },
              {
                text: 'Ir al PNC existente',
                class: 'btn-primary',
                onClick: () => {
                  this._creacionProblemas = [];
                  this._clearCreacionBeforeUnload();
                  router.navigate(`/calidad/pnc/${existingId}`);
                }
              }
            ]
          );
          return;
        }
      }
      toast.error(err.message || 'Error al crear PNC');
    }
  },

  agregarDanoCreacion(editarIdx = null) {
    // Modo edición: cuando editarIdx viene, pre-rellena el modal con los datos del daño.
    // En ambos modos los daños se acumulan en memoria; el PNC se crea al final.
    if (!this.data.tractor || !this.data.tractor.num_serie) {
      toast.error('Busque primero un tractor');
      return;
    }
    const isEdit = typeof editarIdx === 'number' && editarIdx >= 0;
    const existing = isEdit ? this._creacionProblemas?.[editarIdx] : null;
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const valDesc = existing?.descripcion || '';
    const valDetectado = existing?.detectado_por || (authService.getUser()?.nombre || authService.getUser()?.nombre_completo || '');
    const valClasif = existing?.clasificacion || '';
    const valAccion = existing?.accion || '';
    const valJustif = existing?.justificacion || '';
    const valDetalles = existing?.detalles_accion || '';

    const html = `
      <div class="form-group">
        <label class="form-label">Descripción del problema *</label>
        <textarea id="modalNuevoProbDesc" class="form-input" rows="3" placeholder="Describa el problema...">${esc(valDesc)}</textarea>
      </div>
      <div class="form-group">
        <label class="form-label">Detectado por *</label>
        <input id="modalNuevoProbDetectado" class="form-input" placeholder="Persona/rol que detectó" value="${esc(valDetectado)}" />
      </div>
      <div class="form-group">
        <label class="form-label">Clasificación (tipo de daño) *</label>
        <select id="modalNuevoProbClasif" class="form-input">
          ${this._clasifSelectOptions(valClasif)}
        </select>
      </div>
      <div class="form-group">
        <label class="form-label">Acción</label>
        <div style="display:flex;gap:1rem;flex-wrap:wrap;padding:0.4rem 0;">
          <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;"><input type="radio" name="modalNuevoProbAccion" value="concesion" ${valAccion === 'concesion' ? 'checked' : ''} /> Concesión</label>
          <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;"><input type="radio" name="modalNuevoProbAccion" value="mantenimiento" ${valAccion === 'mantenimiento' ? 'checked' : ''} /> Mantenimiento</label>
          <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;"><input type="radio" name="modalNuevoProbAccion" value="reemplazo" ${valAccion === 'reemplazo' ? 'checked' : ''} /> Reemplazo</label>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">Justificación</label>
        <textarea id="modalNuevoProbJustif" class="form-input" rows="2">${esc(valJustif)}</textarea>
      </div>
      <div class="form-group">
        <label class="form-label">Detalle de la solución (opcional)</label>
        <textarea id="modalNuevoProbDetalle" class="form-input" rows="2">${esc(valDetalles)}</textarea>
      </div>
    `;
    modal.open(
      isEdit ? 'Editar daño' : 'Agregar daño',
      html,
      [
        { text: 'Cancelar', class: 'btn-secondary' },
        {
          text: isEdit ? 'Guardar cambios' : 'Agregar',
          class: 'btn-primary',
          closeOnClick: false,
          onClick: () => {
            const desc = document.getElementById('modalNuevoProbDesc')?.value.trim();
            if (!desc) {
              toast.error('La descripción del problema es requerida');
              return;
            }
            const detectado_por = document.getElementById('modalNuevoProbDetectado')?.value.trim();
            if (!detectado_por) {
              toast.error('El campo "Detectado por" es requerido');
              return;
            }
            const clasificacion = document.getElementById('modalNuevoProbClasif')?.value || '';
            if (!clasificacion) {
              toast.error('Selecciona la clasificación (tipo de daño)');
              return;
            }
            const accionRadio = document.querySelector('input[name="modalNuevoProbAccion"]:checked');
            const accion = accionRadio ? accionRadio.value : null;
            const justificacion = document.getElementById('modalNuevoProbJustif')?.value.trim() || null;
            const detalles_accion = document.getElementById('modalNuevoProbDetalle')?.value.trim() || null;

            if (!this._creacionProblemas) this._creacionProblemas = [];
            const dano = {
              descripcion: desc,
              clasificacion,
              detectado_por,
              accion,
              justificacion,
              detalles_accion,
              origen_modulo: 'manual'
            };
            if (isEdit) {
              this._creacionProblemas[editarIdx] = dano;
            } else {
              this._creacionProblemas.push(dano);
            }
            this._setCreacionBeforeUnload();
            modal.close();
            this.renderCreacionConTractor();
            toast.success(isEdit ? 'Daño actualizado' : `Daño agregado (${this._creacionProblemas.length} en total)`);
          }
        }
      ]
    );
  },

  // ======================== GUARDAR NUEVO ========================

  async guardarNuevo() {
    const numSerie = document.getElementById('pncNumSerie')?.value;
    if (!numSerie) {
      toast.error('Busque y seleccione un tractor antes de crear el PNC');
      return;
    }

    const data = {
      num_serie: numSerie,
      marca: document.getElementById('pncMarca')?.value || null,
      modelo: document.getElementById('pncModelo')?.value || null
      // Sin descripción/detectado_por/acción — los daños se agregan después en la edición
    };

    try {
      this._showProgress('Creando PNC...');
      const response = await calidadService.crearPNC(data);
      const pnc = response.data || response;
      this._hideProgress();
      toast.success('PNC creado. Agrega el primer daño.');
      // Redirige a edición + flag para que afterRender abra el modal "Agregar daño"
      router.navigate(`/calidad/pnc/${pnc.id}?addDano=1`);
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al crear PNC');
    }
  },

  // ======================== FORMULARIO EDICIÓN (abierto / en_proceso) ========================

  renderFormularioEdicion() {
    const container = document.getElementById('pncContent');
    const p = this.data.pnc;

    // Banner de origen a nivel PNC eliminado: cada daño puede venir de un módulo
    // distinto. El origen se muestra ahora dentro del modal de Identificación de cada daño.
    let origenBadge = '';

    container.innerHTML = `
      ${origenBadge}
      <!-- Datos del Tractor -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;" class="pnc-grid-2col">
          <div class="form-group">
            <label class="form-label">Número de Serie</label>
            <input type="text" class="form-input" readonly value="${p.num_serie}" />
          </div>
          <div class="form-group">
            <label class="form-label">Marca</label>
            <input type="text" class="form-input" readonly value="${p.marca || ''}" />
          </div>
          <div class="form-group" style="grid-column: 1 / -1;">
            <label class="form-label">Modelo</label>
            <input type="text" class="form-input" readonly value="${p.modelo || ''}" />
          </div>
        </div>
      </div>

      <!-- Tabs (Firmas se oculta hasta que haya al menos un daño capturado) -->
      <div class="pnc-tabs-bar" id="pncTabsBar">
        <button class="pnc-tab active" onclick="window.pncDetalleView.showTab('apt1')">Daños</button>
        ${(p.problemas || []).length > 0 ? `<button class="pnc-tab" onclick="window.pncDetalleView.showTab('firmas')">Firmas</button>` : ''}
      </div>

      <!-- Tab Daños -->
      <div class="pnc-panel" id="panel-apt1" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
        ${this._renderProblemasIndividuales(p, false)}
      </div>

      <!-- Tab Apt 2: Plan de acción -->
      <div class="pnc-panel" id="panel-apt2" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem; font-style: italic;">
          Apartado 2. Requiere la autorización de la acción del responsable del área de producción.
        </p>
        <div class="form-group">
          <label class="form-label">Acción *</label>
          <div style="display: flex; gap: 1.5rem; flex-wrap: wrap; margin-top: 0.25rem;">
            ${['concesion', 'mantenimiento', 'reemplazo'].map(a => `
              <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
                <input type="radio" name="pncAccionEdit" value="${a}" ${p.accion === a ? 'checked' : ''} />
                ${this.formatAccion(a)}
              </label>
            `).join('')}
          </div>
        </div>
        <div class="form-group" style="margin-top: 1rem;">
          <label class="form-label">Justificación</label>
          <textarea id="pncJustificacionEdit" class="form-input pnc-uppercase" rows="4">${p.justificacion || ''}</textarea>
        </div>
        <div class="form-group" style="margin-top: 1rem;">
          <label class="form-label">Responsable del área de producción${(p.origen_modulo && p.origen_modulo !== 'manual') ? ' *' : ''}</label>
          ${(p.origen_modulo && p.origen_modulo !== 'manual')
            ? `<select id="pncRespProduccionEdit" class="form-input" onchange="const ev = document.getElementById('pncRespProduccionEvalEdit'); if (ev) ev.value = this.value;">
                <option value="">Seleccione...</option>
                ${this.data.responsablesProduccion.map(r => `<option value="${r.nombre_completo}" ${p.responsable_produccion === r.nombre_completo ? 'selected' : ''}>${r.nombre_completo}</option>`).join('')}
              </select>`
            : `<input type="text" id="pncRespProduccionEdit" class="form-input" readonly value="${p.responsable_produccion || ''}" />`
          }
        </div>
      </div>

      <!-- Tab Apt 3: Evaluación y cierre -->
      <div class="pnc-panel" id="panel-apt3" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        ${this._renderApt3Edicion(p)}
      </div>

      <!-- Tab Firmas -->
      <div class="pnc-panel" id="panel-firmas" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <div id="firmasContainer" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;" class="pnc-grid-2col">
        </div>
      </div>

      <!-- Tab Anexo: Evidencia fotográfica -->
      <div class="pnc-panel" id="panel-anexo" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        ${this._renderAnexo(p, false)}
      </div>

      <!-- Indicador de cambios sin guardar -->
      <div id="unsavedIndicator" style="display: none; background: var(--warning); color: white; padding: 0.5rem 1rem; border-radius: 8px; margin-bottom: 1rem; text-align: center;">
        Hay cambios sin guardar
      </div>

      <!-- Botones -->
      <div style="display: flex; gap: 1rem; justify-content: flex-end;">
        <button type="button" class="btn btn-secondary" onclick="window.pncDetalleView.volver()">Cancelar</button>
        <button type="button" class="btn btn-primary" onclick="window.pncDetalleView.guardarCambios()">Guardar Cambios</button>
        ${permisos.can('pnc', 'cerrar') ? `<button type="button" class="btn btn-success" onclick="window.pncDetalleView.completarPNC()">Completar</button>` : ''}
      </div>
    `;

    // Delegación: captura cambios en cualquier input/textarea/select dentro de .pnc-panel,
    // incluso los que se inyectan dinámicamente después (firmas, modales reabiertos, etc.).
    document.querySelectorAll('.pnc-panel').forEach(panel => {
      const handler = (e) => {
        const el = e.target;
        if (!el || el.dataset?.skipUnsaved !== undefined) return;
        if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
        this._showUnsaved();
      };
      panel.addEventListener('change', handler);
      panel.addEventListener('input', handler);
    });

    // (legacy) — listeners directos a elementos ya existentes
    document.querySelectorAll('.pnc-panel input:not([data-skip-unsaved]), .pnc-panel textarea:not([data-skip-unsaved]), .pnc-panel select:not([data-skip-unsaved])').forEach(el => {
      el.addEventListener('change', () => this._showUnsaved());
      el.addEventListener('input', () => this._showUnsaved());
    });

    this._beforeUnloadHandler = (e) => {
      if (this._hayCambiosSinGuardar()) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', this._beforeUnloadHandler);

    // Restaurar el indicador de "cambios sin guardar" tras el re-render (el HTML nuevo
    // lo recrea oculto; sin esto, cualquier re-render como toggleGrupo lo "borra").
    if (this._hayCambiosSinGuardar()) this._showUnsaved();
  },

  _renderProblemasIndividuales(p, readonly) {
    const problemas = Array.isArray(p.problemas) ? p.problemas : [];
    if (problemas.length === 0 && readonly) return '';

    // Aplicar filtros activos
    const filtroEstatus = this._filtroEstatus || 'todos';
    const filtroOrigen = this._filtroOrigen || 'todos';
    const filtroClasificacion = this._filtroClasificacion || 'todos';
    const busqueda = (this._busquedaRapidaProblema || '').toLowerCase().trim();
    const problemasFiltrados = problemas.filter(pr => {
      if (filtroEstatus !== 'todos' && pr.estatus !== filtroEstatus) return false;
      if (filtroOrigen !== 'todos' && (pr.origen_modulo || 'manual') !== filtroOrigen) return false;
      if (filtroClasificacion !== 'todos' && (pr.clasificacion || '__sin__') !== filtroClasificacion) return false;
      if (busqueda) {
        const txt = `${pr.descripcion || ''} ${pr.detectado_por || ''}`.toLowerCase();
        if (!txt.includes(busqueda)) return false;
      }
      return true;
    });
    // Agrupado visual: ordenar por clasificación (orden del catálogo; "sin clasificar" al final)
    const clasifOrden = Object.keys(CLASIF_DANOS);
    const rankClasif = (code) => {
      const i = clasifOrden.indexOf(code);
      return i === -1 ? 999 : i;
    };
    problemasFiltrados.sort((a, b) => rankClasif(a.clasificacion) - rankClasif(b.clasificacion));
    const hayFiltros = filtroEstatus !== 'todos' || filtroOrigen !== 'todos' || filtroClasificacion !== 'todos' || busqueda;

    // Paginación
    const pageSize = this._pageSizeProblema || 10;
    const totalFiltrado = problemasFiltrados.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltrado / pageSize));
    const paginaActual = Math.min(this._paginaProblema || 1, totalPages);
    const offset = (paginaActual - 1) * pageSize;
    const problemasPagina = problemasFiltrados.slice(offset, offset + pageSize);
    const inicio = totalFiltrado === 0 ? 0 : offset + 1;
    const fin = Math.min(offset + pageSize, totalFiltrado);

    // Conteos por estatus y origen
    const cnt = (fn) => problemas.filter(fn).length;
    const cntEst = {
      todos: problemas.length,
      pendiente: cnt(pr => pr.estatus === 'pendiente'),
      en_proceso: cnt(pr => pr.estatus === 'en_proceso'),
      corregido: cnt(pr => pr.estatus === 'corregido'),
      diferido: cnt(pr => pr.estatus === 'diferido')
    };
    const cntOri = {
      todos: problemas.length,
      recepcion: cnt(pr => pr.origen_modulo === 'recepcion'),
      checklist_ensamble: cnt(pr => pr.origen_modulo === 'checklist_ensamble'),
      manual: cnt(pr => !pr.origen_modulo || pr.origen_modulo === 'manual')
    };
    const cntClasif = {};
    problemas.forEach(pr => {
      const k = pr.clasificacion || '__sin__';
      cntClasif[k] = (cntClasif[k] || 0) + 1;
    });

    const opt = (val, label, count, activo) => {
      return `<option value="${val}" ${activo ? 'selected' : ''}>${label}</option>`;
    };

    const filtrosHtml = problemas.length > 1 ? `
      <style>
        @media (max-width: 767px) {
          .pnc-prob-filtros { flex-direction: column !important; align-items: stretch !important; }
          .pnc-prob-filtros > div { min-width: 0 !important; width: 100% !important; }
          .pnc-prob-filtros > button { width: 100% !important; }
          .pnc-prob-toolbar { flex-direction: column !important; align-items: stretch !important; }
          .pnc-prob-toolbar > div { width: 100%; justify-content: flex-start !important; }
          .pnc-prob-toolbar input { flex: 1; min-width: 0 !important; }
        }
      </style>
      <div class="pnc-prob-filtros" style="display:flex;flex-wrap:wrap;gap:0.75rem;margin-bottom:0.85rem;align-items:flex-end;">
        <div style="display:flex;flex-direction:column;gap:0.2rem;flex:1;min-width:180px;">
          <label style="font-size:0.7rem;color:var(--gray-500);text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Estatus</label>
          <select class="form-input" data-skip-unsaved style="font-size:0.85rem;" onchange="window.pncDetalleView.setFiltroProblema('estatus', this.value)">
            ${opt('todos', 'Todos', cntEst.todos, filtroEstatus === 'todos')}
            ${opt('pendiente', 'Pendientes', cntEst.pendiente, filtroEstatus === 'pendiente')}
            ${opt('en_proceso', 'En proceso', cntEst.en_proceso, filtroEstatus === 'en_proceso')}
            ${opt('corregido', 'Corregidos', cntEst.corregido, filtroEstatus === 'corregido')}
            ${cntEst.diferido > 0 ? opt('diferido', `Se corrigen con el pedido (${cntEst.diferido})`, cntEst.diferido, filtroEstatus === 'diferido') : ''}
          </select>
        </div>
        <div style="display:flex;flex-direction:column;gap:0.2rem;flex:1;min-width:180px;">
          <label style="font-size:0.7rem;color:var(--gray-500);text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Origen</label>
          <select class="form-input" data-skip-unsaved style="font-size:0.85rem;" onchange="window.pncDetalleView.setFiltroProblema('origen', this.value)">
            ${opt('todos', 'Todos', cntOri.todos, filtroOrigen === 'todos')}
            ${cntOri.recepcion > 0 ? opt('recepcion', 'Recepción', cntOri.recepcion, filtroOrigen === 'recepcion') : ''}
            ${cntOri.checklist_ensamble > 0 ? opt('checklist_ensamble', 'Checklist Ensamble', cntOri.checklist_ensamble, filtroOrigen === 'checklist_ensamble') : ''}
            ${cntOri.manual > 0 ? opt('manual', 'Manual', cntOri.manual, filtroOrigen === 'manual') : ''}
          </select>
        </div>
        <div style="display:flex;flex-direction:column;gap:0.2rem;flex:1;min-width:180px;">
          <label style="font-size:0.7rem;color:var(--gray-500);text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Tipo de daño</label>
          <select class="form-input" data-skip-unsaved style="font-size:0.85rem;" onchange="window.pncDetalleView.setFiltroProblema('clasificacion', this.value)">
            ${opt('todos', 'Todos', 0, filtroClasificacion === 'todos')}
            ${Object.keys(CLASIF_DANOS).filter(code => cntClasif[code]).map(code =>
              opt(code, `${CLASIF_DANOS[code].label} (${cntClasif[code]})`, cntClasif[code], filtroClasificacion === code)
            ).join('')}
            ${cntClasif['__sin__'] ? opt('__sin__', `Sin clasificar (${cntClasif['__sin__']})`, cntClasif['__sin__'], filtroClasificacion === '__sin__') : ''}
          </select>
        </div>
        ${hayFiltros ? `
          <button type="button" style="background:#f3f4f6;border:1px solid var(--gray-300);color:var(--gray-800);border-radius:6px;padding:0.45rem 0.9rem;cursor:pointer;font-size:0.85rem;font-weight:500;transition:background 0.15s;flex-shrink:0;" onmouseover="this.style.background='#e5e7eb';" onmouseout="this.style.background='#f3f4f6';" onclick="window.pncDetalleView.limpiarFiltrosProblema()">
            Limpiar filtros
          </button>
        ` : ''}
      </div>
    ` : '';

    // Barra superior de la tabla: page size (izq) + búsqueda rápida (der)
    const barraSuperiorHtml = problemas.length > 1 ? `
      <div class="pnc-prob-toolbar" style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:0.75rem;margin-bottom:0.75rem;">
        <div style="display:flex;align-items:center;gap:0.4rem;font-size:0.85rem;color:var(--gray-700);">
          Mostrar
          <select class="form-input" data-skip-unsaved style="width:70px;font-size:0.85rem;padding:0.3rem 0.4rem;" onchange="window.pncDetalleView.setPageSizeProblema(parseInt(this.value,10))">
            ${[5, 10, 25, 50].map(n => `<option value="${n}" ${pageSize === n ? 'selected' : ''}>${n}</option>`).join('')}
          </select>
          entradas
        </div>
        <div style="display:flex;align-items:center;gap:0.4rem;font-size:0.85rem;color:var(--gray-700);flex-wrap:nowrap;">
          <span style="white-space:nowrap;">Búsqueda rápida:</span>
          <input type="text" class="form-input" data-skip-unsaved value="${(this._busquedaRapidaProblema || '').replace(/"/g, '&quot;')}" placeholder="Buscar..." style="font-size:0.85rem;padding:0.3rem 0.5rem;min-width:200px;max-width:100%;" oninput="window.pncDetalleView.setBusquedaRapidaProblema(this.value)" />
        </div>
      </div>
    ` : '';

    // Paginación inferior
    const renderPagination = () => {
      // Si no hay >1 daño y nada que paginar, no mostrar nada
      if (problemas.length <= 1) return '';
      const pages = [];
      const around = 2;
      const showRange = (start, end) => {
        for (let i = start; i <= end; i++) pages.push(i);
      };
      if (totalPages <= 7) {
        showRange(1, totalPages);
      } else {
        pages.push(1);
        if (paginaActual - around > 2) pages.push('...');
        showRange(Math.max(2, paginaActual - around), Math.min(totalPages - 1, paginaActual + around));
        if (paginaActual + around < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
      const navStyle = (disabled) =>
        `padding:0.45rem 0.9rem;border:none;background:transparent;color:${disabled ? 'var(--gray-400)' : 'var(--gray-700)'};cursor:${disabled ? 'not-allowed' : 'pointer'};font-size:0.85rem;font-weight:500;`;
      const numStyle = (activo) =>
        `min-width:36px;height:36px;display:inline-flex;align-items:center;justify-content:center;padding:0 0.5rem;border:${activo ? 'none' : '1px solid var(--gray-200)'};background:${activo ? 'var(--primary)' : 'white'};color:${activo ? 'white' : 'var(--gray-700)'};border-radius:6px;cursor:pointer;font-size:0.85rem;font-weight:600;transition:background 0.15s;`;
      // Si hay solo 1 página, mostramos solo el texto "Mostrando..." sin botones
      const showButtons = totalPages > 1;
      return `
        <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:0.5rem;margin-top:0.85rem;font-size:0.85rem;color:var(--gray-600);">
          <div>Mostrando ${inicio} a ${fin} de ${totalFiltrado} entradas</div>
          ${showButtons ? `
            <div style="display:flex;align-items:center;gap:0.3rem;flex-wrap:wrap;">
              <button type="button" ${paginaActual === 1 ? 'disabled' : ''} style="${navStyle(paginaActual === 1)}" onclick="window.pncDetalleView.setPaginaProblema(${paginaActual - 1})">Anterior</button>
              ${pages.map(p =>
                p === '...'
                  ? `<span style="padding:0 0.4rem;color:var(--gray-500);">…</span>`
                  : `<button type="button" style="${numStyle(p === paginaActual)}" onclick="window.pncDetalleView.setPaginaProblema(${p})">${p}</button>`
              ).join('')}
              <button type="button" ${paginaActual === totalPages ? 'disabled' : ''} style="${navStyle(paginaActual === totalPages)}" onclick="window.pncDetalleView.setPaginaProblema(${paginaActual + 1})">Siguiente</button>
            </div>
          ` : ''}
        </div>
      `;
    };

    return `
      <div style="margin-top:1rem;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.75rem;">
          <h5 style="margin:0;font-size:0.85rem;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:var(--primary);">
            Daños
          </h5>
          ${!readonly ? `
            <button type="button" onclick="window.pncDetalleView.agregarProblema()"
              style="font-size: 0.85rem; border: 1px solid var(--gray-300); background: white; padding: 0.45rem 1rem; border-radius: 8px; cursor: pointer; color: var(--gray-700); font-weight: 600; transition: border-color 0.2s, color 0.2s;"
              onmouseover="this.style.borderColor='var(--primary)'; this.style.color='var(--primary)';"
              onmouseout="this.style.borderColor='var(--gray-300)'; this.style.color='var(--gray-700)';">
              + Agregar daño
            </button>
          ` : ''}
        </div>
        ${problemas.some(p => p.clasificacion) ? `
          <div style="font-size:0.78rem;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:0.6rem 0.85rem;margin-bottom:0.75rem;display:flex;gap:0.5rem;align-items:flex-start;">
            <span style="flex-shrink:0;">🧩</span>
            <span>En lugar de llenarlos daño por daño, la <strong>Identificación, el Plan, la Evaluación y los Anexos</strong> se capturan para todo el grupo.</span>
          </div>
        ` : ''}
        ${this._renderDecisionesCalidad(problemas)}
        ${filtrosHtml}
        ${barraSuperiorHtml}
        ${problemasFiltrados.length > 0
          ? this._renderTablaProblemas(problemasFiltrados, readonly)
          : (problemas.length > 0
              ? `<p style="color:var(--gray-500);font-size:0.85rem;font-style:italic;">Ningún daño coincide con los filtros activos.</p>`
              : `<p style="color:var(--gray-500);font-size:0.85rem;font-style:italic;">No hay daños registrados.</p>`
            )
        }
      </div>
    `;
  },

  // ==================== C-323b/c · DECISIONES DE CALIDAD POR DAÑO ====================
  // Un renglón por cada daño que no está corregido, con sus botones:
  //   «No es daño» y «Concesión» (sólo calidad o admin; el servidor también lo exige),
  //   «Se corrige con el pedido» / «Ya no espera el pedido» (con permiso de editar PNC).
  _esCalidadOAdmin() {
    const rol = authService.getUser()?.rol;
    // C-323d: también quien tenga el permiso «Decidir (Calidad)» del PNC (calidad interino); el servidor lo exige igual
    return rol === 'calidad' || rol === 'admin' || permisos.can('pnc', 'decidir_calidad');
  },

  _renderDecisionesCalidad(problemas) {
    const abiertos = (problemas || []).filter(p => p.estatus !== 'corregido');
    if (abiertos.length === 0) return '';
    const calidad = this._esCalidadOAdmin();
    const editar = permisos.canEdit('pnc');
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const etiqueta = (p) => {
      if (p.concesion_at) return { t: 'Concesión autorizada', bg: '#e0f2fe', fg: '#075985' };
      if (p.estatus === 'diferido') return { t: 'Se corrige con el pedido', bg: '#ede9fe', fg: '#5b21b6' };
      if (p.estatus === 'en_proceso') return { t: 'En proceso', bg: '#dbeafe', fg: '#1e40af' };
      return { t: 'Pendiente', bg: '#fef3c7', fg: '#78350f' };
    };
    const btn = (txt, fn, id, estilo) =>
      `<button type="button" class="btn btn-sm ${estilo}" data-decision="${fn}" data-dano="${id}" style="font-size:0.75rem;padding:0.25rem 0.6rem;" onclick="event.stopPropagation();window.pncDetalleView.${fn}(${id})">${txt}</button>`;
    const filas = abiertos.map((p) => {
      const e = etiqueta(p);
      const extra = p.concesion_at
        ? `<div style="font-size:0.75rem;color:#075985;margin-top:0.15rem;">Concesión: ${esc(p.concesion_motivo || '')}${p.concesion_por_nombre ? ' · ' + esc(p.concesion_por_nombre) : ''}</div>`
        : (p.estatus === 'diferido' && p.diferido_nota
          ? `<div style="font-size:0.75rem;color:#5b21b6;margin-top:0.15rem;">Pedido: ${esc(p.diferido_nota)}</div>` : '');
      const botones = [
        calidad ? btn('No es daño', 'noEsDano', p.id, 'btn-danger') : '',
        editar && p.estatus !== 'diferido' && !p.concesion_at ? btn('Se corrige con el pedido', 'diferirDano', p.id, 'btn-secondary') : '',
        editar && p.estatus === 'diferido' ? btn('Ya no espera el pedido', 'quitarDiferidoDano', p.id, 'btn-secondary') : '',
        calidad && !p.concesion_at ? btn('Concesión', 'concesionDano', p.id, 'btn-secondary') : ''
      ].join('');
      return `
        <div class="pnc-decision-fila" data-dano="${p.id}" style="display:flex;align-items:flex-start;gap:0.6rem;padding:0.5rem 0;border-top:1px solid #e9d5ff;flex-wrap:wrap;">
          <span style="color:var(--gray-500);font-size:0.8rem;min-width:2.2rem;">#${p.id}</span>
          <div style="flex:1;min-width:180px;">
            <div style="font-size:0.86rem;color:var(--gray-800);">${esc(this._formatDescripcionLegacy(p.descripcion))}</div>
            ${extra}
          </div>
          <span style="background:${e.bg};color:${e.fg};border-radius:999px;padding:0.1rem 0.55rem;font-size:0.7rem;font-weight:700;white-space:nowrap;">${e.t}</span>
          <div style="display:flex;gap:0.35rem;flex-wrap:wrap;">${botones || '<span style="font-size:0.75rem;color:var(--gray-400);">sin permiso</span>'}</div>
        </div>`;
    }).join('');
    const nPend = abiertos.filter(p => !p.concesion_at && p.estatus !== 'diferido').length;
    const nDif = abiertos.filter(p => !p.concesion_at && p.estatus === 'diferido').length;
    return `
      <div id="pncDecisionesCalidad" style="background:#faf5ff;border:1px solid #e9d5ff;border-radius:8px;padding:0.7rem 0.9rem;margin-bottom:0.85rem;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:0.5rem;flex-wrap:wrap;">
          <strong style="font-size:0.85rem;color:#5b21b6;">Decisiones de Calidad por daño</strong>
          <span style="font-size:0.75rem;color:#6b21a8;">${nPend} por corregir · ${nDif} se corrige${nDif === 1 ? '' : 'n'} con el pedido · mientras haya alguno, la unidad no se libera ni se entrega</span>
        </div>
        ${filas}
      </div>`;
  },

  _decisionBloqueadaPorCambios() {
    if (this._hayCambiosSinGuardar && this._hayCambiosSinGuardar()) {
      toast.warning('Guarda o descarta los cambios del PNC antes de tomar esta decisión');
      return true;
    }
    return false;
  },

  _modalMotivo({ titulo, texto, etiqueta, boton, claseBoton = 'btn-primary', url, campo, exito }) {
    if (this._decisionBloqueadaPorCambios()) return;
    modal.open(titulo, `
      <p style="margin:0 0 0.75rem;font-size:0.9rem;">${texto}</p>
      <div class="form-group">
        <label class="form-label">${etiqueta} *</label>
        <textarea id="pncDecisionMotivo" class="form-input" rows="3" maxlength="500" placeholder="Mínimo 10 letras"></textarea>
      </div>`,
    [
      { text: 'Cancelar', class: 'btn-secondary' },
      {
        text: boton, class: claseBoton, closeOnClick: false,
        onClick: async () => {
          const v = (document.getElementById('pncDecisionMotivo')?.value || '').trim();
          if (v.length < 10) { toast.error(`${etiqueta}: escribe al menos 10 letras`); return; }
          try {
            const r = await api.post(url, { [campo]: v });
            modal.close();
            toast.success(r?.message || exito);
            if (r?.data?.pnc_dado_de_baja) { router.navigate('/calidad/pnc'); return; }
            await this.cargarPNC(this.data.pnc.id);
          } catch (err) {
            toast.error(err.message || 'No se pudo guardar');
          }
        }
      }
    ]);
  },

  noEsDano(id) {
    this._modalMotivo({
      titulo: `No es daño · #${id}`,
      texto: 'El daño se da de baja (no se borra: queda en la historia con tu motivo y tu nombre). Si era el único daño del PNC, el PNC también se da de baja.',
      etiqueta: 'Motivo', boton: 'Dar de baja', claseBoton: 'btn-danger',
      url: `/calidad/pnc/problemas/${id}/no-es-dano`, campo: 'motivo', exito: 'Daño dado de baja'
    });
  },

  diferirDano(id) {
    this._modalMotivo({
      titulo: `Se corrige con el pedido · #${id}`,
      texto: 'El daño sigue abierto (la unidad no se libera ni se entrega), pero se separa de los pendientes porque espera un pedido o una refacción.',
      etiqueta: 'Qué pedido o refacción lo corrige', boton: 'Guardar',
      url: `/calidad/pnc/problemas/${id}/diferir`, campo: 'nota', exito: 'Daño marcado «Se corrige con el pedido»'
    });
  },

  async quitarDiferidoDano(id) {
    if (this._decisionBloqueadaPorCambios()) return;
    try {
      const r = await api.post(`/calidad/pnc/problemas/${id}/quitar-diferido`, {});
      toast.success(r?.message || 'El daño ya no espera el pedido');
      await this.cargarPNC(this.data.pnc.id);
    } catch (err) {
      toast.error(err.message || 'No se pudo guardar');
    }
  },

  concesionDano(id) {
    this._modalMotivo({
      titulo: `Concesión de Calidad · #${id}`,
      texto: 'Calidad acepta la unidad con este daño: deja de frenar la liberación y la entrega. Queda tu nombre, la fecha y el motivo.',
      etiqueta: 'Motivo de la concesión', boton: 'Autorizar concesión',
      url: `/calidad/pnc/problemas/${id}/concesion`, campo: 'motivo', exito: 'Concesión autorizada'
    });
  },

  setPageSizeProblema(size) {
    this._pageSizeProblema = size;
    this._paginaProblema = 1;
    if (this.data.pnc?.completado) this.renderPNCCompletado();
    else this.renderFormularioEdicion();
  },

  setBusquedaRapidaProblema(texto) {
    this._busquedaRapidaProblema = texto || '';
    this._paginaProblema = 1;
    if (this.data.pnc?.completado) this.renderPNCCompletado();
    else this.renderFormularioEdicion();
    // Refocar el input de búsqueda y mantener cursor al final (UX)
    requestAnimationFrame(() => {
      const inp = document.querySelector('input[oninput*="setBusquedaRapidaProblema"]');
      if (inp) {
        inp.focus();
        const len = inp.value.length;
        inp.setSelectionRange(len, len);
      }
    });
  },

  setPaginaProblema(p) {
    this._paginaProblema = p;
    if (this.data.pnc?.completado) this.renderPNCCompletado();
    else this.renderFormularioEdicion();
  },

  setFiltroProblema(grupo, valor) {
    if (grupo === 'estatus') this._filtroEstatus = valor;
    if (grupo === 'origen') this._filtroOrigen = valor;
    if (grupo === 'clasificacion') this._filtroClasificacion = valor;
    this._paginaProblema = 1;
    if (this.data.pnc?.completado) {
      this.renderPNCCompletado();
    } else {
      this.renderFormularioEdicion();
    }
  },

  limpiarFiltrosProblema() {
    this._filtroEstatus = 'todos';
    this._filtroOrigen = 'todos';
    this._filtroClasificacion = 'todos';
    this._busquedaRapidaProblema = '';
    this._paginaProblema = 1;
    if (this.data.pnc?.completado) {
      this.renderPNCCompletado();
    } else {
      this.renderFormularioEdicion();
    }
  },

  _formatDescripcionLegacy(desc) {
    if (!desc) return '';
    // Convierte el formato técnico viejo "[categoria_x] resto" en "Categoria x: resto"
    return String(desc).replace(/^\[([a-z_]+(?:\s*\/\s*[a-z_]+)?)\]\s*/i, (_, cat) => {
      const partes = cat.split('/').map(p => {
        const con = p.trim().replace(/_/g, ' ');
        return con.charAt(0).toUpperCase() + con.slice(1).toLowerCase()
          .replace(/inspeccion/g, 'Inspección')
          .replace(/electrico/g, 'Eléctrico')
          .replace(/hidraulico/g, 'Hidráulico')
          .replace(/direccion/g, 'Dirección');
      });
      return partes.join(' / ') + ': ';
    });
  },

  // Badge de clasificación (tipo de daño). code === null → "Sin clasificar".
  _clasifBadge(code, small = false) {
    const c = CLASIF_DANOS[code];
    const fs = small ? '0.65rem' : '0.7rem';
    if (!c) {
      return `<span style="background:#f3f4f6;color:#9ca3af;padding:0.1rem 0.45rem;border-radius:4px;font-size:${fs};font-weight:500;white-space:nowrap;border:1px dashed #d1d5db;">Sin clasificar</span>`;
    }
    return `<span style="background:${c.color}1a;color:${c.color};padding:0.1rem 0.45rem;border-radius:4px;font-size:${fs};font-weight:600;white-space:nowrap;">${c.label}</span>`;
  },

  // <option> del select de clasificación (las 4 del cliente).
  _clasifSelectOptions(selected) {
    const cur = selected || '';
    const head = `<option value="" ${cur === '' ? 'selected' : ''} disabled>— Selecciona tipo de daño —</option>`;
    const opts = Object.entries(CLASIF_DANOS)
      .map(([code, c]) => `<option value="${code}" ${cur === code ? 'selected' : ''}>${c.label}</option>`)
      .join('');
    return head + opts;
  },

  _renderTablaProblemas(problemas, readonly) {
    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const badgeStatus = (st) => {
      const colors = {
        pendiente: { bg: '#fef3c7', fg: '#78350f' },
        en_proceso: { bg: '#dbeafe', fg: '#1e40af' },
        corregido: { bg: '#d1fae5', fg: '#065f46' },
        diferido: { bg: '#ede9fe', fg: '#5b21b6' } // C-323c: «Se corrige con el pedido»
      };
      const c = colors[st] || { bg: '#e5e7eb', fg: '#374151' };
      if (st === 'diferido') st = 'Se corrige con el pedido';
      return `<span style="background:${c.bg};color:${c.fg};padding:0.15rem 0.5rem;border-radius:999px;font-size:0.7rem;font-weight:600;text-transform:uppercase;white-space:nowrap;">${st || 'pendiente'}</span>`;
    };

    const badgeOrigen = (mod) => {
      const label = ({ recepcion: 'Recepción', checklist_ensamble: 'Checklist', manual: 'Manual' }[mod] || mod || '—');
      const color = ({ recepcion: '#0891b2', checklist_ensamble: '#7c3aed', manual: '#64748b' }[mod] || '#64748b');
      return `<span style="background:${color}1a;color:${color};padding:0.15rem 0.5rem;border-radius:4px;font-size:0.7rem;font-weight:500;white-space:nowrap;">${label}</span>`;
    };

    const formatAccion = (a) => ({ concesion: 'Concesión', mantenimiento: 'Mantenimiento', reemplazo: 'Reemplazo' }[a] || '—');

    const truncate = (s, n) => {
      const t = String(s || '').trim();
      return t.length > n ? esc(t.slice(0, n)) + '…' : esc(t);
    };

    const isCompleta = (prob, sec) => {
      if (sec === 'identif') return !!(prob.descripcion && prob.detectado_por && prob.detectado_por_firma_url);
      if (sec === 'plan') return !!(prob.accion && prob.justificacion);
      if (sec === 'eval') return !!(prob.fecha_cierre && prob.detalles_accion && prob.resultado_final);
      if (sec === 'anexo') {
        const eliminarSet = this._getFotosPendientesEliminar();
        const pendientesPorProb = this._getFotosPendientesPorProblema()?.[prob.id] || { problema: [], solucion: [] };
        const fotosGuardadas = (Array.isArray(prob.fotos) ? prob.fotos : []).filter(f => !eliminarSet.has(f.id));
        const hasProblema = fotosGuardadas.some(f => f.tipo === 'problema') || (pendientesPorProb.problema?.length > 0);
        const hasSolucion = fotosGuardadas.some(f => f.tipo === 'solucion') || (pendientesPorProb.solucion?.length > 0);
        return hasProblema && hasSolucion;
      }
      return false;
    };

    const btnSeccion = (prob, key, label, titulo) => {
      const ok = isCompleta(prob, key);
      const bg = ok ? '#d1fae5' : '#f3f4f6';
      const fg = ok ? '#065f46' : '#6b7280';
      const border = ok ? '#10b981' : '#d1d5db';
      return `
        <button type="button" title="${titulo}${ok ? ' (completo)' : ''}"
          onclick="event.stopPropagation();window.pncDetalleView.abrirModalSeccion(${prob.id},'${key}')"
          style="background:${bg};color:${fg};border:1px solid ${border};border-radius:6px;padding:0.25rem 0.5rem;font-size:0.72rem;font-weight:600;cursor:pointer;margin-right:0.2rem;display:inline-flex;align-items:center;gap:0.25rem;">
          ${ok ? '✓' : '○'} ${label}
        </button>
      `;
    };

    const expandedId = this._expandedProblemaId;

    // Encabezado de grupo + panel "Solución del grupo" (agrupado visual + v2).
    // La lista ya viene ordenada por clasificación. El conteo y el estatus se
    // calculan sobre TODOS los daños del grupo (no solo la página).
    let prevClasif;
    const groupHeader = (prob) => {
      const key = prob.clasificacion || '__sin__';
      if (key === prevClasif) return '';
      const esPrimero = prevClasif === undefined;
      prevClasif = key;
      const meta = CLASIF_DANOS[prob.clasificacion];
      const label = meta ? meta.label : 'Sin clasificar';
      const miembros = (this.data.pnc?.problemas || []).filter(p => (p.clasificacion || '__sin__') === key);
      const n = miembros.length;
      const abierto = this._getGruposAbiertos().has(key);
      // Resuelto = clasificado + todos corregidos + guardado → color VERDE; el resto AZUL.
      const todosCorregidos = miembros.every(m => m.estatus === 'corregido');
      const pendiente = this._grupoTieneCambiosPendientes(miembros);
      const esResuelto = key !== '__sin__' && todosCorregidos && !pendiente;
      const color = esResuelto ? '#1D9E75' : '#378ADD';

      // Estado resumido (pill) para ver cómo va la categoría sin abrirla.
      // "✓ Resuelto" solo si está corregido Y guardado; si está completo pero con cambios
      // pendientes → "Listo · sin guardar" (aún no persiste).
      let estadoPill = '';
      if (key !== '__sin__') {
        if (todosCorregidos && !pendiente) {
          estadoPill = `<span style="background:#d1fae5;color:#065f46;border-radius:999px;padding:0.1rem 0.55rem;font-size:0.7rem;font-weight:700;white-space:nowrap;">✓ Resuelto</span>`;
        } else if (todosCorregidos && pendiente) {
          estadoPill = `<span style="background:#fef3c7;color:#92400e;border-radius:999px;padding:0.1rem 0.55rem;font-size:0.7rem;font-weight:700;white-space:nowrap;">Listo · sin guardar</span>`;
        } else {
          const done = ['identif', 'plan', 'eval', 'anexo'].filter(s => miembros.every(m => this._isProblemaSeccionCompleta(m, s))).length;
          estadoPill = `<span style="background:#fef3c7;color:#92400e;border-radius:999px;padding:0.1rem 0.55rem;font-size:0.7rem;font-weight:700;white-space:nowrap;">${done}/4 secciones</span>`;
        }
      } else {
        const corr = miembros.filter(m => m.estatus === 'corregido').length;
        estadoPill = `<span style="background:#eef2f6;color:#475569;border-radius:999px;padding:0.1rem 0.55rem;font-size:0.7rem;font-weight:600;white-space:nowrap;">${corr}/${n} corregidos</span>`;
      }

      // Aire entre grupos (fila espaciadora, salvo antes del primero)
      const spacer = esPrimero ? '' : `<tr aria-hidden="true"><td colspan="5" style="height:20px;background:transparent;border:none;padding:0;"></td></tr>`;
      // El estilo va en un <div> interno (los border-radius del <td> se ignoran por border-collapse).
      // Abierto: redondeado solo arriba (se une al panel). Cerrado: redondeado completo.
      const headRadius = abierto
        ? 'border-bottom:none;border-radius:var(--border-radius-lg) var(--border-radius-lg) 0 0;'
        : 'border-radius:var(--border-radius-lg);box-shadow:var(--shadow);';
      const headRow = `
        <tr class="pnc-clasif-group" style="cursor:pointer;" onclick="window.pncDetalleView.toggleGrupo('${key}')">
          <td colspan="5" style="padding:0 0.4rem;background:transparent;border:none;">
            <div style="background:#fff;border:1px solid var(--gray-200);border-left:4px solid ${color};${headRadius}padding:0.65rem 0.95rem;display:flex;align-items:center;gap:0.6rem;">
              <span style="color:${color};font-weight:800;font-size:0.72rem;flex-shrink:0;">${abierto ? '▾' : '▸'}</span>
              <span style="font-weight:800;font-size:0.88rem;color:${color};letter-spacing:0.2px;flex:1;min-width:0;">${label} · ${n} daño${n === 1 ? '' : 's'}</span>
              ${estadoPill}
            </div>
          </td>
        </tr>
      `;
      // El panel (botones/solución) solo se muestra si el grupo está ABIERTO.
      return spacer + headRow + (abierto && key !== '__sin__' ? this._grupoSolucionRow(key) : '');
    };

    const filas = problemas.map((prob, i) => {
      const grpKey = prob.clasificacion || '__sin__';
      const header = groupHeader(prob);
      // Grupo cerrado (acordeón): solo su encabezado.
      if (!this._getGruposAbiertos().has(grpKey)) return header;
      // Grupo CLASIFICADO abierto: sus daños van DENTRO de la card (el header ya los incluye).
      if (grpKey !== '__sin__') return header;
      // Solo "Sin clasificar" abierto renderiza renglones por daño (con su expansión).
      const isExpanded = expandedId === prob.id;
      const expandedContent = `
            <div style="background:${prob.estatus === 'corregido' ? '#f0fdf4' : '#f8fafc'};padding:1rem 1.25rem;border-top:1px solid ${prob.estatus === 'corregido' ? '#10b981' : '#dbeafe'};" onclick="event.stopPropagation();">
              ${(() => {
                const isCorregido = prob.estatus === 'corregido';
                const origenHtml = this._renderOrigenInfoCompacto(prob);
                if (!isCorregido) {
                  return this._renderOrigenInfo(prob) + this._renderOrigenEstatusCambiadoBadge(prob);
                }
                const fmtFecha = (f) => {
                  if (!f) return '—';
                  const [y, m, d] = String(f).substring(0, 10).split('-');
                  return d && m && y ? `${d}/${m}/${y}` : '—';
                };
                const fechasInfo = (() => {
                  if (!prob.fecha_deteccion || !prob.fecha_cierre) return '';
                  const d1 = new Date(String(prob.fecha_deteccion).substring(0, 10));
                  const d2 = new Date(String(prob.fecha_cierre).substring(0, 10));
                  const dias = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
                  if (isNaN(dias) || dias < 0) return '';
                  const totalTxt = dias === 0 ? 'el mismo día' : (dias === 1 ? '1 día' : `${dias} días`);
                  return `
                    <div style="display:flex;flex-wrap:wrap;align-items:center;gap:0.4rem 0.75rem;font-size:0.75rem;color:#374151;margin-top:0.25rem;">
                      <span><strong>Detección:</strong> ${fmtFecha(prob.fecha_deteccion)}</span>
                      <span style="color:#6366f1;">→</span>
                      <span><strong>Cierre:</strong> ${fmtFecha(prob.fecha_cierre)}</span>
                      <span style="color:#6b7280;">(${totalTxt})</span>
                    </div>
                  `;
                })();
                return `
                  <div style="background:#eef2ff;border:1px solid #6366f1;border-radius:8px;padding:0.65rem 1rem;margin-bottom:0.75rem;">
                    <div style="display:flex;align-items:center;gap:0.6rem;flex-wrap:wrap;">
                      <div style="width:28px;height:28px;border-radius:50%;background:#6366f1;color:white;display:flex;align-items:center;justify-content:center;font-weight:700;flex-shrink:0;">✓</div>
                      <div style="flex:1;min-width:0;">
                        <div style="font-weight:700;color:#111827;font-size:0.9rem;">Daño cerrado</div>
                        ${fechasInfo}
                      </div>
                      ${permisos.canPrint('pnc') ? `
                        <button type="button" title="Imprimir solo este daño" style="background:white;color:#3730a3;border:1px solid #6366f1;border-radius:6px;padding:0.35rem 0.7rem;font-size:0.78rem;font-weight:600;cursor:pointer;flex-shrink:0;" onclick="window.pncDetalleView.imprimirProblema(${prob.id})">🖨️ Imprimir</button>
                      ` : ''}
                    </div>
                    ${origenHtml ? `<div style="margin-top:0.5rem;padding-top:0.5rem;border-top:1px dashed #6366f1;">${origenHtml}</div>` : ''}
                  </div>
                `;
              })()}
              <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:0.6rem;">
                ${(() => {
                  // Daños CLASIFICADOS: Identificación y Solución se capturan arriba, a nivel grupo.
                  // La pieza es solo su descripción (sin resumen ni botones por pieza).
                  if (prob.clasificacion) {
                    return '';
                  }
                  // Daños SIN CLASIFICAR: flujo por daño completo (4 secciones).
                  if (prob.estatus === 'corregido') {
                    return `
                      ${this._renderBotonSeccion(prob, 'identif', '1. Identificación', 'Solo lectura', true, false, null)}
                      ${this._renderBotonSeccion(prob, 'plan', '2. Plan de acción', 'Solo lectura', true, false, null)}
                      ${this._renderBotonSeccion(prob, 'eval', '3. Evaluación', 'Solo lectura', true, false, null)}
                      ${this._renderBotonSeccion(prob, 'anexo', 'Anexo', 'Solo lectura', true, false, null)}
                    `;
                  }
                  const identifOk = isCompleta(prob, 'identif');
                  const planOk = isCompleta(prob, 'plan');
                  const evalLocked = !identifOk || !planOk;
                  const evalReason = !identifOk ? 'Completa Identificación primero' : 'Completa Plan de acción primero';
                  return `
                    ${this._renderBotonSeccion(prob, 'identif', '1. Identificación', 'Descripción + detectado por', identifOk, false, null)}
                    ${this._renderBotonSeccion(prob, 'plan', '2. Plan de acción', 'Acción + justificación', planOk, !identifOk, 'Completa Identificación primero')}
                    ${this._renderBotonSeccion(prob, 'eval', '3. Evaluación', 'Verificación + cierre', isCompleta(prob, 'eval'), evalLocked, evalReason)}
                    ${this._renderBotonSeccion(prob, 'anexo', 'Anexo', 'Fotos del problema y solución', isCompleta(prob, 'anexo'), false, null)}
                  `;
                })()}
              </div>
              ${!readonly && prob.estatus !== 'corregido' && (prob.origen_modulo === 'manual' || prob.origen_estatus_cambiado_at) ? `
                <div style="display:flex;gap:0.5rem;justify-content:flex-end;margin-top:0.85rem;flex-wrap:wrap;">
                  <button class="btn btn-danger btn-sm" type="button" onclick="window.pncDetalleView.eliminarProblema(${prob.id})">Eliminar daño</button>
                </div>
              ` : ''}
            </div>
      `;
      // Los daños CLASIFICADOS no expanden (todo se gestiona en el panel del grupo): renglón simple.
      const esClasificado = !!prob.clasificacion;
      const expandido = !esClasificado && isExpanded;
      return header + `
        <tr style="${esClasificado ? '' : 'cursor:pointer;'}${expandido ? 'background:#eff6ff;' : ''}" ${esClasificado ? '' : `onclick="window.pncDetalleView.toggleExpandirProblema(${prob.id})"`}>
          <td style="width:40px;">
            ${esClasificado ? '' : `<button class="tbl-expand-btn ${isExpanded ? 'open' : ''}" type="button" style="display:inline-block;" onclick="event.stopPropagation();window.pncDetalleView.toggleExpandirProblema(${prob.id})">&#9654;</button>`}
          </td>
          <td style="color:var(--gray-500);font-size:0.85rem;">${i + 1}</td>
          <td><strong>${truncate(this._formatDescripcionLegacy(prob.descripcion), 100)}</strong></td>
          <td class="tbl-hide-mobile">${badgeOrigen(prob.origen_modulo)}</td>
          <td class="tbl-hide-mobile">${badgeStatus(prob.estatus)}</td>
        </tr>
        ${expandido ? `
          <tr>
            <td colspan="5" style="padding:0;border-top:none;">
              <div style="display:none;padding:0.5rem 1rem;background:var(--gray-50);border-bottom:1px solid var(--gray-200);" class="pnc-prob-mobile-meta">
                <dl class="tbl-detail-grid" style="margin:0;">
                  <dt>Origen</dt><dd>${badgeOrigen(prob.origen_modulo)}</dd>
                  <dt>Estatus</dt><dd>${badgeStatus(prob.estatus)}</dd>
                </dl>
              </div>
              ${expandedContent}
            </td>
          </tr>
        ` : ''}
      `;
    }).join('');

    return `
      <style>
        @media (max-width: 768px) {
          .pnc-prob-mobile-meta { display: block !important; }
        }
      </style>
      <div class="pnc-prob-table" style="background:transparent;">
        <div style="display:flex;align-items:center;justify-content:space-between;padding:0.4rem 1rem 0.55rem;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.6px;color:var(--gray-500);font-weight:700;border-bottom:1px solid var(--gray-200);margin-bottom:0.4rem;">
          <span>Categoría</span>
          <span>Estado</span>
        </div>
        <table class="table" style="margin:0;">
          <tbody>
            ${filas}
          </tbody>
        </table>
      </div>
    `;
  },

  _renderBotonSeccion(prob, key, titulo, subtitulo, completa, locked = false, lockReason = null) {
    if (locked) {
      const tooltip = lockReason || 'Sección bloqueada';
      return `
        <button type="button" disabled title="${tooltip}"
          style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:0.75rem;text-align:left;cursor:not-allowed;display:flex;align-items:center;gap:0.6rem;width:100%;opacity:0.6;">
          <div style="width:32px;height:32px;border-radius:50%;background:#e5e7eb;color:#9ca3af;display:flex;align-items:center;justify-content:center;font-weight:700;flex-shrink:0;">
            🔒
          </div>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:0.85rem;color:var(--gray-500);">${titulo}</div>
            <div style="font-size:0.7rem;color:var(--gray-400);margin-top:0.1rem;font-style:italic;">${tooltip}</div>
          </div>
        </button>
      `;
    }
    const bg = completa ? '#ecfdf5' : '#ffffff';
    const border = completa ? '#10b981' : '#e5e7eb';
    const iconBg = completa ? '#10b981' : '#f3f4f6';
    const iconFg = completa ? '#ffffff' : '#9ca3af';
    return `
      <button type="button"
        onclick="event.stopPropagation();window.pncDetalleView.abrirModalSeccion(${prob.id}, '${key}')"
        style="background:${bg};border:1px solid ${border};border-radius:8px;padding:0.75rem;text-align:left;cursor:pointer;display:flex;align-items:center;gap:0.6rem;transition:all 0.15s;width:100%;">
        <div style="width:32px;height:32px;border-radius:50%;background:${iconBg};color:${iconFg};display:flex;align-items:center;justify-content:center;font-weight:700;flex-shrink:0;">
          ${completa ? '✓' : '○'}
        </div>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--gray-800);">${titulo}</div>
          <div style="font-size:0.7rem;color:var(--gray-500);margin-top:0.1rem;">${subtitulo}</div>
        </div>
        <span style="color:var(--gray-400);font-size:0.85rem;flex-shrink:0;">›</span>
      </button>
    `;
  },

  // ==================== TRABAJO POR GRUPO (v2) ====================
  // Un grupo se resuelve como bloque: Identificación (detector+firma), Plan, Evaluación y
  // Anexos se capturan UNA vez reusando las MISMAS modales por daño (contra una pieza
  // representante) y el resultado se hace fan-out al resto del grupo.
  // La DESCRIPCIÓN queda por pieza.
  _grupoMiembros(key) {
    return (this.data.pnc?.problemas || []).filter(p => (p.clasificacion || '__sin__') === key);
  },

  // Estado abierto/cerrado de los grupos (acordeón). Cerrados por defecto.
  _getGruposAbiertos() {
    if (!this._gruposAbiertos) this._gruposAbiertos = new Set();
    return this._gruposAbiertos;
  },

  toggleGrupo(key) {
    const s = this._getGruposAbiertos();
    if (s.has(key)) s.delete(key); else s.add(key);
    if (this.data.pnc?.completado) this.renderPNCCompletado(); else this.renderFormularioEdicion();
  },

  // Grupos "resueltos" que el usuario reabrió para editar su solución (candado suave).
  // NO aplica cuando el PNC ya está completado (ese candado es duro).
  _getGruposEnEdicion() {
    if (!this._gruposEnEdicion) this._gruposEnEdicion = new Set();
    return this._gruposEnEdicion;
  },

  editarSolucionGrupo(key) {
    this._getGruposEnEdicion().add(key);
    if (this.data.pnc?.completado) this.renderPNCCompletado(); else this.renderFormularioEdicion();
  },

  cancelarEdicionGrupo(key) {
    this._getGruposEnEdicion().delete(key);
    if (this.data.pnc?.completado) this.renderPNCCompletado(); else this.renderFormularioEdicion();
  },

  // ¿Alguno de los daños del grupo tiene cambios sin persistir? (dirty / firma / fotos pendientes)
  // Se usa para NO mostrar "Resuelto"/Imprimir hasta que de verdad se guarde.
  _grupoTieneCambiosPendientes(members) {
    const dirty = this._getProblemasDirty();
    const firmas = this._getProblemasFirmasPendientes();
    const fotos = this._getFotosPendientesPorProblema();
    return members.some(m => {
      if (dirty[m.id]) return true;
      if (firmas[m.id]) return true;
      const f = fotos[m.id];
      if (f && ((f.problema?.length || 0) + (f.solucion?.length || 0) > 0)) return true;
      return false;
    });
  },

  // Fila-panel del grupo: 4 secciones (Identificación, Plan, Evaluación, Anexos) a nivel grupo.
  _grupoSolucionRow(key) {
    const meta = CLASIF_DANOS[key];
    const members = this._grupoMiembros(key);
    if (members.length === 0) return '';
    // Verde si el grupo está resuelto (corregido y guardado); azul si no — para que el
    // acento del panel coincida con el encabezado.
    const resueltoGrupo = members.every(m => m.estatus === 'corregido') && !this._grupoTieneCambiosPendientes(members);
    const color = resueltoGrupo ? '#1D9E75' : '#378ADD';

    // El ORIGEN no se comparte entre daños (cada uno viene de donde se creó). Detectar si
    // todos comparten origen para mostrarlo una vez; si no, se muestra un badge por daño.
    const origenSig = (m) => `${m.origen_modulo || 'manual'}|${m.origen_id || ''}`;
    const mismoOrigen = members.every(m => origenSig(m) === origenSig(members[0]));
    const badgeOri = (mod) => {
      const label = ({ recepcion: 'Recepción', checklist_ensamble: 'Checklist', manual: 'Manual' }[mod] || mod || 'Manual');
      const c = ({ recepcion: '#0891b2', checklist_ensamble: '#7c3aed', manual: '#64748b' }[mod] || '#64748b');
      return `<span style="background:${c}1a;color:${c};padding:0.05rem 0.4rem;border-radius:4px;font-size:0.65rem;font-weight:600;white-space:nowrap;">${label}</span>`;
    };
    const okIdentif = members.every(m => this._isProblemaSeccionCompleta(m, 'identif'));
    const okPlan  = members.every(m => this._isProblemaSeccionCompleta(m, 'plan'));
    const okEval  = members.every(m => this._isProblemaSeccionCompleta(m, 'eval'));
    const okAnexo = members.every(m => this._isProblemaSeccionCompleta(m, 'anexo'));
    const readonly = !!this.data.pnc?.completado;
    const b = (sec, titulo, sub, ok, locked, lockReason) => this._grupoBotonSeccion(key, sec, titulo, sub, ok, locked, lockReason);
    const botones = readonly
      ? `${b('identif', 'Identificación', 'Solo lectura', true, false, null)}${b('plan', 'Plan de acción', 'Solo lectura', true, false, null)}${b('eval', 'Evaluación', 'Solo lectura', true, false, null)}${b('anexo', 'Anexos', 'Solo lectura', true, false, null)}`
      : `${b('identif', '1. Identificación', 'Detector + firma (grupo)', okIdentif, false, null)}${b('plan', '2. Plan de acción', 'Acción + justificación', okPlan, !okIdentif, 'Completa Identificación primero')}${b('eval', '3. Evaluación', 'Solución + cierre', okEval, !okIdentif || !okPlan, !okIdentif ? 'Completa Identificación primero' : 'Completa Plan primero')}${b('anexo', 'Anexos', 'Fotos del problema y solución', okAnexo, false, null)}`;

    // Card "Grupo resuelto": solo cuando TODOS los daños están corregidos Y YA GUARDADOS
    // (si hay cambios pendientes, no tiene sentido mostrar Imprimir ni "resuelto").
    let resueltoCard = '';
    if (members.every(m => m.estatus === 'corregido') && !this._grupoTieneCambiosPendientes(members)) {
      const rep = members[0];
      const fmt = (f) => { if (!f) return '—'; const [y, m, d] = String(f).substring(0, 10).split('-'); return d && m && y ? `${d}/${m}/${y}` : '—'; };
      let fechas = '';
      if (rep.fecha_deteccion && rep.fecha_cierre) {
        const d1 = new Date(String(rep.fecha_deteccion).substring(0, 10));
        const d2 = new Date(String(rep.fecha_cierre).substring(0, 10));
        const dias = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
        const txt = (isNaN(dias) || dias < 0) ? '' : (dias === 0 ? 'el mismo día' : (dias === 1 ? '1 día' : `${dias} días`));
        fechas = `<span><strong>Detección:</strong> ${fmt(rep.fecha_deteccion)}</span> <span style="color:#6366f1;">→</span> <span><strong>Cierre:</strong> ${fmt(rep.fecha_cierre)}</span>${txt ? ` <span style="color:#6b7280;">(${txt})</span>` : ''}`;
      }
      const origenHtml = mismoOrigen ? this._renderOrigenInfoCompacto(rep) : '';
      const printBtn = permisos.canPrint('pnc')
        ? `<button type="button" title="Imprimir esta categoría" style="background:white;color:#3730a3;border:1px solid #6366f1;border-radius:6px;padding:0.3rem 0.65rem;font-size:0.76rem;font-weight:600;cursor:pointer;flex-shrink:0;" onclick="event.stopPropagation();window.pncDetalleView.imprimirGrupo('${key}')">🖨️ Imprimir</button>`
        : '';
      // Candado suave: mientras el PNC NO esté completado, se puede reabrir la solución
      // de un grupo resuelto para editarla ("Editar solución"). Al Guardar se re-cierra.
      const enEd = this._getGruposEnEdicion().has(key);
      const editBtn = (!this.data.pnc?.completado && !enEd)
        ? `<button type="button" title="Reabrir la solución de este grupo para editarla" style="background:white;color:#b45309;border:1px solid #d97706;border-radius:6px;padding:0.3rem 0.65rem;font-size:0.76rem;font-weight:600;cursor:pointer;flex-shrink:0;" onclick="event.stopPropagation();window.pncDetalleView.editarSolucionGrupo('${key}')">✏️ Editar solución</button>`
        : '';
      const editNota = enEd
        ? `<div style="margin-top:0.5rem;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:0.4rem 0.6rem;font-size:0.75rem;color:#92400e;display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;"><span>✏️ Editando la solución — abre una sección, ajusta y presiona <strong>Guardar Cambios</strong> para volver a cerrar.</span><a href="javascript:void(0)" style="color:#92400e;font-weight:600;text-decoration:underline;white-space:nowrap;" onclick="event.stopPropagation();window.pncDetalleView.cancelarEdicionGrupo('${key}')">Cancelar edición</a></div>`
        : '';
      resueltoCard = `
        <div style="background:#eef2ff;border:1px solid #6366f1;border-radius:8px;padding:0.6rem 0.9rem;margin-bottom:0.6rem;">
          <div style="display:flex;align-items:center;gap:0.6rem;flex-wrap:wrap;">
            <div style="width:26px;height:26px;border-radius:50%;background:#6366f1;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;flex-shrink:0;">✓</div>
            <div style="font-weight:700;color:#111827;font-size:0.88rem;flex:1;min-width:0;">Grupo resuelto</div>
            ${editBtn}
            ${printBtn}
          </div>
          ${editNota}
          ${fechas ? `<div style="display:flex;flex-wrap:wrap;gap:0.25rem 0.7rem;font-size:0.75rem;color:#374151;margin-top:0.45rem;">${fechas}</div>` : ''}
          ${origenHtml ? `<div style="margin-top:0.45rem;padding-top:0.45rem;border-top:1px dashed #6366f1;font-size:0.78rem;">${origenHtml}</div>` : ''}
        </div>
      `;
    }

    // Lista de daños DENTRO de la card (solo # · descripción; sin estatus/origen por daño).
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const dañosList = `
      <div style="margin-top:0.85rem;padding-top:0.75rem;border-top:1px solid var(--gray-200);">
        <div style="font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--gray-500);font-weight:700;margin-bottom:0.35rem;">Daños</div>
        ${members.map((m, i) => `
          <div style="display:flex;align-items:baseline;gap:0.55rem;padding:0.28rem 0;${i > 0 ? 'border-top:1px solid var(--gray-100);' : ''}">
            <span style="color:var(--gray-400);font-size:0.8rem;min-width:1.1rem;text-align:right;">${i + 1}</span>
            <span style="font-size:0.88rem;color:var(--gray-800);flex:1;min-width:0;">${esc(this._formatDescripcionLegacy(m.descripcion))}</span>
            ${!mismoOrigen ? badgeOri(m.origen_modulo) : ''}
          </div>
        `).join('')}
      </div>
    `;
    // Origen a nivel grupo (una vez), SOLO si todos comparten origen. Si difieren, va por daño (arriba).
    const origenGrupo = mismoOrigen ? this._renderOrigenInfoCompacto(members[0]) : '';
    const origenBlock = (!resueltoCard && origenGrupo)
      ? `<div style="margin-top:0.6rem;padding-top:0.5rem;border-top:1px dashed var(--gray-200);font-size:0.78rem;color:var(--gray-600);">${origenGrupo}</div>`
      : '';

    // El panel se une al encabezado: sin borde/redondeo arriba, redondeado abajo.
    return `
      <tr class="pnc-grupo-sol">
        <td colspan="5" style="padding:0 0.4rem 0.3rem;background:transparent;border:none;">
          <div style="background:#fff;border:1px solid var(--gray-200);border-top:none;border-left:4px solid ${color};border-radius:0 0 var(--border-radius-lg) var(--border-radius-lg);box-shadow:var(--shadow);padding:1rem;">
            ${resueltoCard}
            <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:0.6rem;">
              ${botones}
            </div>
            ${dañosList}
            ${origenBlock}
          </div>
        </td>
      </tr>
    `;
  },

  _grupoBotonSeccion(key, sec, titulo, subtitulo, completa, locked, lockReason) {
    if (locked) {
      const tip = lockReason || 'Sección bloqueada';
      return `
        <button type="button" disabled title="${tip}" style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:0.75rem;text-align:left;cursor:not-allowed;display:flex;align-items:center;gap:0.6rem;width:100%;opacity:0.6;">
          <div style="width:32px;height:32px;border-radius:50%;background:#e5e7eb;color:#9ca3af;display:flex;align-items:center;justify-content:center;font-weight:700;flex-shrink:0;">🔒</div>
          <div style="flex:1;min-width:0;"><div style="font-weight:600;font-size:0.85rem;color:var(--gray-500);">${titulo}</div><div style="font-size:0.7rem;color:var(--gray-400);margin-top:0.1rem;font-style:italic;">${tip}</div></div>
        </button>`;
    }
    const bg = completa ? '#ecfdf5' : 'var(--gray-50)';
    const border = completa ? '#10b981' : '#e5e7eb';
    const iconBg = completa ? '#10b981' : '#f3f4f6';
    const iconFg = completa ? '#ffffff' : '#9ca3af';
    return `
      <button type="button" onclick="event.stopPropagation();window.pncDetalleView.abrirModalSeccionGrupo('${key}','${sec}')" style="background:${bg};border:1px solid ${border};border-radius:8px;padding:0.75rem;text-align:left;cursor:pointer;display:flex;align-items:center;gap:0.6rem;width:100%;">
        <div style="width:32px;height:32px;border-radius:50%;background:${iconBg};color:${iconFg};display:flex;align-items:center;justify-content:center;font-weight:700;flex-shrink:0;">${completa ? '✓' : '○'}</div>
        <div style="flex:1;min-width:0;"><div style="font-weight:600;font-size:0.85rem;color:var(--gray-800);">${titulo}</div><div style="font-size:0.7rem;color:var(--gray-500);margin-top:0.1rem;">${subtitulo}</div></div>
        <span style="color:var(--gray-400);font-size:0.85rem;flex-shrink:0;">›</span>
      </button>`;
  },

  // Abre la MISMA modal por daño (Identificación/Plan/Evaluación/Anexos) contra la pieza
  // representante, en modo grupo. Al guardar se hace fan-out al resto (guardarSeccionGrupo).
  abrirModalSeccionGrupo(key, seccion) {
    const members = this._grupoMiembros(key);
    if (members.length === 0) return;
    const rep = members[0];
    // Candado DURO: PNC completado. Candado SUAVE: grupo resuelto (todos corregidos),
    // salvo que el usuario lo haya reabierto con "Editar solución" (_gruposEnEdicion).
    const readonly = !!this.data.pnc?.completado
      || (seccion !== 'anexo' && members.every(m => m.estatus === 'corregido') && !this._getGruposEnEdicion().has(key));

    const renderers = { identif: this._renderModalIdentif, plan: this._renderModalPlan, eval: this._renderModalEval, anexo: this._renderModalAnexo };
    const titulos = { identif: 'Identificación', plan: 'Plan de acción', eval: 'Evaluación', anexo: 'Anexos (fotos)' };
    // La Identificación de grupo oculta la descripción por-pieza y muestra la lista de piezas.
    const html = seccion === 'identif'
      ? this._renderModalIdentif(rep, readonly, members)
      : renderers[seccion].call(this, rep, readonly);

    // Anexo: las fotos se capturan sobre el representante; marcamos contexto de grupo
    // para que los handlers de foto reabran ESTA modal y para el fan-out final.
    this._anexoGrupoKey = (seccion === 'anexo') ? key : null;

    const buttons = readonly
      ? [{ text: 'Cerrar', class: 'btn-secondary', onClick: () => { this._anexoGrupoKey = null; } }]
      : (seccion === 'anexo'
          ? [{
              text: 'Aplicar fotos al grupo', class: 'btn-primary', closeOnClick: false,
              onClick: () => { this.fanOutAnexoGrupo(key); this._anexoGrupoKey = null; modal.close(); }
            }]
          : [
              { text: 'Cancelar', class: 'btn-secondary', onClick: () => { this._anexoGrupoKey = null; } },
              {
                text: 'Guardar', class: 'btn-primary', closeOnClick: false,
                onClick: async () => { if (await this.guardarSeccionGrupo(key, seccion)) modal.close(); }
              }
            ]);

    modal.open(
      `<span style="color:var(--primary);">${titulos[seccion]}</span>`,
      html,
      buttons
    );

    if (seccion === 'identif' && !readonly && !rep.detectado_por_firma_url) {
      this._probFirmaReemplazar = null;
      setTimeout(() => this._initFirmaProblemaModal(rep.id), 50);
    }
  },

  // Guarda la sección en el representante (reusa guardarSeccionProblema) y hace fan-out al resto.
  async guardarSeccionGrupo(key, seccion) {
    const members = this._grupoMiembros(key);
    if (members.length === 0) return false;
    const rep = members[0];

    // 1) Reusar la lógica existente para validar + guardar en el representante
    const ok = await this.guardarSeccionProblema(rep.id, seccion);
    if (!ok) return false;

    // 2) Campos compartidos por sección (la DESCRIPCIÓN nunca se comparte)
    const camposPorSeccion = {
      identif: ['detectado_por', 'fecha_deteccion', 'evidencia_whatsapp_problema'],
      plan: ['accion', 'justificacion'],
      eval: ['fecha_cierre', 'evidencia_whatsapp_solucion', 'detalles_accion', 'resultado_final']
    };
    const campos = camposPorSeccion[seccion] || [];
    const dirty = this._getProblemasDirty();
    const repDirty = dirty[rep.id] || {};
    const shared = {};
    for (const c of campos) if (c in repDirty) shared[c] = repDirty[c];

    // Firma (identif): copiar el mismo blob del representante a las demás piezas
    const firmasPend = this._getProblemasFirmasPendientes();
    const repFirmaBlob = (seccion === 'identif') ? firmasPend[rep.id] : null;

    for (const m of members) {
      if (m.id === rep.id) continue;
      const idx = this.data.pnc.problemas.findIndex(p => p.id === m.id);
      if (idx < 0) continue;
      this.data.pnc.problemas[idx] = { ...this.data.pnc.problemas[idx], ...shared };
      dirty[m.id] = { ...(dirty[m.id] || {}), ...shared };
      if (repFirmaBlob) {
        const old = this.data.pnc.problemas[idx].detectado_por_firma_url;
        if (old && old !== '__pendiente__') this._getFirmasViejasABorrar().add(old);
        firmasPend[m.id] = repFirmaBlob;
        this.data.pnc.problemas[idx].detectado_por_firma_url = '__pendiente__';
      }
      const nuevo = this._computeEstatusProblema(this.data.pnc.problemas[idx]);
      this.data.pnc.problemas[idx].estatus = nuevo;
      dirty[m.id].estatus = nuevo;
    }

    if (this.data.pnc?.completado) this.renderPNCCompletado(); else this.renderFormularioEdicion();
    this._showUnsaved(); // DESPUÉS del re-render, si no el render lo oculta
    toast.success(`Aplicado a ${members.length} daño${members.length === 1 ? '' : 's'} del grupo`);
    return true;
  },

  // Copia las fotos PENDIENTES del representante al resto de las piezas (sobrescribe, idempotente).
  fanOutAnexoGrupo(key) {
    const members = this._grupoMiembros(key);
    if (members.length === 0) return;
    const rep = members[0];
    const pend = this._getFotosPendientesPorProblema();
    const repPend = pend[rep.id] || { problema: [], solucion: [] };
    const copiar = (lista) => (lista || []).map(ph => ({ file: ph.file, previewUrl: URL.createObjectURL(ph.file), tipo: ph.tipo }));
    for (const m of members) {
      if (m.id === rep.id) continue;
      pend[m.id] = { problema: copiar(repPend.problema), solucion: copiar(repPend.solucion) };
      const idx = this.data.pnc.problemas.findIndex(p => p.id === m.id);
      if (idx >= 0) {
        const nuevo = this._computeEstatusProblema(this.data.pnc.problemas[idx]);
        this.data.pnc.problemas[idx].estatus = nuevo;
        this._getProblemasDirty()[m.id] = { ...(this._getProblemasDirty()[m.id] || {}), estatus: nuevo };
      }
    }
    if (this.data.pnc?.completado) this.renderPNCCompletado(); else this.renderFormularioEdicion();
    this._showUnsaved(); // DESPUÉS del re-render, si no el render lo oculta
    toast.success(`Fotos aplicadas a ${members.length} daño${members.length === 1 ? '' : 's'} del grupo`);
  },

  abrirModalSeccion(problemaId, seccion) {
    const prob = (this.data.pnc?.problemas || []).find(p => p.id === problemaId);
    if (!prob) return;
    this._anexoGrupoKey = null; // modal por daño → salir de cualquier contexto de grupo
    // Daño corregido → modal en solo lectura (sin botón Guardar, sin edición).
    // Excepción: el Anexo (fotos) sigue editable mientras el PNC no esté finalizado,
    // porque agregar evidencia no altera el diagnóstico y una falla se marca
    // "corregido" en cuanto tiene 1 foto de cada tipo (lo que impedía sumar más).
    const readonly = !!this.data.pnc.completado
      || (seccion !== 'anexo' && prob.estatus === 'corregido');

    const renderers = {
      identif: this._renderModalIdentif,
      plan: this._renderModalPlan,
      eval: this._renderModalEval,
      anexo: this._renderModalAnexo
    };
    const titulos = {
      identif: 'Identificación',
      plan: 'Plan de acción',
      eval: 'Evaluación',
      anexo: 'Anexo (fotos)'
    };

    const subtitulos = {
      plan: 'Apartado 2. Requiere la autorización de la acción del responsable del área de producción.',
      eval: 'Llenado por el responsable de la línea de producto.'
    };

    const html = renderers[seccion].call(this, prob, readonly);

    const buttons = readonly
      ? [{ text: 'Cerrar', class: 'btn-secondary' }]
      : (seccion === 'anexo'
          ? [{ text: 'Cerrar', class: 'btn-secondary' }]
          : [
              { text: 'Cancelar', class: 'btn-secondary' },
              {
                text: 'Guardar',
                class: 'btn-primary',
                closeOnClick: false,
                onClick: async () => {
                  const ok = await this.guardarSeccionProblema(problemaId, seccion);
                  if (ok) modal.close();
                }
              }
            ]
        );

    const sinPrefijo = ['identif', 'plan', 'eval', 'anexo'];
    const tituloTexto = sinPrefijo.includes(seccion)
      ? `<span style="color:var(--primary);">${titulos[seccion]}</span>`
      : `Daño #${prob.id} — <span style="color:var(--primary);">${titulos[seccion]}</span>`;
    const subtitulo = subtitulos[seccion]
      ? `<span style="display:block;font-size:0.75rem;font-weight:400;color:#6b7280;margin-top:0.25rem;line-height:1.4;">${subtitulos[seccion]}</span>`
      : '';
    modal.open(tituloTexto + subtitulo, html, buttons);

    // Init SignaturePad si el modal de Identificación tiene canvas vacío
    if (seccion === 'identif' && !readonly && !prob.detectado_por_firma_url) {
      this._probFirmaReemplazar = null;
      setTimeout(() => this._initFirmaProblemaModal(problemaId), 50);
    }
  },

  _renderOrigenInfoCompacto(prob) {
    if (!prob.origen_modulo || prob.origen_modulo === 'manual') return '';
    const config = {
      recepcion: {
        icon: '📦', label: 'Recepción Visual',
        ruta: prob.origen_id ? `/almacen/recepciones/${prob.origen_id}` : null,
        textColor: '#3730a3'
      },
      checklist_ensamble: {
        icon: '🔧', label: 'Checklist Ensamble',
        ruta: prob.origen_id ? `/produccion/checklists/${prob.origen_id}` : null,
        textColor: '#5b21b6'
      }
    };
    const cfg = config[prob.origen_modulo];
    if (!cfg) return '';
    const formato = prob.origen_formato ? ` · ${prob.origen_formato}` : '';
    return `
      <div style="display:flex;align-items:center;gap:0.5rem;font-size:0.78rem;color:${cfg.textColor};">
        <span style="font-size:1rem;line-height:1;">${cfg.icon}</span>
        <span style="flex:1;"><strong>Origen:</strong> ${cfg.label}${formato}</span>
        ${cfg.ruta ? `
          <a href="javascript:void(0)" style="color:${cfg.textColor};text-decoration:underline;font-weight:600;cursor:pointer;" onclick="router.navigate('${cfg.ruta}')">Ver origen</a>
        ` : ''}
      </div>
    `;
  },

  _renderOrigenInfo(prob) {
    if (!prob.origen_modulo || prob.origen_modulo === 'manual') return '';
    const config = {
      recepcion: {
        icon: '📦',
        label: 'Recepción Visual',
        ruta: prob.origen_id ? `/almacen/recepciones/${prob.origen_id}` : null,
        color: '#6366f1',
        bgColor: '#eef2ff',
        textColor: '#3730a3'
      },
      checklist_ensamble: {
        icon: '🔧',
        label: 'Checklist Ensamble',
        ruta: prob.origen_id ? `/produccion/checklists/${prob.origen_id}` : null,
        color: '#7c3aed',
        bgColor: '#f5f3ff',
        textColor: '#5b21b6'
      }
    };
    const cfg = config[prob.origen_modulo];
    if (!cfg) return '';
    const formato = prob.origen_formato ? ` · ${prob.origen_formato}` : '';
    return `
      <div style="padding:0.75rem;background:${cfg.bgColor};border:1px solid ${cfg.color};border-radius:8px;margin-bottom:1rem;display:flex;align-items:center;gap:0.6rem;">
        <div style="font-size:1.25rem;line-height:1;">${cfg.icon}</div>
        <div style="flex:1;font-size:0.85rem;color:${cfg.textColor};">
          <strong>Origen:</strong> ${cfg.label}${formato}
        </div>
        ${cfg.ruta ? `
          <button type="button" style="padding:0.25rem 0.65rem;background:white;border:1px solid ${cfg.color};border-radius:4px;cursor:pointer;color:${cfg.textColor};font-size:0.78rem;font-weight:600;"
                  onclick="window.modal.close();router.navigate('${cfg.ruta}')">
            Ver origen
          </button>
        ` : ''}
      </div>
    `;
  },

  _renderOrigenEstatusCambiadoBadge(prob) {
    if (!prob.origen_estatus_cambiado_at) return '';
    if (prob.estatus === 'corregido') return '';
    const fmt = (f) => {
      if (!f) return '';
      const d = new Date(f);
      if (isNaN(d.getTime())) return '';
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    };
    const fechaTxt = fmt(prob.origen_estatus_cambiado_at);
    return `
      <div style="padding:0.75rem;background:#fef3c7;border:1px solid #f59e0b;border-radius:8px;margin-bottom:1rem;display:flex;align-items:flex-start;gap:0.6rem;">
        <div style="font-size:1.25rem;line-height:1;">⚠️</div>
        <div style="flex:1;font-size:0.85rem;color:#78350f;">
          <strong>Cambio en origen:</strong> el técnico cambió el estatus del item en checklist (de "Corregir" a otro estado)${fechaTxt ? ` el ${fechaTxt}` : ''}.
          Este daño puede eliminarse excepcionalmente si calidad determina que ya no aplica.
        </div>
      </div>
    `;
  },

  _renderModalIdentif(prob, readonly, grupoMembers = null) {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    // Si la firma está pendiente de subir (__pendiente__), mostrar un preview del trazo
    // capturado (blob en memoria) en vez de intentar cargar una URL que aún no existe.
    const firmaPendienteBlob = this._getProblemasFirmasPendientes()[prob.id] || null;
    const firmaActual = (prob.detectado_por_firma_url === '__pendiente__')
      ? (firmaPendienteBlob ? URL.createObjectURL(firmaPendienteBlob) : null)
      : prob.detectado_por_firma_url;
    const esGrupo = Array.isArray(grupoMembers) && grupoMembers.length > 0;
    // En modo grupo la descripción y la clasificación son POR PIEZA: van ocultas (con el
    // valor del representante, solo para validar) y se muestra la lista de piezas del grupo.
    const bloqueDescripcion = esGrupo
      ? `
        <div class="form-group">
          <label class="form-label">Descripción del problema</label>
          <div style="border:1px solid var(--gray-200);border-radius:8px;padding:0.4rem 0.75rem;background:var(--gray-50);max-height:170px;overflow:auto;">
            ${grupoMembers.map((m, i) => `<div style="font-size:0.85rem;padding:0.25rem 0;${i > 0 ? 'border-top:1px solid var(--gray-100);' : ''}">${i + 1}. ${esc(this._formatDescripcionLegacy(m.descripcion))}</div>`).join('')}
          </div>
          <p style="font-size:0.72rem;color:var(--gray-500);margin:0.35rem 0 0;">El detector y la firma se aplican a todos estos daños. La descripción es individual de cada uno.</p>
        </div>
        <textarea data-prob-field="descripcion" style="display:none">${esc(this._formatDescripcionLegacy(prob.descripcion))}</textarea>
        <select data-prob-field="clasificacion" style="display:none">${this._clasifSelectOptions(prob.clasificacion)}</select>
      `
      : `
        <div class="form-group">
          <label class="form-label">Descripción del problema *</label>
          <textarea class="form-input" rows="3" data-prob-field="descripcion" ${readonly ? 'readonly' : ''}>${esc(this._formatDescripcionLegacy(prob.descripcion))}</textarea>
        </div>
        <div class="form-group">
          <label class="form-label">Clasificación (tipo de daño) *</label>
          <select class="form-input" data-prob-field="clasificacion" ${readonly ? 'disabled' : ''}>
            ${this._clasifSelectOptions(prob.clasificacion)}
          </select>
        </div>
      `;
    return `
      <div data-prob-modal-id="${prob.id}" data-prob-modal-sec="identif">
        <div class="form-group">
          <label style="display:flex;align-items:center;gap:0.5rem;cursor:${readonly ? 'default' : 'pointer'};">
            <input type="checkbox" data-prob-field="evidencia_whatsapp_problema" ${prob.evidencia_whatsapp_problema ? 'checked' : ''} ${readonly ? 'disabled' : ''} />
            Evidencia del problema enviada por WhatsApp
          </label>
        </div>
        ${bloqueDescripcion}
        <div class="form-group">
          <label class="form-label">Fecha de detección</label>
          <input type="date" class="form-input" data-prob-field="fecha_deteccion" value="${prob.fecha_deteccion ? String(prob.fecha_deteccion).substring(0,10) : ''}" ${readonly ? 'readonly disabled style="background:var(--gray-50);cursor:not-allowed;"' : ''} />
        </div>

        <div class="form-group" style="margin-top:0.75rem;">
          <div id="probFirmaWrap-${prob.id}" style="border:1px solid var(--gray-200);border-radius:8px;overflow:hidden;background:white;">
            <div style="background:var(--bg-secondary);padding:0.6rem;text-align:center;border-bottom:1px solid var(--gray-200);">
              <span style="font-size:0.7rem;text-transform:uppercase;letter-spacing:1px;color:var(--primary);font-weight:600;">Firma del detectado por *</span>
            </div>
            ${firmaActual
              ? `<div style="padding:1.5rem;text-align:center;">
                   <img src="${firmaActual}" style="width:100%;max-height:100px;object-fit:contain;" onerror="this.style.display='none'" />
                 </div>
                 ${!readonly ? `
                   <div style="padding:0 1rem 0.25rem;text-align:center;">
                     <a href="javascript:void(0)" style="font-size:0.8rem;color:var(--primary);cursor:pointer;" onclick="window.pncDetalleView.cambiarFirmaProblema(${prob.id})">Cambiar firma</a>
                   </div>
                 ` : ''}`
              : (readonly
                  ? `<div style="padding:2rem 1rem;text-align:center;color:var(--gray-400);font-size:0.85rem;">Sin firma capturada</div>`
                  : `<div class="firma-box" style="position:relative;">
                       <canvas id="probFirmaCanvas-${prob.id}" class="firma-canvas-new"></canvas>
                       <span class="firma-placeholder" id="probFirmaPlaceholder-${prob.id}" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);text-align:center;pointer-events:none;color:var(--gray-400);">
                         <span style="font-size:0.8rem;">Firme aquí</span>
                       </span>
                     </div>
                     <div style="padding:0 1rem 0.25rem;text-align:center;">
                       <a href="javascript:void(0)" style="font-size:0.8rem;color:var(--primary);cursor:pointer;" onclick="window.pncDetalleView.limpiarFirmaProblema(${prob.id})">Limpiar</a>
                     </div>`
                )
            }
            <div style="padding:0.5rem 1rem 0.75rem;border-top:1px solid var(--gray-200);">
              <span style="font-size:0.7rem;color:var(--gray-500);text-transform:uppercase;letter-spacing:0.5px;">Nombre</span>
              <p style="margin:0.15rem 0 0;font-weight:600;font-size:0.85rem;">${esc(prob.detectado_por) || '—'}</p>
            </div>
          </div>
        </div>
      </div>
    `;
  },

  async _initFirmaProblemaModal(problemaId) {
    if (!window.SignaturePad) {
      try {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/signature_pad/4.1.7/signature_pad.umd.min.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Error al cargar SignaturePad'));
          document.head.appendChild(script);
        });
      } catch (e) { return; }
    }
    const canvas = document.getElementById(`probFirmaCanvas-${problemaId}`);
    if (!canvas) return;
    // Ajustar resolución del canvas
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    this._probSignaturePad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255,255,255)',
      penColor: 'rgb(0,0,0)'
    });
    // Ocultar el placeholder "Firme aquí" en cuanto se empiece a dibujar
    const placeholder = document.getElementById(`probFirmaPlaceholder-${problemaId}`);
    if (placeholder) {
      this._probSignaturePad.addEventListener('beginStroke', () => {
        placeholder.style.display = 'none';
      });
    }
  },

  limpiarFirmaProblema(problemaId) {
    if (this._probSignaturePad) this._probSignaturePad.clear();
    const placeholder = document.getElementById(`probFirmaPlaceholder-${problemaId}`);
    if (placeholder) placeholder.style.display = '';
  },

  cambiarFirmaProblema(problemaId) {
    const wrap = document.getElementById(`probFirmaWrap-${problemaId}`);
    if (!wrap) return;
    const prob = (this.data.pnc?.problemas || []).find(p => p.id === problemaId);
    const nombre = prob?.detectado_por || '—';
    // Pendiente: solo se borra del bucket SI se sube una nueva firma
    if (prob?.detectado_por_firma_url) {
      this._getFirmasPendientesReemplazo()[`detector_${problemaId}`] = prob.detectado_por_firma_url;
      prob.detectado_por_firma_url = null;
    }
    wrap.innerHTML = `
      <div style="background:var(--bg-secondary);padding:0.6rem;text-align:center;border-bottom:1px solid var(--gray-200);">
        <span style="font-size:0.7rem;text-transform:uppercase;letter-spacing:1px;color:var(--primary);font-weight:600;">Firma del detectado por *</span>
      </div>
      <div class="firma-box" style="position:relative;">
        <canvas id="probFirmaCanvas-${problemaId}" class="firma-canvas-new"></canvas>
        <span class="firma-placeholder" id="probFirmaPlaceholder-${problemaId}" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);text-align:center;pointer-events:none;color:var(--gray-400);">
          <span style="font-size:0.8rem;">Firme aquí</span>
        </span>
      </div>
      <div style="padding:0 1rem 0.25rem;text-align:center;">
        <a href="javascript:void(0)" style="font-size:0.8rem;color:var(--primary);cursor:pointer;" onclick="window.pncDetalleView.limpiarFirmaProblema(${problemaId})">Limpiar</a>
      </div>
      <div style="padding:0.5rem 1rem 0.75rem;border-top:1px solid var(--gray-200);">
        <span style="font-size:0.7rem;color:var(--gray-500);text-transform:uppercase;letter-spacing:0.5px;">Nombre</span>
        <p style="margin:0.15rem 0 0;font-weight:600;font-size:0.85rem;">${nombre}</p>
      </div>
    `;
    this._probFirmaReemplazar = problemaId;
    this._initFirmaProblemaModal(problemaId);
    this._showUnsaved(); // cambiar firma ya es un cambio (la vieja queda marcada para reemplazo)
  },

  _renderModalPlan(prob, readonly) {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const tipos = [['concesion', 'Concesión'], ['mantenimiento', 'Mantenimiento'], ['reemplazo', 'Reemplazo']];
    return `
      <div data-prob-modal-id="${prob.id}" data-prob-modal-sec="plan">
        <div class="form-group">
          <label class="form-label">Acción *</label>
          <div style="display:flex;gap:1.5rem;flex-wrap:wrap;padding:0.4rem 0;">
            ${tipos.map(([v, l]) => `
              <label style="display:flex;align-items:center;gap:0.35rem;cursor:${readonly ? 'default' : 'pointer'};">
                <input type="radio" name="prob-modal-accion" value="${v}" data-prob-field="accion" ${prob.accion === v ? 'checked' : ''} ${readonly ? 'disabled' : ''} />
                ${l}
              </label>
            `).join('')}
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Justificación *</label>
          <textarea class="form-input" rows="3" data-prob-field="justificacion" ${readonly ? 'readonly' : ''}>${esc(prob.justificacion)}</textarea>
        </div>
      </div>
    `;
  },

  _renderModalEval(prob, readonly) {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    return `
      <div data-prob-modal-id="${prob.id}" data-prob-modal-sec="eval">
        <div class="form-group">
          <label class="form-label">Fecha de cierre</label>
          <input type="date" class="form-input" data-prob-field="fecha_cierre" value="${prob.fecha_cierre ? String(prob.fecha_cierre).substring(0,10) : ''}" ${readonly ? 'readonly' : ''} />
        </div>
        <div class="form-group">
          <label style="display:flex;align-items:center;gap:0.5rem;cursor:${readonly ? 'default' : 'pointer'};">
            <input type="checkbox" data-prob-field="evidencia_whatsapp_solucion" ${prob.evidencia_whatsapp_solucion ? 'checked' : ''} ${readonly ? 'disabled' : ''} />
            Evidencia de la acción recibida por WhatsApp
          </label>
        </div>
        <div class="form-group">
          <label class="form-label">Detalles de la acción</label>
          <textarea class="form-input" rows="3" data-prob-field="detalles_accion" ${readonly ? 'readonly' : ''}>${esc(prob.detalles_accion)}</textarea>
        </div>
        <div class="form-group" style="margin-top:1rem;">
          <label class="form-label">Resultado final</label>
          <div style="display:flex;gap:1.5rem;flex-wrap:wrap;padding:0.4rem 0;">
            <label style="display:flex;align-items:center;gap:0.35rem;cursor:${readonly ? 'default' : 'pointer'};">
              <input type="radio" name="prob-modal-resultado" value="conforme" data-prob-field="resultado_final" ${prob.resultado_final === 'conforme' ? 'checked' : ''} ${readonly ? 'disabled' : ''} />
              Conforme
            </label>
            <label style="display:flex;align-items:center;gap:0.35rem;cursor:${readonly ? 'default' : 'pointer'};">
              <input type="radio" name="prob-modal-resultado" value="no_conforme" data-prob-field="resultado_final" ${prob.resultado_final === 'no_conforme' ? 'checked' : ''} ${readonly ? 'disabled' : ''} />
              No conforme
            </label>
          </div>
        </div>
      </div>
    `;
  },

  _renderModalAnexo(prob, readonly) {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const fotos = Array.isArray(prob.fotos) ? prob.fotos : [];
    const fotosProblema = fotos.filter(f => f.tipo === 'problema');
    const fotosSolucion = fotos.filter(f => f.tipo === 'solucion');

    // Plan completo? — gate para fotos de solución
    const planOk = !!(prob.accion && prob.justificacion);

    const renderBlock = (titulo, fs, tipo, locked = false, lockMsg = '') => {
      if (locked) {
        return `
          <div>
            <label class="form-label" style="font-weight:700;">${titulo}</label>
            <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:0.75rem;">
              <div style="aspect-ratio:4/3;border:2px dashed #e5e7eb;border-radius:8px;background:#f9fafb;display:flex;flex-direction:column;align-items:center;justify-content:center;color:var(--gray-400);">
                <div style="font-size:2rem;">🔒</div>
                <p style="margin:0.5rem 1rem 0;font-size:0.8rem;text-align:center;font-style:italic;">${lockMsg}</p>
              </div>
            </div>
          </div>
        `;
      }
      const pendientesPorProb = this._getFotosPendientesPorProblema();
      const pendientes = pendientesPorProb?.[prob.id]?.[tipo] || [];

      const xBtnStyle = 'position:absolute;top:6px;right:6px;background:rgba(220,38,38,0.95);color:white;border:none;border-radius:50%;width:28px;height:28px;font-size:1.1rem;line-height:1;cursor:pointer;text-align:center;padding:0;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,0.2);z-index:10;';
      const eliminarSet = this._getFotosPendientesEliminar();

      const fotosHtml = fs.map(f => {
        const marcadaParaEliminar = eliminarSet.has(f.id);
        if (marcadaParaEliminar) {
          return `
            <div style="position:relative;aspect-ratio:4/3;border:2px solid #dc2626;border-radius:8px;background:white;overflow:hidden;">
              <img src="${esc(f.foto_url)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;opacity:0.35;filter:grayscale(80%);" />
              <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.4rem;">
                <span style="background:rgba(220,38,38,0.95);color:white;font-size:0.7rem;padding:0.2rem 0.55rem;border-radius:4px;font-weight:700;">SE ELIMINARÁ</span>
                <button type="button" style="background:white;color:#dc2626;border:1px solid #dc2626;border-radius:4px;padding:0.2rem 0.55rem;font-size:0.72rem;font-weight:600;cursor:pointer;" onclick="window.pncDetalleView.desmarcarEliminacion(${prob.id}, ${f.id});return false;">↺ Deshacer</button>
              </div>
            </div>
          `;
        }
        return `
          <div style="position:relative;aspect-ratio:4/3;border:2px solid var(--gray-300);border-radius:8px;background:white;overflow:hidden;">
            ${!readonly
              ? `<img src="${esc(f.foto_url)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;cursor:pointer;" title="Click para reemplazar" onclick="this.parentElement.querySelector('input[type=file]').click()" />`
              : `<img src="${esc(f.foto_url)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />`
            }
            ${!readonly ? `
              <button type="button" style="${xBtnStyle}" onclick="window.pncDetalleView.eliminarFotoProblema(${prob.id}, ${f.id});return false;">×</button>
              <input type="file" accept="image/*" capture="environment" style="display:none;" onchange="window.pncDetalleView.reemplazarFotoGuardada(${prob.id}, ${f.id}, '${tipo}', this)" />
            ` : ''}
          </div>
        `;
      }).join('');

      const pendientesHtml = pendientes.map((p, idx) => `
        <div style="position:relative;aspect-ratio:4/3;border:2px dashed #f59e0b;border-radius:8px;background:white;overflow:hidden;">
          ${!readonly
            ? `<img src="${p.previewUrl}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;cursor:pointer;" title="Click para reemplazar" onclick="this.parentElement.querySelector('input[type=file]').click()" />`
            : `<img src="${p.previewUrl}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />`
          }
          <span style="position:absolute;top:6px;left:6px;background:rgba(245,158,11,0.95);color:white;font-size:0.65rem;padding:0.15rem 0.4rem;border-radius:4px;font-weight:600;pointer-events:none;z-index:5;">PENDIENTE</span>
          ${!readonly ? `
            <button type="button" style="${xBtnStyle}" onclick="window.pncDetalleView.removerFotoPendienteProblema(${prob.id}, '${tipo}', ${idx});return false;">×</button>
            <input type="file" accept="image/*" capture="environment" style="display:none;" onchange="window.pncDetalleView.reemplazarFotoPendiente(${prob.id}, '${tipo}', ${idx}, this)" />
          ` : ''}
        </div>
      `).join('');

      // Sin límite: se pueden agregar N fotos por tipo (problema, solución).
      const placeholderHtml = !readonly ? `
        <div style="aspect-ratio:4/3;border:2px dashed var(--gray-300);border-radius:8px;background:white;display:flex;align-items:center;justify-content:center;cursor:pointer;overflow:hidden;"
             onclick="this.querySelector('input[type=file]').click()">
          <div style="text-align:center;color:var(--gray-400);">
            <div style="font-size:2rem;">📷</div>
            <p style="margin:0.5rem 0 0;font-size:0.8rem;">Toque para capturar</p>
          </div>
          <input type="file" accept="image/*" capture="environment" style="display:none;" onchange="window.pncDetalleView.subirFotoProblema(${prob.id}, '${tipo}', this)" />
        </div>
      ` : '';
      return `
        <div>
          <label class="form-label" style="font-weight:700;">${titulo}</label>
          <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:0.75rem;">
            ${fotosHtml}
            ${pendientesHtml}
            ${placeholderHtml}
          </div>
          ${(fs.length + pendientes.length) === 0 && readonly ? '<p style="font-size:0.75rem;color:var(--gray-500);font-style:italic;">Sin fotos.</p>' : ''}
        </div>
      `;
    };

    return `
      <div data-prob-modal-id="${prob.id}" data-prob-modal-sec="anexo">
        <p style="font-size:0.8rem;color:var(--gray-500);margin-bottom:0.75rem;">Las fotos se guardan automáticamente al subir o eliminar.</p>
        <div style="display:grid;grid-template-columns:1fr;gap:1rem;">
          ${renderBlock('Fotos del problema', fotosProblema, 'problema')}
          ${renderBlock('Fotos de la solución', fotosSolucion, 'solucion', !planOk, 'Disponible una vez completado Plan de acción.')}
        </div>
      </div>
    `;
  },

  // Mapa { problemaId: { campo: valor } } con cambios pendientes de persistir
  _getProblemasDirty() {
    if (!this._problemasDirty) this._problemasDirty = {};
    return this._problemasDirty;
  },

  _isProblemaSeccionCompleta(prob, sec) {
    if (sec === 'identif') return !!(prob.descripcion && prob.detectado_por && prob.detectado_por_firma_url);
    if (sec === 'plan') return !!(prob.accion && prob.justificacion);
    if (sec === 'eval') return !!(prob.fecha_cierre && prob.detalles_accion && prob.resultado_final);
    if (sec === 'anexo') {
      const eliminarSet = this._getFotosPendientesEliminar();
      const pendientesPorProb = this._getFotosPendientesPorProblema()?.[prob.id] || { problema: [], solucion: [] };
      const fotosGuardadas = (Array.isArray(prob.fotos) ? prob.fotos : []).filter(f => !eliminarSet.has(f.id));
      const hasProblema = fotosGuardadas.some(f => f.tipo === 'problema') || (pendientesPorProb.problema?.length > 0);
      const hasSolucion = fotosGuardadas.some(f => f.tipo === 'solucion') || (pendientesPorProb.solucion?.length > 0);
      return hasProblema && hasSolucion;
    }
    return false;
  },

  _computeEstatusProblema(prob) {
    const identif = this._isProblemaSeccionCompleta(prob, 'identif');
    const plan = this._isProblemaSeccionCompleta(prob, 'plan');
    const evalOk = this._isProblemaSeccionCompleta(prob, 'eval');
    const anexo = this._isProblemaSeccionCompleta(prob, 'anexo');
    if (identif && plan && evalOk && anexo) return 'corregido';
    // C-323c: «Se corrige con el pedido» se queda así al guardar secciones; sólo lo cierra «corregido»
    // o su botón «Ya no espera el pedido».
    if (prob.estatus === 'diferido') return 'diferido';
    if (identif) return 'en_proceso';
    return 'pendiente';
  },

  // Recomputa el estatus tras cambios en fotos (Anexo) y lo agrega al dirty.
  // También re-renderiza la tabla para que el badge "PENDIENTE/EN_PROCESO/CORREGIDO" refleje el cambio.
  _recomputeEstatusEnMemoria(problemaId) {
    const idx = (this.data.pnc?.problemas || []).findIndex(p => p.id === problemaId);
    if (idx < 0) return;
    const prob = this.data.pnc.problemas[idx];
    const nuevo = this._computeEstatusProblema(prob);
    if (prob.estatus !== nuevo) {
      this.data.pnc.problemas[idx].estatus = nuevo;
      const dirty = this._getProblemasDirty();
      dirty[problemaId] = { ...(dirty[problemaId] || {}), estatus: nuevo };
    }
    // Re-render la página detrás del modal para refrescar el badge de estatus
    this._expandedProblemaId = problemaId;
    if (this.data.pnc?.completado) {
      this.renderPNCCompletado();
    } else {
      this.renderFormularioEdicion();
    }
    this._showUnsaved();
  },

  // Mapa { problemaId: Blob } con firmas pendientes de subir
  _getProblemasFirmasPendientes() {
    if (!this._problemasFirmasPendientes) this._problemasFirmasPendientes = {};
    return this._problemasFirmasPendientes;
  },

  // Muestra un aviso de error DENTRO del modal de sección (no como toast que se pierde al cerrar)
  _showModalError(root, msg) {
    if (!root) return;
    let box = root.querySelector('[data-prob-error]');
    if (!box) {
      box = document.createElement('div');
      box.setAttribute('data-prob-error', '');
      box.style.cssText = 'background:#fef2f2;border:1px solid #fca5a5;color:#b91c1c;padding:0.6rem 0.85rem;border-radius:8px;font-size:0.85rem;margin-bottom:0.85rem;display:flex;align-items:center;gap:0.5rem;';
      root.insertBefore(box, root.firstChild);
    }
    box.innerHTML = `<span style="font-size:1rem;flex-shrink:0;">⚠️</span><span>${msg}</span>`;
    box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  },

  _clearModalError(root) {
    const box = root?.querySelector('[data-prob-error]');
    if (box) box.remove();
  },

  async guardarSeccionProblema(problemaId, seccion) {
    const root = document.querySelector(`[data-prob-modal-id="${problemaId}"][data-prob-modal-sec="${seccion}"]`);
    if (!root) return false;
    this._clearModalError(root);

    const v = (sel) => root.querySelector(sel)?.value?.trim() || null;
    const c = (sel) => !!root.querySelector(sel)?.checked;

    let data = {};
    if (seccion === 'identif') {
      const clasifSel = root.querySelector('select[data-prob-field="clasificacion"]');
      data = {
        descripcion: v('textarea[data-prob-field="descripcion"]'),
        clasificacion: clasifSel ? (clasifSel.value || null) : null,
        evidencia_whatsapp_problema: c('input[data-prob-field="evidencia_whatsapp_problema"]'),
        fecha_deteccion: v('input[data-prob-field="fecha_deteccion"]')
      };
      if (!data.descripcion) {
        this._showModalError(root, 'La descripción es requerida.');
        return false;
      }
      if (!data.clasificacion) {
        this._showModalError(root, 'Selecciona la clasificación (tipo de daño).');
        return false;
      }
      // La fecha de detección no puede ser posterior a la fecha de cierre (si ya existe)
      const probIdentif = (this.data.pnc.problemas || []).find(p => p.id === problemaId);
      if (data.fecha_deteccion && probIdentif?.fecha_cierre) {
        const det = String(data.fecha_deteccion).substring(0, 10);
        const cie = String(probIdentif.fecha_cierre).substring(0, 10);
        if (det > cie) {
          this._showModalError(root, 'La fecha de detección no puede ser posterior a la fecha de cierre.');
          return false;
        }
      }

      // Firma del detector: si se firmó en el pad, guardar el Blob en memoria
      // para subirlo cuando el usuario presione "Guardar Cambios".
      const pad = this._probSignaturePad;
      if (pad && !pad.isEmpty()) {
        try {
          const blob = await this.toBlob(pad);
          this._getProblemasFirmasPendientes()[problemaId] = blob;
          // Preview en memoria mientras se guarda
          data._firmaPendiente = true;
        } catch (err) {
          this._showModalError(root, 'Error al procesar la firma.');
          return false;
        }
      }
    } else if (seccion === 'plan') {
      const accionRadio = root.querySelector('input[data-prob-field="accion"]:checked');
      data = {
        accion: accionRadio ? accionRadio.value : null,
        justificacion: v('textarea[data-prob-field="justificacion"]')
      };
    } else if (seccion === 'eval') {
      const resultRadio = root.querySelector('input[data-prob-field="resultado_final"]:checked');
      data = {
        fecha_cierre: v('input[data-prob-field="fecha_cierre"]'),
        evidencia_whatsapp_solucion: c('input[data-prob-field="evidencia_whatsapp_solucion"]'),
        detalles_accion: v('textarea[data-prob-field="detalles_accion"]'),
        resultado_final: resultRadio ? resultRadio.value : null
      };
      // La fecha de cierre no puede ser anterior a la fecha de detección
      const probEval = (this.data.pnc.problemas || []).find(p => p.id === problemaId);
      if (data.fecha_cierre && probEval?.fecha_deteccion) {
        const cie = String(data.fecha_cierre).substring(0, 10);
        const det = String(probEval.fecha_deteccion).substring(0, 10);
        if (cie < det) {
          this._showModalError(root, 'La fecha de cierre no puede ser anterior a la fecha de detección.');
          return false;
        }
      }
    }

    // Actualización en memoria — la persistencia ocurre al presionar "Guardar Cambios"
    try {
      // Guardar deltas de esta sección
      const dirty = this._getProblemasDirty();
      dirty[problemaId] = { ...(dirty[problemaId] || {}), ...data };
      delete dirty[problemaId]._firmaPendiente; // flag UI, no se manda al backend

      // Reflejar cambios en el array de problemas en memoria para refrescar la tabla
      const idx = (this.data.pnc.problemas || []).findIndex(p => p.id === problemaId);
      if (idx >= 0) {
        this.data.pnc.problemas[idx] = { ...this.data.pnc.problemas[idx], ...data };
        // Si tiene firma pendiente, simular que ya está para el cálculo de estatus
        if (data._firmaPendiente) {
          this.data.pnc.problemas[idx].detectado_por_firma_url = '__pendiente__';
        }
        // Auto-derivar estatus (pendiente / en_proceso / corregido) según completitud
        const nuevoEstatus = this._computeEstatusProblema(this.data.pnc.problemas[idx]);
        this.data.pnc.problemas[idx].estatus = nuevoEstatus;
        dirty[problemaId].estatus = nuevoEstatus;
      }

      // Re-renderizar la tabla para que los pills ✓/○ se actualicen
      if (this.data.pnc.completado) {
        this.renderPNCCompletado();
      } else {
        this.renderFormularioEdicion();
      }

      // Marcar página como sucia DESPUÉS del re-render (si no, el render
      // sobrescribe el indicador y queda oculto)
      this._showUnsaved();

      return true;
    } catch (err) {
      toast.error(err.message || 'Error al actualizar');
      return false;
    }
  },

  abrirDrawerProblema(problemaId) {
    const prob = (this.data.pnc?.problemas || []).find(p => p.id === problemaId);
    if (!prob) return;
    const readonly = !!this.data.pnc.completado || this.data.pnc.estado === 'cerrado';
    this._mostrarDrawer(this._renderDrawerProblema(prob, readonly));
  },

  _renderDrawerProblema(prob, readonly) {
    const card = this._renderProblemaCard(prob, readonly, true);
    const formatOrigen = (m) => ({ recepcion: 'Recepción', checklist_ensamble: 'Checklist Ensamble', manual: 'Manual' }[m] || m || '—');
    return `
      <div style="display:flex;flex-direction:column;height:100%;background:#ffffff;">
        <div style="padding:1rem 1.25rem;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;background:#f9fafb;flex-shrink:0;">
          <div>
            <h4 style="margin:0;font-size:1rem;font-weight:700;color:#111827;">Daño #${prob.id}</h4>
            <p style="margin:0.15rem 0 0;font-size:0.75rem;color:#6b7280;">${formatOrigen(prob.origen_modulo)}${prob.origen_formato ? ' · ' + prob.origen_formato : ''}</p>
          </div>
          <button type="button" onclick="window.pncDetalleView.cerrarDrawer()" style="background:transparent;border:none;font-size:1.4rem;cursor:pointer;color:#6b7280;padding:0.25rem 0.6rem;line-height:1;">✕</button>
        </div>
        <div style="flex:1;overflow-y:auto;padding:1rem 1.25rem;background:#ffffff;">
          ${card}
        </div>
        ${!readonly ? `
          <div style="padding:0.75rem 1.25rem;border-top:1px solid #e5e7eb;background:#f9fafb;display:flex;gap:0.5rem;justify-content:flex-end;flex-shrink:0;">
            <button class="btn btn-secondary" type="button" onclick="window.pncDetalleView.cerrarDrawer()">Cancelar</button>
            <button class="btn btn-primary" type="button" onclick="window.pncDetalleView.guardarProblema(${prob.id})">Guardar daño</button>
          </div>
        ` : ''}
      </div>
    `;
  },

  _mostrarDrawer(html) {
    let backdrop = document.getElementById('pncProbBackdrop');
    let drawer = document.getElementById('pncProbDrawer');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.id = 'pncProbBackdrop';
      backdrop.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:99998;display:none;';
      backdrop.onclick = () => this.cerrarDrawer();
      document.body.appendChild(backdrop);
    }
    if (!drawer) {
      drawer = document.createElement('div');
      drawer.id = 'pncProbDrawer';
      drawer.style.cssText = 'position:fixed;top:0;right:0;height:100vh;width:min(720px,95vw);background:#ffffff;box-shadow:-4px 0 24px rgba(0,0,0,0.2);z-index:99999;transform:translateX(100%);transition:transform 0.25s ease-out;overflow:hidden;';
      document.body.appendChild(drawer);
    }
    drawer.innerHTML = html;
    backdrop.style.display = 'block';
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      drawer.style.transform = 'translateX(0)';
    });
  },

  cerrarDrawer() {
    const backdrop = document.getElementById('pncProbBackdrop');
    const drawer = document.getElementById('pncProbDrawer');
    if (drawer) drawer.style.transform = 'translateX(100%)';
    if (backdrop) backdrop.style.display = 'none';
    document.body.style.overflow = '';
  },

  _renderProblemaCard(prob, readonly, expandIdentif = false) {
    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const badgeStatus = (st) => {
      const colors = {
        pendiente: { bg: '#fef3c7', fg: '#78350f' },
        en_proceso: { bg: '#dbeafe', fg: '#1e40af' },
        corregido: { bg: '#d1fae5', fg: '#065f46' },
        diferido: { bg: '#ede9fe', fg: '#5b21b6' } // C-323c: «Se corrige con el pedido»
      };
      const c = colors[st] || { bg: '#e5e7eb', fg: '#374151' };
      if (st === 'diferido') st = 'Se corrige con el pedido';
      return `<span style="background:${c.bg};color:${c.fg};padding:0.15rem 0.5rem;border-radius:999px;font-size:0.7rem;font-weight:600;text-transform:uppercase;">${st || 'pendiente'}</span>`;
    };

    const formatOrigen = (mod) => ({
      recepcion: 'Recepción',
      checklist_ensamble: 'Checklist Ensamble',
      manual: 'Manual'
    }[mod] || mod || '—');

    const seccion = (key, titulo, content, expanded = false) => `
      <details data-prob-section="${key}" ${expanded ? 'open' : ''} style="border-top:1px solid var(--gray-200);margin-top:0.5rem;">
        <summary style="cursor:pointer;padding:0.5rem 0;font-size:0.78rem;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:var(--primary);user-select:none;">
          ${titulo}
        </summary>
        <div style="padding:0.5rem 0 0.75rem;">
          ${content}
        </div>
      </details>
    `;

    // ---- Apt 1: Identificación ----
    const apt1 = `
      <div class="form-group">
        <label class="form-label">Descripción del problema *</label>
        ${readonly
          ? `<div style="font-size:0.9rem;white-space:pre-wrap;background:var(--gray-50);padding:0.5rem;border-radius:4px;">${esc(prob.descripcion)}</div>`
          : `<textarea class="form-input" rows="3" data-prob-field="descripcion">${esc(prob.descripcion)}</textarea>`
        }
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;">
        <div>
          <label class="form-label">Detectado por</label>
          ${readonly
            ? `<input type="text" class="form-input" readonly value="${esc(prob.detectado_por)}" />`
            : `<input type="text" class="form-input" data-prob-field="detectado_por" value="${esc(prob.detectado_por)}" placeholder="Persona/rol que detectó" />`
          }
        </div>
        <div>
          <label class="form-label">Fecha de detección</label>
          <input type="date" class="form-input" data-prob-field="fecha_deteccion" value="${prob.fecha_deteccion ? String(prob.fecha_deteccion).substring(0,10) : ''}" ${readonly ? 'readonly' : ''} />
        </div>
      </div>
      <div class="form-group" style="margin-top:0.5rem;">
        <label style="display:flex;align-items:center;gap:0.5rem;cursor:${readonly ? 'default' : 'pointer'};">
          <input type="checkbox" data-prob-field="evidencia_whatsapp_problema" ${prob.evidencia_whatsapp_problema ? 'checked' : ''} ${readonly ? 'disabled' : ''} />
          Evidencia del problema enviada por WhatsApp
        </label>
      </div>
    `;

    // ---- Apt 2: Plan de acción ----
    const accionTipos = [['concesion', 'Concesión'], ['mantenimiento', 'Mantenimiento'], ['reemplazo', 'Reemplazo']];
    const apt2 = `
      <div class="form-group">
        <label class="form-label">Acción</label>
        ${readonly
          ? `<input type="text" class="form-input" readonly value="${(accionTipos.find(([v]) => v === prob.accion) || ['', '—'])[1]}" />`
          : `<div style="display:flex;gap:1.5rem;flex-wrap:wrap;padding:0.4rem 0;">
              ${accionTipos.map(([v, l]) => `
                <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;">
                  <input type="radio" name="prob-accion-${prob.id}" value="${v}" data-prob-field="accion" ${prob.accion === v ? 'checked' : ''} />
                  ${l}
                </label>
              `).join('')}
            </div>`
        }
      </div>
      <div class="form-group">
        <label class="form-label">Justificación</label>
        <textarea class="form-input" rows="3" data-prob-field="justificacion" ${readonly ? 'readonly' : ''}>${esc(prob.justificacion)}</textarea>
      </div>
      <div class="form-group">
        <label class="form-label">Responsable del área de producción</label>
        <input type="text" class="form-input" data-prob-field="responsable_produccion" value="${esc(prob.responsable_produccion)}" ${readonly ? 'readonly' : ''} />
      </div>
    `;

    // ---- Apt 3: Evaluación / cierre ----
    const apt3 = `
      <h6 style="margin:0 0 0.5rem;font-size:0.75rem;font-weight:600;color:var(--gray-700);">3.1 Verificación de la acción</h6>
      <div class="form-group">
        <label class="form-label">Detalles de la acción aplicada</label>
        <textarea class="form-input" rows="3" data-prob-field="detalles_accion" ${readonly ? 'readonly' : ''}>${esc(prob.detalles_accion)}</textarea>
      </div>
      <div class="form-group">
        <label class="form-label">Responsable de línea</label>
        <input type="text" class="form-input" data-prob-field="responsable_linea" value="${esc(prob.responsable_linea)}" ${readonly ? 'readonly' : ''} />
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:0.5rem;cursor:${readonly ? 'default' : 'pointer'};">
          <input type="checkbox" data-prob-field="evidencia_whatsapp_solucion" ${prob.evidencia_whatsapp_solucion ? 'checked' : ''} ${readonly ? 'disabled' : ''} />
          Evidencia de solución enviada por WhatsApp
        </label>
      </div>

      <h6 style="margin:1rem 0 0.5rem;font-size:0.75rem;font-weight:600;color:var(--gray-700);">3.2 Cierre</h6>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;">
        <div>
          <label class="form-label">Resultado final</label>
          ${readonly
            ? `<input type="text" class="form-input" readonly value="${prob.resultado_final || '—'}" />`
            : `<select class="form-input" data-prob-field="resultado_final">
                <option value="">—</option>
                <option value="conforme" ${prob.resultado_final === 'conforme' ? 'selected' : ''}>Conforme</option>
                <option value="no_conforme" ${prob.resultado_final === 'no_conforme' ? 'selected' : ''}>No conforme</option>
              </select>`
          }
        </div>
        <div>
          <label class="form-label">Estatus del problema</label>
          ${readonly
            ? `<input type="text" class="form-input" readonly value="${prob.estatus || 'pendiente'}" />`
            : `<select class="form-input" data-prob-field="estatus">
                ${['pendiente','en_proceso','corregido'].map(s => `<option value="${s}" ${prob.estatus === s ? 'selected' : ''}>${s}</option>`).join('')}
              </select>`
          }
        </div>
      </div>
      <div class="form-group" style="margin-top:0.5rem;">
        <label class="form-label">Responsable de calidad</label>
        <input type="text" class="form-input" data-prob-field="responsable_calidad" value="${esc(prob.responsable_calidad)}" ${readonly ? 'readonly' : ''} />
      </div>
      <div style="display:flex;gap:1.5rem;flex-wrap:wrap;padding:0.4rem 0;">
        <label style="display:flex;align-items:center;gap:0.35rem;cursor:${readonly ? 'default' : 'pointer'};">
          <input type="checkbox" data-prob-field="cor" ${prob.cor ? 'checked' : ''} ${readonly ? 'disabled' : ''} /> COR
        </label>
        <label style="display:flex;align-items:center;gap:0.35rem;cursor:${readonly ? 'default' : 'pointer'};">
          <input type="checkbox" data-prob-field="cap" ${prob.cap ? 'checked' : ''} ${readonly ? 'disabled' : ''} /> CAP
        </label>
        <label style="display:flex;align-items:center;gap:0.35rem;cursor:${readonly ? 'default' : 'pointer'};">
          <input type="checkbox" data-prob-field="dig" ${prob.dig ? 'checked' : ''} ${readonly ? 'disabled' : ''} /> DIG
        </label>
      </div>
      <div class="form-group">
        <label class="form-label">Fecha de cierre</label>
        <input type="date" class="form-input" data-prob-field="fecha_cierre" value="${prob.fecha_cierre ? String(prob.fecha_cierre).substring(0,10) : ''}" ${readonly ? 'readonly' : ''} />
      </div>
    `;

    // ---- Anexo (fotos por daño) ----
    const fotos = Array.isArray(prob.fotos) ? prob.fotos : [];
    const fotosProblema = fotos.filter(f => f.tipo === 'problema');
    const fotosSolucion = fotos.filter(f => f.tipo === 'solucion');
    const renderFotoBlock = (titulo, fs, tipo) => `
      <div>
        <h6 style="margin:0 0 0.5rem;font-size:0.75rem;font-weight:600;color:var(--gray-700);">${titulo}</h6>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(100px,1fr));gap:0.4rem;">
          ${fs.map(f => `
            <div style="position:relative;border:1px solid var(--gray-200);border-radius:4px;overflow:hidden;">
              <img src="${esc(f.foto_url)}" alt="" style="width:100%;height:80px;object-fit:cover;display:block;" />
              ${!readonly ? `<button class="btn btn-xs btn-danger" type="button" style="position:absolute;top:2px;right:2px;padding:0.1rem 0.3rem;font-size:0.65rem;" onclick="window.pncDetalleView.eliminarFotoProblema(${prob.id}, ${f.id})">×</button>` : ''}
            </div>
          `).join('')}
          ${!readonly ? `
            <label style="border:1px dashed var(--gray-300);border-radius:4px;display:flex;align-items:center;justify-content:center;height:80px;cursor:pointer;font-size:0.7rem;color:var(--gray-500);">
              + Agregar
              <input type="file" accept="image/*" style="display:none;" onchange="window.pncDetalleView.subirFotoProblema(${prob.id}, '${tipo}', this)" />
            </label>
          ` : ''}
        </div>
        ${fs.length === 0 && readonly ? '<p style="font-size:0.75rem;color:var(--gray-500);font-style:italic;">Sin fotos.</p>' : ''}
      </div>
    `;
    const apt5 = `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;">
        ${renderFotoBlock('Foto del problema', fotosProblema, 'problema')}
        ${renderFotoBlock('Foto de la solución', fotosSolucion, 'solucion')}
      </div>
    `;

    return `
      <div data-problema-id="${prob.id}" style="border:1px solid var(--gray-300);border-radius:8px;padding:0.85rem;margin-bottom:0.75rem;background:var(--bg);">
        <div style="display:flex;justify-content:space-between;gap:0.5rem;align-items:flex-start;flex-wrap:wrap;border-bottom:1px solid var(--gray-200);padding-bottom:0.5rem;">
          <div style="flex:1;min-width:200px;font-weight:600;font-size:0.9rem;">
            ${esc((prob.descripcion || '').slice(0, 120))}${(prob.descripcion || '').length > 120 ? '…' : ''}
          </div>
          <div style="display:flex;gap:0.4rem;align-items:center;flex-wrap:wrap;">
            ${badgeStatus(prob.estatus)}
            <span style="font-size:0.7rem;color:var(--gray-500);">${formatOrigen(prob.origen_modulo)}${prob.origen_formato ? ' · ' + esc(prob.origen_formato) : ''}</span>
          </div>
        </div>

        ${seccion('apt1', '1. Identificación', apt1, expandIdentif)}
        ${seccion('apt2', '2. Plan de acción', apt2)}
        ${seccion('apt3', '3. Evaluación', apt3)}
        ${seccion('anexo', 'Anexo (fotos)', apt5)}

        ${!readonly ? `
          <div style="margin-top:0.75rem;display:flex;gap:0.5rem;justify-content:flex-end;border-top:1px solid var(--gray-200);padding-top:0.5rem;">
            <button class="btn btn-sm btn-primary" type="button"
              onclick="window.pncDetalleView.guardarProblema(${prob.id})">
              Guardar problema
            </button>
            ${prob.origen_modulo === 'manual' ? `
              <button class="btn btn-sm btn-danger" type="button"
                onclick="window.pncDetalleView.eliminarProblema(${prob.id})">
                Eliminar
              </button>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  },

  async guardarProblema(problemaId) {
    const row = document.querySelector(`[data-problema-id="${problemaId}"]`);
    if (!row) return;

    const v = (sel) => row.querySelector(sel)?.value?.trim() || null;
    const c = (sel) => !!row.querySelector(sel)?.checked;

    const accionRadio = row.querySelector('input[data-prob-field="accion"]:checked');

    const data = {
      // Apt 1
      descripcion: v('textarea[data-prob-field="descripcion"]'),
      detectado_por: v('input[data-prob-field="detectado_por"]'),
      fecha_deteccion: v('input[data-prob-field="fecha_deteccion"]'),
      evidencia_whatsapp_problema: c('input[data-prob-field="evidencia_whatsapp_problema"]'),
      // Apt 2
      accion: accionRadio ? accionRadio.value : null,
      justificacion: v('textarea[data-prob-field="justificacion"]'),
      responsable_produccion: v('input[data-prob-field="responsable_produccion"]'),
      // Apt 3
      detalles_accion: v('textarea[data-prob-field="detalles_accion"]'),
      responsable_linea: v('input[data-prob-field="responsable_linea"]'),
      evidencia_whatsapp_solucion: c('input[data-prob-field="evidencia_whatsapp_solucion"]'),
      resultado_final: v('select[data-prob-field="resultado_final"]') || null,
      responsable_calidad: v('input[data-prob-field="responsable_calidad"]'),
      cor: c('input[data-prob-field="cor"]'),
      cap: c('input[data-prob-field="cap"]'),
      dig: c('input[data-prob-field="dig"]'),
      fecha_cierre: v('input[data-prob-field="fecha_cierre"]'),
      estatus: v('select[data-prob-field="estatus"]') || 'pendiente'
    };

    try {
      await calidadService.actualizarProblema(problemaId, data);
      toast.success('Daño actualizado');
      await this.cargarPNC(this.data.pnc.id);
    } catch (err) {
      toast.error(err.message || 'Error al guardar el daño');
    }
  },

  reabrirProblema(problemaId) {
    modal.confirm(
      'Reabrir daño',
      '¿Reabrir este daño para modificarlo? El estatus volverá a "en proceso" y deberás guardar cambios.',
      () => {
        const idx = (this.data.pnc?.problemas || []).findIndex(p => p.id === problemaId);
        if (idx < 0) return;
        this.data.pnc.problemas[idx].estatus = 'en_proceso';
        const dirty = this._getProblemasDirty();
        dirty[problemaId] = { ...(dirty[problemaId] || {}), estatus: 'en_proceso' };
        this._showUnsaved();
        this._expandedProblemaId = problemaId;
        if (this.data.pnc.completado) {
          this.renderPNCCompletado();
        } else {
          this.renderFormularioEdicion();
        }
        toast.info('Daño reabierto. Modifica lo necesario y guarda los cambios.');
      }
    );
  },

  toggleExpandirProblema(problemaId) {
    this._expandedProblemaId = this._expandedProblemaId === problemaId ? null : problemaId;
    // Re-render solo la sección de problemas — pero el camino actual re-renderiza todo el PNC
    if (this.data.pnc.completado) {
      this.renderPNCCompletado();
    } else {
      this.renderFormularioEdicion();
    }
  },

  // Mapa { problemaId: { problema: [{file,previewUrl,tipo}], solucion: [...] } }
  _getFotosPendientesPorProblema() {
    if (!this._fotosPendientesPorProblema) this._fotosPendientesPorProblema = {};
    return this._fotosPendientesPorProblema;
  },

  // Set de IDs de fotos guardadas marcadas para eliminar al "Guardar Cambios"
  _getFotosPendientesEliminar() {
    if (!this._fotosPendientesEliminar) this._fotosPendientesEliminar = new Set();
    return this._fotosPendientesEliminar;
  },

  // Set de URLs de firmas viejas a borrar del bucket al "Guardar Cambios"
  _getFirmasViejasABorrar() {
    if (!this._firmasViejasABorrar) this._firmasViejasABorrar = new Set();
    return this._firmasViejasABorrar;
  },

  // Mapa { 'pnc_<tipo>' | 'detector_<probId>': urlVieja } con firmas que el usuario
  // marcó "Cambiar". Se resuelven al Guardar Cambios:
  //  - Si subió una nueva → vieja se mueve al Set de borrar
  //  - Si no subió nada → vieja se restaura (no se borra del bucket)
  _getFirmasPendientesReemplazo() {
    if (!this._firmasPendientesReemplazo) this._firmasPendientesReemplazo = {};
    return this._firmasPendientesReemplazo;
  },

  async subirFotoProblema(problemaId, tipo, inputEl) {
    const file = inputEl.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const previewUrl = URL.createObjectURL(compressed);

      // Encolar para subida diferida — se sube al presionar "Guardar Cambios"
      const pendientes = this._getFotosPendientesPorProblema();
      if (!pendientes[problemaId]) pendientes[problemaId] = { problema: [], solucion: [] };
      pendientes[problemaId][tipo].push({ file: compressed, previewUrl, tipo });

      // Marcar como sucio (banner amarillo) + recomputar estatus
      this._showUnsaved();
      this._recomputeEstatusEnMemoria(problemaId);

      // Refrescar el modal manteniéndolo abierto con la foto en preview
      const prob = (this.data.pnc?.problemas || []).find(p => p.id === problemaId);
      if (prob && typeof modal.close === 'function') {
        modal.close();
        if (this._anexoGrupoKey) this.abrirModalSeccionGrupo(this._anexoGrupoKey, 'anexo');
      else this.abrirModalSeccion(problemaId, 'anexo');
      }
    } catch (err) {
      toast.error(err.message || 'Error al procesar foto');
    } finally {
      inputEl.value = '';
    }
  },

  async reemplazarFotoPendiente(problemaId, tipo, idx, inputEl) {
    const file = inputEl.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const previewUrl = URL.createObjectURL(compressed);
      const pendientes = this._getFotosPendientesPorProblema();
      const lista = pendientes?.[problemaId]?.[tipo];
      if (!lista || !lista[idx]) return;
      // Liberar el preview anterior y reemplazar el item
      URL.revokeObjectURL(lista[idx].previewUrl);
      lista[idx] = { file: compressed, previewUrl, tipo };
      this._showUnsaved();
      this._recomputeEstatusEnMemoria(problemaId);
      if (typeof modal.close === 'function') modal.close();
      if (this._anexoGrupoKey) this.abrirModalSeccionGrupo(this._anexoGrupoKey, 'anexo');
      else this.abrirModalSeccion(problemaId, 'anexo');
    } catch (err) {
      toast.error(err.message || 'Error al procesar foto');
    } finally {
      inputEl.value = '';
    }
  },

  async reemplazarFotoGuardada(problemaId, fotoId, tipo, inputEl) {
    const file = inputEl.files?.[0];
    if (!file) return;
    try {
      // Encolar la guardada para eliminar + agregar la nueva como pendiente.
      // Ambas se procesan al presionar "Guardar Cambios".
      this._getFotosPendientesEliminar().add(fotoId);

      const compressed = await compressImage(file);
      const previewUrl = URL.createObjectURL(compressed);
      const pendientes = this._getFotosPendientesPorProblema();
      if (!pendientes[problemaId]) pendientes[problemaId] = { problema: [], solucion: [] };
      pendientes[problemaId][tipo].push({ file: compressed, previewUrl, tipo });

      this._showUnsaved();
      if (typeof modal.close === 'function') modal.close();
      if (this._anexoGrupoKey) this.abrirModalSeccionGrupo(this._anexoGrupoKey, 'anexo');
      else this.abrirModalSeccion(problemaId, 'anexo');
    } catch (err) {
      toast.error(err.message || 'Error al procesar foto');
    } finally {
      inputEl.value = '';
    }
  },

  removerFotoPendienteProblema(problemaId, tipo, idx) {
    const pendientes = this._getFotosPendientesPorProblema();
    const lista = pendientes?.[problemaId]?.[tipo];
    if (!lista || !lista[idx]) return;
    URL.revokeObjectURL(lista[idx].previewUrl);
    lista.splice(idx, 1);
    this._recomputeEstatusEnMemoria(problemaId);
    if (typeof modal.close === 'function') modal.close();
    if (this._anexoGrupoKey) this.abrirModalSeccionGrupo(this._anexoGrupoKey, 'anexo');
    else this.abrirModalSeccion(problemaId, 'anexo');
  },

  eliminarFotoProblema(problemaId, fotoId) {
    // Encolar para eliminación diferida — se procesa al presionar "Guardar Cambios"
    this._getFotosPendientesEliminar().add(fotoId);
    this._showUnsaved();
    this._recomputeEstatusEnMemoria(problemaId);
    if (typeof modal.close === 'function') modal.close();
    if (this._anexoGrupoKey) this.abrirModalSeccionGrupo(this._anexoGrupoKey, 'anexo');
    else this.abrirModalSeccion(problemaId, 'anexo');
  },

  desmarcarEliminacion(problemaId, fotoId) {
    this._getFotosPendientesEliminar().delete(fotoId);
    this._recomputeEstatusEnMemoria(problemaId);
    if (typeof modal.close === 'function') modal.close();
    if (this._anexoGrupoKey) this.abrirModalSeccionGrupo(this._anexoGrupoKey, 'anexo');
    else this.abrirModalSeccion(problemaId, 'anexo');
  },

  async eliminarProblema(problemaId) {
    modal.confirm(
      'Eliminar problema',
      '¿Seguro que deseas eliminar este problema individual? Esta acción no se puede deshacer.',
      async () => {
        try {
          await calidadService.eliminarProblema(problemaId);
          toast.success('Problema eliminado');
          await this.cargarPNC(this.data.pnc.id);
        } catch (err) {
          toast.error(err.message || 'Error al eliminar problema');
        }
      }
    );
  },

  agregarProblema() {
    const pncId = this.data.pnc.id;
    const html = `
      <div class="form-group">
        <label class="form-label">Descripción del problema *</label>
        <textarea id="modalNuevoProbDesc" class="form-input" rows="3" placeholder="Describa el problema..."></textarea>
      </div>
      <div class="form-group">
        <label class="form-label">Detectado por *</label>
        <input id="modalNuevoProbDetectado" class="form-input" placeholder="Persona/rol que detectó" value="${(authService.getUser()?.nombre || authService.getUser()?.nombre_completo || '').replace(/"/g, '&quot;')}" />
      </div>
      <div class="form-group">
        <label class="form-label">Clasificación (tipo de daño) *</label>
        <select id="modalNuevoProbClasif" class="form-input">
          ${this._clasifSelectOptions('')}
        </select>
      </div>
      <div class="form-group">
        <label class="form-label">Acción</label>
        <div style="display:flex;gap:1rem;flex-wrap:wrap;padding:0.4rem 0;">
          <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;"><input type="radio" name="modalNuevoProbAccion" value="concesion" /> Concesión</label>
          <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;"><input type="radio" name="modalNuevoProbAccion" value="mantenimiento" /> Mantenimiento</label>
          <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;"><input type="radio" name="modalNuevoProbAccion" value="reemplazo" /> Reemplazo</label>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">Justificación</label>
        <textarea id="modalNuevoProbJustif" class="form-input" rows="2"></textarea>
      </div>
      <div class="form-group">
        <label class="form-label">Detalle de la solución (opcional)</label>
        <textarea id="modalNuevoProbDetalle" class="form-input" rows="2"></textarea>
      </div>
    `;
    modal.open(
      'Agregar daño',
      html,
      [
        { text: 'Cancelar', class: 'btn-secondary' },
        {
          text: 'Agregar',
          class: 'btn-primary',
          closeOnClick: false,
          onClick: async () => {
            const desc = document.getElementById('modalNuevoProbDesc')?.value.trim();
            if (!desc) {
              toast.error('La descripción del problema es requerida');
              return;
            }
            const detectado_por = document.getElementById('modalNuevoProbDetectado')?.value.trim();
            if (!detectado_por) {
              toast.error('El campo "Detectado por" es requerido');
              return;
            }
            const clasificacion = document.getElementById('modalNuevoProbClasif')?.value || '';
            if (!clasificacion) {
              toast.error('Selecciona la clasificación (tipo de daño)');
              return;
            }
            const accionRadio = document.querySelector('input[name="modalNuevoProbAccion"]:checked');
            const accion = accionRadio ? accionRadio.value : null;
            const justificacion = document.getElementById('modalNuevoProbJustif')?.value.trim() || null;
            const detalles_accion = document.getElementById('modalNuevoProbDetalle')?.value.trim() || null;

            try {
              await api.post(`/calidad/pnc/${pncId}/problemas`, {
                descripcion: desc,
                clasificacion,
                detectado_por,
                accion,
                justificacion,
                detalles_accion,
                origen_modulo: 'manual'
              });
              modal.close();
              toast.success('Daño agregado');
              await this.cargarPNC(pncId);
            } catch (err) {
              toast.error(err.message || 'Error al agregar daño');
            }
          }
        }
      ]
    );
  },

  _renderApt3Edicion(p) {
    return `
      <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem; font-style: italic;">
        Apartado 3. Evaluación y cierre.
      </p>

      <!-- Verificación de la acción -->
      <p style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.75rem;">
        Llenado por el responsable de la línea de producto.
      </p>
      <div class="form-group">
        <label class="form-label">Fecha de cierre</label>
        <input type="date" id="pncFechaCierre" class="form-input" value="${p.fecha_cierre ? p.fecha_cierre.split('T')[0] : ''}" />
      </div>
      <div class="form-group" style="display: flex; align-items: center;">
        <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
          <input type="checkbox" id="pncEvidenciaWsSolucion" ${p.evidencia_whatsapp_solucion ? 'checked' : ''} />
          Evidencia de la acción recibida por WhatsApp
        </label>
      </div>
      <div class="form-group">
        <label class="form-label">Detalles de la acción</label>
        <textarea id="pncDetallesAccion" class="form-input pnc-uppercase" rows="4" placeholder="Describa las acciones realizadas...">${p.detalles_accion || ''}</textarea>
      </div>
      <div class="form-group">
        <label class="form-label">Responsable de la línea de producto</label>
        <select id="pncRespLinea" class="form-input">
          <option value="">Seleccione...</option>
          ${this.data.responsablesLinea.map(r => `<option value="${r.nombre_completo}" ${p.responsable_linea === r.nombre_completo ? 'selected' : ''}>${r.nombre_completo}</option>`).join('')}
        </select>
      </div>

      <hr style="border: none; border-top: 1px solid var(--border-color); margin: 1.5rem 0;" />

      <!-- Cierre del producto no conforme -->
      <div class="form-group">
        <label class="form-label">Resultado final</label>
        <div style="display: flex; gap: 1.5rem; margin-top: 0.25rem;">
          <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
            <input type="radio" name="pncResultadoFinal" value="conforme" ${p.resultado_final === 'conforme' ? 'checked' : ''} /> Conforme
          </label>
          <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
            <input type="radio" name="pncResultadoFinal" value="no_conforme" ${p.resultado_final === 'no_conforme' ? 'checked' : ''} /> No conforme
          </label>
        </div>
      </div>
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; margin-top: 1rem;" class="pnc-grid-2col">
        <div class="form-group">
          <label class="form-label">Responsable del área de producción</label>
          <input type="text" class="form-input" readonly value="${p.responsable_produccion || ''}" />
        </div>
        <div class="form-group">
          <label class="form-label">Responsable del área de calidad</label>
          <select id="pncRespCalidad" class="form-input">
            <option value="">Seleccione...</option>
            ${this.data.responsablesCalidad.map(r => `<option value="${r.nombre_completo}" ${p.responsable_calidad === r.nombre_completo ? 'selected' : ''}>${r.nombre_completo}</option>`).join('')}
          </select>
        </div>
      </div>
    `;
  },

  _renderAnexo(p, readonly) {
    const fotos = p.fotos || [];
    const fotosProblema = fotos.filter(f => f.tipo === 'problema');
    const fotosSolucion = fotos.filter(f => f.tipo === 'solucion');

    return `
      <h4 style="margin-bottom: 1rem; font-size: 0.95rem;">Anexo: Evidencia fotográfica</h4>

      <div style="margin-bottom: 1.5rem;">
        <p style="color: var(--text-secondary); font-size: 0.875rem; margin-bottom: 1rem;">1. Evidencia del problema (máx. 2)</p>
        <div class="fotos-grid-new" id="pncFotoGridProblema">
          ${this._renderFotoGrid(fotosProblema, 'problema', readonly, readonly ? [] : this.data.fotosNuevasProblema)}
        </div>
        ${!readonly ? `<input type="file" id="pncFotoInputProblema" accept="image/*" style="display: none;" onchange="window.pncDetalleView.agregarFotoArchivo(event, 'problema')" />` : ''}
      </div>

      <hr style="border: none; border-top: 1px solid var(--border-color); margin: 1.5rem 0;" />

      <div>
        <p style="color: var(--text-secondary); font-size: 0.875rem; margin-bottom: 1rem;">2. Evidencia de la solución (máx. 2)</p>
        <div class="fotos-grid-new" id="pncFotoGridSolucion">
          ${this._renderFotoGrid(fotosSolucion, 'solucion', readonly, readonly ? [] : this.data.fotosNuevasSolucion)}
        </div>
        ${!readonly ? `<input type="file" id="pncFotoInputSolucion" accept="image/*" style="display: none;" onchange="window.pncDetalleView.agregarFotoArchivo(event, 'solucion')" />` : ''}
      </div>
    `;
  },

  _renderFotoGrid(fotos, tipo, readonly, fotosPendientes = []) {
    let html = '';
    // Fotos existentes (ya subidas)
    fotos.forEach(f => {
      html += `
        <div class="foto-item">
          <img src="${f.foto_url}" onclick="window.open('${f.foto_url}', '_blank')" />
          ${!readonly ? `<button class="foto-remove" onclick="window.pncDetalleView.eliminarFoto(${f.id})">×</button>` : ''}
        </div>
      `;
    });

    // Fotos pendientes (aún no subidas, preview local)
    if (!readonly) {
      fotosPendientes.forEach((f, idx) => {
        html += `
          <div class="foto-item" style="position: relative;">
            <img src="${f.previewUrl}" />
            <span style="position: absolute; top: 4px; left: 4px; background: var(--warning); color: white; font-size: 0.7rem; padding: 2px 6px; border-radius: 4px;">Pendiente</span>
            <button class="foto-remove" onclick="window.pncDetalleView.removerFotoPendiente('${tipo}', ${idx})">×</button>
          </div>
        `;
      });

      const totalFotos = fotos.length + fotosPendientes.length;
      if (totalFotos < 2) {
        html += `<div class="foto-add-btn" onclick="document.getElementById('pncFotoInput${tipo.charAt(0).toUpperCase() + tipo.slice(1)}').click()">+</div>`;
      }
    }

    return html;
  },

  // ======================== VISTA COMPLETADO (cerrado, solo lectura) ========================

  renderPNCCompletado() {
    const container = document.getElementById('pncContent');
    const p = this.data.pnc;

    // Badge de origen (si viene de una recepción u otro módulo)
    let origenBadge = '';
    if (p.origen_modulo === 'recepcion' && p.origen_id) {
      origenBadge = `
        <div style="padding: 0.9rem; background: #eef2ff; border: 1px solid #6366f1; border-radius: 8px; margin-bottom: 1.5rem;">
          <div style="font-weight: 600; color: #3730a3;">📦 Origen: Recepción Visual</div>
          <div style="font-size: 0.9rem; color: var(--gray-700); margin-top: 0.25rem;">
            Este PNC se generó automáticamente al completar la recepción visual del tractor <strong>${p.num_serie}</strong>.
            <button style="margin-left: 0.5rem; padding: 0.2rem 0.6rem; background: white; border: 1px solid #6366f1; border-radius: 4px; cursor: pointer; color: #3730a3; font-size: 0.8rem;"
                    onclick="router.navigate('/almacen/recepciones/${p.origen_id}')">Ver recepción</button>
          </div>
        </div>
      `;
    }

    container.innerHTML = `
      ${origenBadge}
      <!-- Datos del Tractor -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;" class="pnc-grid-2col">
          <div class="form-group">
            <label class="form-label">Número de Serie</label>
            <input type="text" class="form-input" readonly value="${p.num_serie}" />
          </div>
          <div class="form-group">
            <label class="form-label">Marca</label>
            <input type="text" class="form-input" readonly value="${p.marca || ''}" />
          </div>
          <div class="form-group" style="grid-column: 1 / -1;">
            <label class="form-label">Modelo</label>
            <input type="text" class="form-input" readonly value="${p.modelo || ''}" />
          </div>
        </div>
      </div>


      <!-- Tabs (Firmas se oculta hasta que haya al menos un daño capturado) -->
      <div class="pnc-tabs-bar" id="pncTabsBar">
        <button class="pnc-tab active" onclick="window.pncDetalleView.showTab('apt1')">Daños</button>
        ${(p.problemas || []).length > 0 ? `<button class="pnc-tab" onclick="window.pncDetalleView.showTab('firmas')">Firmas</button>` : ''}
      </div>

      <!-- Tab Daños -->
      <div class="pnc-panel" id="panel-apt1" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
        ${this._renderProblemasIndividuales(p, true)}
      </div>

      <!-- Tab Apt 2: Plan de acción -->
      <div class="pnc-panel" id="panel-apt2" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem; font-style: italic;">
          Apartado 2. Requiere la autorización de la acción del responsable del área de producción.
        </p>
        <div class="form-group">
          <label class="form-label">Acción</label>
          <div style="display: flex; gap: 1.5rem; flex-wrap: wrap; margin-top: 0.25rem;">
            ${['concesion', 'mantenimiento', 'reemplazo'].map(a => `
              <label style="display: flex; align-items: center; gap: 0.5rem;">
                <input type="radio" disabled ${p.accion === a ? 'checked' : ''} />
                ${this.formatAccion(a)}
              </label>
            `).join('')}
          </div>
        </div>
        ${p.justificacion ? `
          <div class="form-group" style="margin-top: 1rem;">
            <label class="form-label">Justificación</label>
            <textarea class="form-input" rows="4" readonly>${p.justificacion}</textarea>
          </div>
        ` : ''}
        <div class="form-group" style="margin-top: 1rem;">
          <label class="form-label">Responsable del área de producción</label>
          <input type="text" class="form-input" readonly value="${p.responsable_produccion || ''}" />
        </div>
      </div>

      <!-- Tab Apt 3: Evaluación y cierre (solo lectura) -->
      <div class="pnc-panel" id="panel-apt3" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem; font-style: italic;">
          Apartado 3. Evaluación y cierre.
        </p>

        <!-- 3.1 -->
        <div style="border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem; margin-bottom: 1.5rem;">
          <h4 style="margin-bottom: 0.75rem; font-size: 0.95rem;">Verificación de la acción</h4>
          <div class="form-group" style="display: flex; align-items: center;">
            <label style="display: flex; align-items: center; gap: 0.5rem;">
              <input type="checkbox" disabled ${p.evidencia_whatsapp_solucion ? 'checked' : ''} />
              Evidencia de la acción recibida por WhatsApp
            </label>
          </div>
          <div class="form-group">
            <label class="form-label">Detalles de la acción</label>
            <textarea class="form-input" rows="4" readonly>${p.detalles_accion || ''}</textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Responsable de la línea de producto</label>
            <input type="text" class="form-input" readonly value="${p.responsable_linea || ''}" />
          </div>
        </div>

        <!-- 3.2 -->
        <div style="border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem;">
          <h4 style="margin-bottom: 0.75rem; font-size: 0.95rem;">Cierre del producto no conforme</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;" class="pnc-grid-2col">
            <div class="form-group">
              <label class="form-label">Fecha de cierre</label>
              <input type="date" class="form-input" readonly value="${p.fecha_cierre ? p.fecha_cierre.split('T')[0] : ''}" />
            </div>
            <div class="form-group">
              <label class="form-label">Resultado final</label>
              <div style="display: flex; gap: 1.5rem; margin-top: 0.25rem;">
                <label style="display: flex; align-items: center; gap: 0.5rem;">
                  <input type="radio" disabled ${p.resultado_final === 'conforme' ? 'checked' : ''} /> Conforme
                </label>
                <label style="display: flex; align-items: center; gap: 0.5rem;">
                  <input type="radio" disabled ${p.resultado_final === 'no_conforme' ? 'checked' : ''} /> No conforme
                </label>
              </div>
            </div>
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; margin-top: 1rem;" class="pnc-grid-2col">
            <div class="form-group">
              <label class="form-label">Responsable del área de producción</label>
              <input type="text" id="pncRespProduccionEvalEdit" class="form-input" readonly value="${p.responsable_produccion || ''}" />
            </div>
            <div class="form-group">
              <label class="form-label">Responsable del área de calidad</label>
              <input type="text" class="form-input" readonly value="${p.responsable_calidad || ''}" />
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Firmas (solo lectura). La firma del "Detectado por" es por daño
           en el rediseño "1 PNC = N daños" — se ve en el modal de Identificación
           de cada daño, no a nivel PNC. -->
      <div class="pnc-panel" id="panel-firmas" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;" class="pnc-grid-2col">
          ${this._renderFirmaDetalle('Resp. área de producción', p.responsable_produccion_firma_url, p.responsable_produccion)}
          ${this._renderFirmaDetalle('Resp. línea de producto', p.responsable_linea_firma_url, p.responsable_linea)}
          ${this._renderFirmaDetalle('Resp. área de calidad', p.responsable_calidad_firma_url, p.responsable_calidad)}
        </div>
        <div style="grid-column: 1 / -1;background:#eef2ff;border:1px solid #6366f1;border-radius:8px;padding:0.75rem 1rem;margin-top:1rem;font-size:0.8rem;color:#3730a3;">
          <strong>Firma del detectado por:</strong> se captura por daño (puede variar según el módulo de origen). Se muestra en cada daño dentro del Apartado 1 — Identificación.
        </div>
      </div>

      <!-- Tab Anexo -->
      <div class="pnc-panel" id="panel-anexo" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        ${this._renderAnexo(p, true)}
      </div>
    `;
  },

  // ======================== TABS ========================

  showTab(tab) {
    document.querySelectorAll('.pnc-tab').forEach(btn => btn.classList.remove('active'));
    document.querySelector(`.pnc-tab[onclick*="'${tab}'"]`)?.classList.add('active');
    document.querySelectorAll('.pnc-panel').forEach(p => p.style.display = 'none');
    const panel = document.getElementById(`panel-${tab}`);
    if (panel) panel.style.display = 'block';

    if (tab === 'firmas' && this.data.pnc && !this.data.pnc.completada) {
      this._renderFirmasDinamicas();
      this.initFirmas();
    }
  },

  // ======================== ACCIONES ========================

  async guardarCambios() {
    const p = this.data.pnc;

    try {
      // 1) Subir firmas pendientes de los daños (Identificación).
      // Dedup por referencia de blob: en un grupo, el fan-out asigna el MISMO blob de
      // firma a varios daños; se sube UNA sola vez y todos apuntan a la misma ruta
      // (una firma compartida, sin duplicar el archivo por cada daño).
      const firmasPendientes = this._getProblemasFirmasPendientes();
      const firmasIds = Object.keys(firmasPendientes);
      if (firmasIds.length > 0) {
        const distintas = new Set(firmasIds.map(id => firmasPendientes[id])).size;
        this._showProgress(`Subiendo ${distintas} firma(s) de detector...`);
        const numSerie = p?.num_serie;
        const urlByBlob = new Map();
        for (const probId of firmasIds) {
          const blob = firmasPendientes[probId];
          let url = urlByBlob.get(blob);
          if (!url) {
            url = await recepcionService.subirFirma(
              blob,
              `firma-detector-prob-${probId}-${Date.now()}.png`,
              numSerie,
              'pnc'
            );
            urlByBlob.set(blob, url);
          }
          const dirty = this._getProblemasDirty();
          dirty[probId] = { ...(dirty[probId] || {}), detectado_por_firma_url: url };
        }
      }

      // 2a) Resolver firmas pendientes de reemplazo a nivel detector (antes de
      // actualizarProblema). La firma vieja siempre se borra; si el detector
      // no firmó la nueva, se guarda firma_url=NULL.
      const dirty = this._getProblemasDirty();
      const pendientesRempl = this._getFirmasPendientesReemplazo();
      for (const [key, urlVieja] of Object.entries(pendientesRempl)) {
        if (!urlVieja || !key.startsWith('detector_')) continue;
        this._getFirmasViejasABorrar().add(urlVieja);
        const probId = parseInt(key.substring(9), 10);
        const newUrl = dirty[probId]?.detectado_por_firma_url;
        if (!newUrl) {
          dirty[probId] = { ...(dirty[probId] || {}), detectado_por_firma_url: null };
          const idx = (p.problemas || []).findIndex(x => x.id === probId);
          if (idx >= 0) p.problemas[idx].detectado_por_firma_url = null;
        }
      }

      // 2b) Persistir cambios de cada daño (modales pendientes)
      const dirtyIds = Object.keys(dirty);
      if (dirtyIds.length > 0) {
        this._showProgress(`Guardando ${dirtyIds.length} daño(s)...`);
        for (const probId of dirtyIds) {
          await calidadService.actualizarProblema(probId, dirty[probId]);
        }
      }

      // 3) Subir firmas oficiales del PNC (responsables) y guardar selects.
      // Fallback al valor en memoria si el tab Firmas nunca se abrió (selects no
      // existen en DOM porque _renderFirmasDinamicas solo corre al abrir el tab).
      // Sin este fallback, guardar desde Daños/Anexo borraría los responsables.
      const respValor = (tipo) => {
        const el = document.getElementById(`pncFirma_${tipo}`);
        return el ? (el.value || null) : (p[tipo] || null);
      };
      const dataPNC = {
        responsable_produccion: respValor('responsable_produccion'),
        responsable_linea: respValor('responsable_linea'),
        responsable_calidad: respValor('responsable_calidad')
      };
      this._showProgress('Subiendo firmas del documento...');
      const firmaUrls = await this.subirFirmas();
      Object.assign(dataPNC, firmaUrls);

      // 3a) Resolver firmas pendientes de reemplazo a nivel PNC (responsables).
      // La firma vieja siempre se borra; si el nuevo responsable no firmó,
      // queda firma_url=NULL y deberá firmar después.
      for (const [key, urlVieja] of Object.entries(pendientesRempl)) {
        if (!urlVieja || !key.startsWith('pnc_')) continue;
        this._getFirmasViejasABorrar().add(urlVieja);
        const tipo = key.substring(4);
        const newField = `${tipo}_firma_url`;
        if (!dataPNC[newField]) {
          dataPNC[newField] = null;
          p[newField] = null;
        }
      }

      this._showProgress('Guardando datos del PNC...');
      await calidadService.actualizarPNC(p.id, dataPNC);

      // 4) Subir fotos pendientes (a nivel PNC general, sin problema_id)
      await this._subirFotosPendientes();

      // 4a) Eliminar fotos marcadas para eliminar (las del Anexo por daño)
      const fotosAEliminar = this._getFotosPendientesEliminar();
      if (fotosAEliminar.size > 0) {
        this._showProgress(`Eliminando ${fotosAEliminar.size} foto(s)...`);
        for (const fotoId of fotosAEliminar) {
          try {
            await calidadService.eliminarFoto(p.id, fotoId);
          } catch (err) {
            console.warn('No se pudo eliminar foto', fotoId, err.message);
          }
        }
      }

      // 4b) Subir fotos pendientes por daño (Anexo).
      // Dedup por referencia de File: en un grupo el fan-out reutiliza el MISMO File
      // para varios daños; se sube UNA sola vez y todos los daños apuntan a la misma
      // ruta (no se duplica el binario en el bucket). Cada daño conserva su registro
      // en pnc_fotos (así ver/imprimir individual sigue funcionando).
      const fotosPorProb = this._getFotosPendientesPorProblema();
      const probIdsConFotos = Object.keys(fotosPorProb);
      if (probIdsConFotos.length > 0) {
        const numSerie = p?.num_serie;
        const archivosDistintos = new Set();
        for (const id of probIdsConFotos)
          for (const t of ['problema', 'solucion'])
            for (const it of (fotosPorProb[id][t] || [])) archivosDistintos.add(it.file);
        if (archivosDistintos.size > 0) this._showProgress(`Subiendo ${archivosDistintos.size} foto(s) de daños...`);
        const urlByFile = new Map();
        let seq = 0;
        for (const probId of probIdsConFotos) {
          for (const tipo of ['problema', 'solucion']) {
            const lista = fotosPorProb[probId][tipo] || [];
            for (const item of lista) {
              let url = urlByFile.get(item.file);
              if (!url) {
                url = await recepcionService.subirFoto(
                  item.file,
                  `pnc-${p.id}-${tipo}-${Date.now()}-${seq++}.jpg`,
                  numSerie,
                  'pnc'
                );
                urlByFile.set(item.file, url);
              }
              await calidadService.agregarFoto(p.id, {
                foto_url: url,
                tipo,
                problema_id: parseInt(probId, 10)
              });
              URL.revokeObjectURL(item.previewUrl);
            }
          }
        }
      }

      // 4c) Borrar firmas viejas del bucket (las que el usuario reemplazó).
      // Salvaguarda: como una firma puede quedar COMPARTIDA entre daños del grupo,
      // NO se borra del bucket si alguna firma vigente (de un daño o del PNC) aún la usa.
      const firmasViejas = this._getFirmasViejasABorrar();
      if (firmasViejas.size > 0) {
        const firmasEnUso = new Set();
        for (const d of Object.values(dirty)) if (d.detectado_por_firma_url) firmasEnUso.add(d.detectado_por_firma_url);
        for (const pr of (p.problemas || [])) if (pr.detectado_por_firma_url && pr.detectado_por_firma_url !== '__pendiente__') firmasEnUso.add(pr.detectado_por_firma_url);
        for (const k of ['responsable_produccion_firma_url', 'responsable_linea_firma_url', 'responsable_calidad_firma_url']) {
          if (dataPNC[k]) firmasEnUso.add(dataPNC[k]);
          if (p[k]) firmasEnUso.add(p[k]);
        }
        this._showProgress('Limpiando firma(s) anterior(es)...');
        for (const url of firmasViejas) {
          if (firmasEnUso.has(url)) continue; // sigue en uso por otro daño/PNC → no borrar
          try {
            await api.post('/files/eliminar', { url });
          } catch (err) {
            console.warn('No se pudo borrar firma vieja del bucket:', url, err.message);
          }
        }
      }

      // 5) Reset de estado y refresh
      this._resetBuffersEdicion();
      this._hideProgress();
      this._clearUnsaved();
      toast.success('Cambios guardados');
      await this.cargarPNC(p.id);
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al guardar cambios');
    }
  },

  async completarPNC() {
    const p = this.data.pnc;

    // Validar que haya al menos un daño y todos estén "completos"
    const problemas = Array.isArray(p.problemas) ? p.problemas : [];
    if (problemas.length === 0) {
      toast.error('Agregue al menos un daño antes de completar el PNC');
      this.showTab('apt1');
      return;
    }
    const incompletos = problemas.filter(prob =>
      !(prob.descripcion && prob.detectado_por && prob.detectado_por_firma_url
        && prob.accion && prob.justificacion
        && prob.fecha_cierre && prob.detalles_accion && prob.resultado_final)
    );
    if (incompletos.length > 0) {
      toast.error(`Hay ${incompletos.length} daño(s) incompleto(s). Llene todos los apartados (incluida la firma del detector) antes de completar.`);
      this.showTab('apt1');
      return;
    }

    // Validar firmas oficiales del PNC (a nivel documento). La firma del
    // detectado_por se valida por daño en el bloque anterior — ya no es a nivel PNC.
    const tipos = ['responsable_produccion', 'responsable_linea', 'responsable_calidad'];
    for (const tipo of tipos) {
      const pad = this[`signaturePad_${tipo}`];
      const existente = p[`${tipo}_firma_url`];
      if (!pad && !existente) {
        toast.error('Faltan firmas');
        this.showTab('firmas');
        return;
      }
      if (pad && pad.isEmpty() && !existente) {
        toast.error('Faltan firmas');
        this.showTab('firmas');
        return;
      }
    }

    if (!confirm('¿Completar el PNC? Esta acción no se puede deshacer.')) return;

    try {
      this._showProgress('Completando PNC...');

      // Guardar firmas
      await this.guardarCambios();

      // Cierre del PNC padre
      this._showProgress('Cerrando PNC...');
      await calidadService.cerrarPNC(p.id, {
        resultado_final: 'conforme',
        responsable_calidad: p.responsable_calidad || ''
      });

      this._hideProgress();
      this._clearUnsaved();
      toast.success('PNC completado exitosamente');
      await this.cargarPNC(p.id);
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al completar PNC');
    }
  },

  async guardarVerificacion() {
    const detalles = document.getElementById('pncDetallesAccion')?.value?.trim();
    const respLinea = document.getElementById('pncRespLinea')?.value?.trim();

    if (!detalles) { toast.error('Los detalles de la acción son requeridos'); return; }
    if (!respLinea) { toast.error('El responsable de línea es requerido'); return; }

    const data = {
      detalles_accion: detalles.toUpperCase(),
      responsable_linea: respLinea,
      evidencia_whatsapp_solucion: document.getElementById('pncEvidenciaWsSolucion')?.checked || false
    };

    try {
      this._showProgress('Guardando verificación...');
      await calidadService.registrarVerificacion(this.data.pnc.id, data);
      this._hideProgress();
      this._clearUnsaved();
      toast.success('Verificación registrada');
      await this.cargarPNC(this.data.pnc.id);
      setTimeout(() => this.showTab('apt3'), 100);
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al registrar verificación');
    }
  },

  async cerrarPNC() {
    const resultado = document.querySelector('input[name="pncResultadoFinal"]:checked')?.value;
    const respCalidad = document.getElementById('pncRespCalidad')?.value?.trim();

    if (!resultado) { toast.error('Seleccione el resultado final'); return; }
    if (!respCalidad) { toast.error('El responsable de calidad es requerido'); return; }

    if (!confirm('¿Cerrar este PNC? Esta acción no se puede deshacer.')) return;

    const data = {
      resultado_final: resultado,
      responsable_calidad: respCalidad
    };

    try {
      this._showProgress('Cerrando PNC...');
      await calidadService.cerrarPNC(this.data.pnc.id, data);
      this._hideProgress();
      this._clearUnsaved();
      toast.success('PNC cerrado exitosamente');
      await this.cargarPNC(this.data.pnc.id);
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al cerrar PNC');
    }
  },

  async agregarFotoArchivo(event, tipo) {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = '';

    const fotosExistentes = (this.data.pnc.fotos || []).filter(f => f.tipo === tipo);
    const fotosPendientes = tipo === 'problema' ? this.data.fotosNuevasProblema : this.data.fotosNuevasSolucion;
    if (fotosExistentes.length + fotosPendientes.length >= 2) { toast.error('Máximo 2 fotografías por sección'); return; }

    try {
      const compressed = await compressImage(file);
      const previewUrl = URL.createObjectURL(compressed);
      fotosPendientes.push({ file: compressed, previewUrl, tipo });
      this._showUnsaved();
      this._refreshAnexoGrid();
      toast.info('Foto agregada (se subirá al guardar cambios)');
    } catch (error) {
      toast.error(error.message || 'Error al procesar foto');
    }
  },

  removerFotoPendiente(tipo, index) {
    const arr = tipo === 'problema' ? this.data.fotosNuevasProblema : this.data.fotosNuevasSolucion;
    if (arr[index]) {
      URL.revokeObjectURL(arr[index].previewUrl);
      arr.splice(index, 1);
      this._refreshAnexoGrid();
    }
  },

  _refreshAnexoGrid() {
    const p = this.data.pnc;
    const fotos = p.fotos || [];

    // Refresh problema grid
    const gridProblema = document.getElementById('pncFotoGridProblema');
    if (gridProblema) {
      gridProblema.innerHTML = this._renderFotoGrid(
        fotos.filter(f => f.tipo === 'problema'), 'problema', false,
        this.data.fotosNuevasProblema
      );
    }

    // Refresh solucion grid
    const gridSolucion = document.getElementById('pncFotoGridSolucion');
    if (gridSolucion) {
      gridSolucion.innerHTML = this._renderFotoGrid(
        fotos.filter(f => f.tipo === 'solucion'), 'solucion', false,
        this.data.fotosNuevasSolucion
      );
    }
  },

  async _subirFotosPendientes() {
    const allPending = [...this.data.fotosNuevasProblema, ...this.data.fotosNuevasSolucion];
    if (allPending.length === 0) return;

    const numSerie = this.data.pnc.num_serie;
    this._showProgress(`Subiendo ${allPending.length} foto(s)...`);

    const uploads = allPending.map(async (foto) => {
      const url = await recepcionService.subirFoto(
        foto.file,
        `pnc-${this.data.pnc.id}-${foto.tipo}-${Date.now()}.jpg`,
        numSerie,
        'pnc'
      );
      await calidadService.agregarFoto(this.data.pnc.id, { foto_url: url, tipo: foto.tipo });
      URL.revokeObjectURL(foto.previewUrl);
    });

    await Promise.all(uploads);
    this.data.fotosNuevasProblema = [];
    this.data.fotosNuevasSolucion = [];
  },

  async eliminarFoto(fotoId) {
    if (!confirm('¿Eliminar esta foto?')) return;
    try {
      await calidadService.eliminarFoto(this.data.pnc.id, fotoId);
      toast.success('Foto eliminada');
      await this.cargarPNC(this.data.pnc.id);
      setTimeout(() => this.showTab('anexo'), 100);
    } catch (error) {
      toast.error(error.message || 'Error al eliminar foto');
    }
  },

  // ======================== IMPRIMIR / PDF ========================

  imprimir() {
    if (!this.data.pnc) return;
    router.navigate(`/calidad/pnc/${this.data.pnc.id}/print`);
  },

  imprimirProblema(problemaId) {
    this._imprimirConAviso(`?problema=${problemaId}`);
  },

  // Imprime TODOS los daños de una categoría (clasificación).
  imprimirGrupo(key) {
    this._imprimirConAviso(`?clasificacion=${encodeURIComponent(key)}`);
  },

  // Navega al print con el query dado, avisando si faltan las firmas oficiales del PNC.
  _imprimirConAviso(query) {
    const p = this.data.pnc;
    if (!p) return;
    const firmasFaltan = [];
    if (!p.responsable_produccion_firma_url) firmasFaltan.push('producción');
    if (!p.responsable_linea_firma_url) firmasFaltan.push('línea');
    if (!p.responsable_calidad_firma_url) firmasFaltan.push('calidad');

    const irImprimir = () => router.navigate(`/calidad/pnc/${p.id}/print${query}`);

    if (firmasFaltan.length === 0) {
      irImprimir();
      return;
    }

    // Modal de aviso antes de navegar al print
    modal.open(
      'Faltan firmas del PNC',
      `<p style="margin:0 0 0.5rem;">El formato AMX-CC-FR-05 saldrá impreso con las siguientes líneas de firma <strong>vacías</strong>:</p>
       <ul style="margin:0.25rem 0 0.75rem 1.25rem;color:#374151;">
         ${firmasFaltan.map(f => `<li>Responsable de ${f}</li>`).join('')}
       </ul>
       <p style="margin:0;color:#6b7280;font-size:0.85rem;">¿Quieres continuar de todos modos?</p>`,
      [
        { text: 'Cancelar', class: 'btn-secondary' },
        {
          text: 'Imprimir igual',
          class: 'btn-primary',
          onClick: () => irImprimir()
        }
      ]
    );
  },

  async descargarPDF() {
    const p = this.data.pnc;
    if (!p) return;

    this._showProgress('Generando PDF...');

    try {
      if (typeof html2pdf === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.2/html2pdf.bundle.min.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Error al cargar librería PDF'));
          document.head.appendChild(script);
        });
      }

      const { default: PrintPNC } = await import('./PrintPNC.js');

      const contentEl = document.getElementById('content');
      const savedHTML = contentEl.innerHTML;

      const html = PrintPNC.generateHTML(p);
      contentEl.innerHTML = html;

      // Las fotos/firmas viven en el bucket GCS (otro origen). html2canvas NO puede
      // rasterizarlas por CORS y saldrían en blanco en el PDF. Se traen por el proxy
      // del backend (mismo origen) y se incrustan como data URI antes de generar.
      this._showProgress('Preparando imágenes...');
      const gcsImgs = Array.from(contentEl.querySelectorAll('img'))
        .filter(im => (im.getAttribute('src') || '').includes('storage.googleapis.com'));
      const dataUriCache = new Map(); // varias imágenes del grupo comparten la misma ruta
      for (const im of gcsImgs) {
        const original = im.getAttribute('src');
        try {
          let dataUri = dataUriCache.get(original);
          if (!dataUri) {
            const resp = await api.get('/files/proxy', { url: original });
            dataUri = resp?.data?.dataUri;
            if (dataUri) dataUriCache.set(original, dataUri);
          }
          if (dataUri) im.src = dataUri;
        } catch (e) {
          console.warn('No se pudo cargar imagen para el PDF:', original, e.message);
        }
      }
      this._showProgress('Generando PDF...');

      const imgs = contentEl.querySelectorAll('img');
      await Promise.all(Array.from(imgs).map(img =>
        img.complete ? Promise.resolve() : new Promise(res => { img.onload = res; img.onerror = res; })
      ));

      await new Promise(res => setTimeout(res, 300));

      const opt = {
        margin: 0,
        filename: `PNC_${p.num_serie || p.id}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] }
      };

      await html2pdf().set(opt).from(contentEl).save();
      contentEl.innerHTML = savedHTML;
      this._hideProgress();
      toast.success('PDF descargado');
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al generar PDF');
    }
  },

  // ======================== FIRMAS ========================

  _renderFirmasDinamicas() {
    const container = document.getElementById('firmasContainer');
    if (!container) return;

    const p = this.data.pnc || {};

    this.signaturePad_detectado_por = null;
    this.signaturePad_responsable_produccion = null;
    this.signaturePad_responsable_linea = null;
    this.signaturePad_responsable_calidad = null;

    container.innerHTML = `
      ${this._renderFirmaConSelect('responsable_produccion', 'Responsable del área de producción', p.responsable_produccion, p.responsable_produccion_firma_url, this.data.responsablesProduccion || [])}
      ${this._renderFirmaConSelect('responsable_linea', 'Responsable de la línea de producto', p.responsable_linea, p.responsable_linea_firma_url, this.data.responsablesLinea || [])}
      ${this._renderFirmaConSelect('responsable_calidad', 'Responsable del área de calidad', p.responsable_calidad, p.responsable_calidad_firma_url, this.data.responsablesCalidad || [])}
      <div style="grid-column: 1 / -1;background:#fef3c7;border:1px solid #f59e0b;border-radius:8px;padding:0.75rem 1rem;margin-top:0.5rem;font-size:0.8rem;color:#78350f;">
        <strong>Firma del detectado por:</strong> se captura por daño en el modal de Identificación (puede variar según el módulo de origen).
      </div>
    `;
  },

  _renderFirmaConSelect(tipo, label, currentName, existingUrl, responsables) {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const opciones = responsables.map(r =>
      `<option value="${esc(r.nombre_completo)}" ${currentName === r.nombre_completo ? 'selected' : ''}>${esc(r.nombre_completo)}</option>`
    ).join('');

    // Si no hay nombre seleccionado, el canvas se bloquea con un overlay para
    // evitar firma huérfana (firma sin responsable). El overlay se oculta
    // dinámicamente en _onResponsableChange cuando seleccionan un nombre.
    const cuerpo = existingUrl
      ? `<div style="padding:1.5rem;text-align:center;">
           <img src="${existingUrl}" style="width:100%;max-height:100px;object-fit:contain;" onerror="this.style.display='none'" />
         </div>
         <div style="padding:0 1rem 0.25rem;text-align:center;">
           <a href="javascript:void(0)" style="font-size:0.8rem;color:var(--primary);" onclick="window.pncDetalleView.cambiarFirmaExistente('${tipo}')">Cambiar firma</a>
         </div>`
      : `<div class="firma-box" style="position:relative;">
           <canvas id="firma-${tipo}" class="firma-canvas-new"></canvas>
           <span class="firma-placeholder" id="firma-placeholder-${tipo}" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);text-align:center;pointer-events:none;color:var(--gray-400);">
             <span style="font-size:0.8rem;">Firme aquí</span>
           </span>
           <div id="firma-lock-${tipo}" style="position:absolute;inset:0;background:rgba(255,255,255,0.92);display:${currentName ? 'none' : 'flex'};flex-direction:column;align-items:center;justify-content:center;text-align:center;color:#92400e;font-weight:600;font-size:0.85rem;padding:1rem;cursor:not-allowed;z-index:5;">
             <span style="font-size:1.5rem;line-height:1;margin-bottom:0.4rem;">🔒</span>
             <span>Selecciona el responsable abajo antes de firmar</span>
           </div>
         </div>
         <div style="padding:0 1rem 0.25rem;text-align:center;">
           <a href="javascript:void(0)" style="font-size:0.8rem;color:var(--primary);" onclick="window.pncDetalleView.limpiarFirma('${tipo}')">Limpiar</a>
         </div>`;

    return `
      <div style="border:1px solid var(--gray-200);border-radius:8px;overflow:hidden;background:white;">
        <div style="background:var(--bg-secondary);padding:0.6rem;text-align:center;border-bottom:1px solid var(--gray-200);">
          <span style="font-size:0.7rem;text-transform:uppercase;letter-spacing:1px;color:var(--primary);font-weight:600;">${label} *</span>
        </div>
        ${cuerpo}
        <div style="padding:0.5rem 1rem 0.75rem;border-top:1px solid var(--gray-200);">
          <span style="font-size:0.7rem;color:var(--gray-500);text-transform:uppercase;letter-spacing:0.5px;">Nombre</span>
          <select id="pncFirma_${tipo}" class="form-input" style="margin-top:0.25rem;font-weight:600;"
            data-skip-unsaved
            data-prev-value="${(currentName || '').replace(/"/g, '&quot;')}"
            onchange="window.pncDetalleView._onResponsableChange('${tipo}', this)">
            <option value="">Seleccione...</option>
            ${opciones}
          </select>
        </div>
      </div>
    `;
  },

  _onResponsableChange(tipo, selectEl) {
    const newValue = selectEl.value || '';
    const prevValue = selectEl.dataset.prevValue || '';
    if (newValue === prevValue) return;

    const p = this.data.pnc;
    const firmaUrl = p?.[`${tipo}_firma_url`];

    if (!firmaUrl) {
      // No había firma → actualizar nombre + toggle del candado del canvas.
      // Si quitan el nombre, limpiamos el pad (cualquier trazo que pudiera
      // haberse colado) y mostramos el overlay de bloqueo.
      if (p) p[tipo] = newValue;
      selectEl.dataset.prevValue = newValue;
      const lockEl = document.getElementById(`firma-lock-${tipo}`);
      if (lockEl) lockEl.style.display = newValue ? 'none' : 'flex';
      if (!newValue) {
        const pad = this[`signaturePad_${tipo}`];
        if (pad) pad.clear();
        const placeholder = document.getElementById(`firma-placeholder-${tipo}`);
        if (placeholder) placeholder.style.display = '';
      }
      this._showUnsaved();
      return;
    }

    // Hay firma capturada → confirmar antes de invalidarla
    const labels = {
      responsable_produccion: 'Responsable del área de producción',
      responsable_linea: 'Responsable de la línea de producto',
      responsable_calidad: 'Responsable del área de calidad'
    };
    modal.confirm(
      '¿Cambiar de responsable?',
      `Ya hay una firma capturada para <strong>${prevValue || '(actual)'}</strong> como ${labels[tipo] || tipo}. ` +
      `Si cambias a <strong>${newValue || '(ninguno)'}</strong>, la firma actual se invalidará y la nueva persona deberá firmar de nuevo.`,
      () => {
        // Confirmar: invalidar firma + queue para borrar del bucket
        if (firmaUrl) this._getFirmasPendientesReemplazo()[`pnc_${tipo}`] = firmaUrl;
        if (p) {
          p[tipo] = newValue;
          p[`${tipo}_firma_url`] = null;
        }
        selectEl.dataset.prevValue = newValue;
        // Re-render primero (reconstruye el DOM y borra el indicator viejo),
        // luego mostrar banner sobre el nuevo DOM.
        this.renderFormularioEdicion();
        this.showTab('firmas');
        this._showUnsaved();
      }
    );
    // Si no confirma (el modal se cierra sin onClick), revertimos el select
    // Como modal.confirm no tiene onCancel, programamos un listener al close del modal
    // para detectar si el usuario canceló y revertir.
    setTimeout(() => {
      const observer = new MutationObserver(() => {
        const modalEl = document.getElementById('modal-container')?.querySelector('.modal');
        if (!modalEl) {
          // Modal se cerró — revertir si no se confirmó
          if (selectEl.value !== selectEl.dataset.prevValue) {
            selectEl.value = selectEl.dataset.prevValue;
          }
          observer.disconnect();
        }
      });
      observer.observe(document.getElementById('modal-container') || document.body, { childList: true, subtree: true });
    }, 50);
  },

  _renderFirmaDisabled(label) {
    return `
      <div class="firma-container-new" style="text-align: center;">
        <label class="form-label firma-label">${label}</label>
        <p style="margin: 0 0 0.5rem; font-weight: 600; color: var(--text-secondary);">Seleccione en tab Evaluación</p>
      </div>
    `;
  },

  _renderFirmaEdicion(tipo, label, nombre, existingUrl) {
    if (existingUrl) {
      return `
        <div class="firma-container-new" style="text-align: center;">
          <label class="form-label firma-label">${label}</label>
          <p style="margin: 0 0 0.5rem; font-weight: 600;">${nombre || ''}</p>
          <img src="${existingUrl}" style="width: 100%; max-height: 80px; object-fit: contain; margin-bottom: 0.5rem;" onerror="this.style.display='none'" />
          <a href="javascript:void(0)" class="firma-limpiar" onclick="window.pncDetalleView.cambiarFirmaExistente('${tipo}')">Cambiar firma</a>
        </div>
      `;
    }
    return `
      <div class="firma-container-new" style="text-align: center;">
        <label class="form-label firma-label">${label}</label>
        <p style="margin: 0 0 0.5rem; font-weight: 600;">${nombre || ''}</p>
        <div class="firma-box">
          <canvas id="firma-${tipo}" class="firma-canvas-new"></canvas>
          <span class="firma-placeholder" id="firma-placeholder-${tipo}">Toque para firmar</span>
        </div>
        <a href="javascript:void(0)" class="firma-limpiar" onclick="window.pncDetalleView.limpiarFirma('${tipo}')">Limpiar</a>
      </div>
    `;
  },

  _renderFirmaDetalle(label, url, nombre) {
    return `
      <div style="text-align: center; padding: 0.5rem;">
        ${url
          ? `<img src="${url}" style="width: 100%; max-height: 120px; object-fit: contain;" onerror="this.style.display='none'" />`
          : '<div style="min-height: 120px; display: flex; align-items: center; justify-content: center;"><span style="color: var(--text-secondary);">Sin firma</span></div>'
        }
        <hr style="border: none; border-top: 2px solid #000; margin: 0.5rem 0;" />
        <p style="margin: 0.25rem 0 0; font-weight: 700; font-size: 0.95rem; text-transform: uppercase;">${nombre || ''}</p>
        <p style="margin: 0.25rem 0 0; color: var(--text-secondary); font-size: 0.85rem;">${label}</p>
      </div>
    `;
  },

  async initFirmas() {
    if (!window.SignaturePad) {
      try {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/signature_pad/4.1.7/signature_pad.umd.min.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Error al cargar SignaturePad'));
          document.head.appendChild(script);
        });
      } catch (e) { toast.error('Error al cargar librería de firmas'); return; }
    }

    setTimeout(() => {
      ['detectado_por', 'responsable_produccion', 'responsable_linea', 'responsable_calidad'].forEach(tipo => {
        const canvas = document.getElementById(`firma-${tipo}`);
        if (!canvas || this[`signaturePad_${tipo}`]) return;

        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d').scale(ratio, ratio);

        const pad = new SignaturePad(canvas, {
          backgroundColor: 'rgb(255, 255, 255)',
          penColor: 'rgb(0, 0, 0)'
        });
        this[`signaturePad_${tipo}`] = pad;

        // Activar indicador "hay cambios" al terminar un trazo
        pad.addEventListener('endStroke', () => this._showUnsaved());

        const placeholder = document.getElementById(`firma-placeholder-${tipo}`);
        if (placeholder) {
          canvas.addEventListener('pointerdown', () => placeholder.style.display = 'none');
        }
      });
    }, 100);
  },

  limpiarFirma(tipo) {
    const pad = this[`signaturePad_${tipo}`];
    if (pad) {
      const tenia = !pad.isEmpty();
      pad.clear();
      const placeholder = document.getElementById(`firma-placeholder-${tipo}`);
      if (placeholder) placeholder.style.display = 'block';
      if (tenia) this._showUnsaved();
    }
  },

  cambiarFirmaExistente(tipo) {
    const p = this.data.pnc;
    if (p) {
      const urlVieja = p[`${tipo}_firma_url`];
      // Guardar como pendiente: solo se borra del bucket SI se sube una nueva
      if (urlVieja) this._getFirmasPendientesReemplazo()[`pnc_${tipo}`] = urlVieja;
      p[`${tipo}_firma_url`] = null;
    }
    this.renderFormularioEdicion();
    this.showTab('firmas');
    this._showUnsaved();
  },

  async subirFirmas() {
    const urls = {};
    const uploads = [];
    const tipos = ['detectado_por', 'responsable_produccion', 'responsable_linea', 'responsable_calidad'];
    const p = this.data.pnc;
    const numSerie = p?.num_serie;

    for (const tipo of tipos) {
      // Guarda por URL: solo subir si NO existe URL persistida.
      // "Cambiar firma" pone la URL a null, así que la única vía para re-subir es ese flujo.
      const urlExistente = p?.[`${tipo}_firma_url`];
      const pad = this[`signaturePad_${tipo}`];
      if (!urlExistente && pad && !pad.isEmpty()) {
        const blob = await this.toBlob(pad);
        uploads.push(
          recepcionService.subirFirma(blob, `firma-${tipo}-pnc-${Date.now()}.png`, numSerie, 'pnc')
            .then(url => { urls[`${tipo}_firma_url`] = url; })
        );
      }
    }

    if (uploads.length > 0) {
      this._showProgress(`Subiendo ${uploads.length} firma(s)...`);
      await Promise.all(uploads);
    }
    return urls;
  },

  async toBlob(signaturePad) {
    const dataURL = signaturePad.toDataURL('image/png');
    const response = await fetch(dataURL);
    return response.blob();
  },

  // ======================== HELPERS ========================

  formatAccion(accion) {
    const acciones = { concesion: 'Concesión (Justificar)', mantenimiento: 'Mantenimiento', reemplazo: 'Reemplazo' };
    return acciones[accion] || accion || '-';
  },

  formatEstado(estado) {
    const estados = { abierto: 'ABIERTO', en_proceso: 'EN PROCESO', cerrado: 'CERRADO' };
    return estados[estado] || estado;
  },

  getEstadoBadge(estado) {
    const badges = { abierto: 'badge-warning', en_proceso: 'badge-info', cerrado: 'badge-success' };
    return badges[estado] || 'badge-secondary';
  },

  /**
   * "← Volver" del header. Enruta según el modo para no perder datos en silencio:
   * en creación pasa por `_cancelarCreacion` (confirma si hay daños capturados);
   * en edición pasa por `volver` (confirma si hay cambios sin guardar).
   */
  _headerVolver() {
    if (this.data.isNew) {
      this._cancelarCreacion();
    } else {
      this.volver();
    }
  },

  volver() {
    if (this._hayCambiosSinGuardar()) {
      if (!confirm('Hay cambios sin guardar. ¿Está seguro de salir?')) return;
    }
    this._clearUnsaved();
    router.navigate('/calidad/pnc');
  },

  _showUnsaved() {
    this._hasUnsaved = true;
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  // Fuente de verdad para "¿hay cambios sin guardar?": la bandera visual O cualquier
  // buffer diferido con contenido (cambios de daño, firmas, fotos pendientes/eliminar).
  // Evita depender solo de _hasUnsaved, que puede desincronizarse.
  _hayCambiosSinGuardar() {
    if (this._hasUnsaved) return true;
    const noVacio = (obj) => obj && Object.keys(obj).length > 0;
    if (noVacio(this._problemasDirty)) return true;
    if (noVacio(this._problemasFirmasPendientes)) return true;
    if (noVacio(this._firmasPendientesReemplazo)) return true;
    if (this._fotosPendientesEliminar && this._fotosPendientesEliminar.size > 0) return true;
    const fp = this._fotosPendientesPorProblema || {};
    for (const k of Object.keys(fp)) {
      const g = fp[k] || {};
      if ((g.problema?.length || 0) + (g.solucion?.length || 0) > 0) return true;
    }
    return false;
  },

  _clearUnsaved() {
    this._hasUnsaved = false;
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'none';
    if (this._beforeUnloadHandler) window.removeEventListener('beforeunload', this._beforeUnloadHandler);
  },

  _showProgress(message) {
    let overlay = document.getElementById('progressOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'progressOverlay';
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.9); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 9999;';
      overlay.innerHTML = `
        <div class="spinner" style="margin-bottom: 1rem;"></div>
        <p id="progressMessage" style="font-size: 1rem; font-weight: 600; color: var(--gray-700);"></p>
      `;
      document.body.appendChild(overlay);
    }
    document.getElementById('progressMessage').textContent = message;
  },

  _hideProgress() {
    const overlay = document.getElementById('progressOverlay');
    if (overlay) overlay.remove();
  }
};
